import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { bearerHeaders } from './api.ts';
import { apiFetch } from './appFetch';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { Room, RoomEvent, Track, RemoteTrackPublication, RemoteParticipant, LocalParticipant, RemoteVideoTrack, ConnectionState, LocalVideoTrack } from 'livekit-client';
import { coerceLivekitUrlForBrowser } from './livekit-ws';
import { QPanel } from './q/QPanel';
import { Q_CONVERSATION } from './q/qClient';
import { createQHuntPause } from './q/huntPause';
import { onQLaunch } from './q/qLaunch';
import { devicePosition, lookThrough } from './q/qField';
import { GuestRequests } from './components/GuestRequests';
import { resolveUrl, publicWebAppUrl, api, auth, guestApi, isGuestHunt, tokenHeader as guestTokenHeader, huntTrackUpload, type MarkSkjutmeny, type MarkShootingPlanItem, type HuntPosition, type HuntChatMessage, type HuntDogPosition, type HuntTrack, type DogWatch, type MarkCamera } from './api';
import { onWatchCommand, onWatchHeartRate, setWatchHuntState } from './watchBridge';
import { drainBackgroundTrack, onBackgroundTrackResume, startBackgroundTrack, stopBackgroundTrack } from './backgroundTrack';
import { HuntSheet, huntBarSurface, CameraSourcePicker, VideoFullscreen, type CameraSource, MapRoundButton, IconMenuLines, MenuActionButton, MenuSection, SubRow, SegmentedChoice, ColorDots, LightToggleRow, TRAIL_COLORS } from './karta/MapUIComponents';
import { beginHuntWorkout, finishHuntWorkout, markWatchInHunt, stepsToday } from './hunterHealth';
import { extractAssistantInsights, killPat, obsPat, parseCameraCommand, SHOT_EVENT_TEXT } from './huntTranscriptInsights';
import { heardGameText, heardOutcome, matchingSubcats, parseHeardGame } from './heardGame';
import { shouldSendChunk, startVoiceActivityMeter } from './voiceActivity';
import { setHuntActive } from './appUpdate';
import { planSpeciesFamilyKey } from './swedishHuntingLegality';
import { SUBCAT_MAP, FAMILY_DISPLAY_NAMES } from './wildlifeData.ts';
import { GAME_STATUS_LABEL, groupByFamily, kr } from './lib/skjutmeny';
import { GameStatusIcon, GameStatusLegend } from './components/GameStatusIcon';
import { SkjutmenyFagel } from './components/SkjutmenyFagel';
import QRCode from 'qrcode';
import { getTheme, useThemeMode } from './theme';
import { Maximize2, Play } from 'lucide-react';
import { Button, Card, Icon, IconButton, useIsMobile } from './components/ui';
import {
  HuntToolbarButton,
  IconShareArrow,
  IconChatBubble,
  WindFlowArrow,
} from './karta/MapUIComponents';
import { Karta3D, type Karta3DProps } from './karta/Karta3D';
import { PitchControl } from './karta/PitchControl';
import { SatPanel } from './karta/SatPanel';
import { busyBySat, occupancyLabel, parseSatar, satColor, SAT_BUSY_COLOR, stockholmToday } from './karta/satar';
import type { SatOccupancy } from './api';
import type maplibregl from 'maplibre-gl';
import { HuntFloatingButton, HuntStartSheet, HUNT_ICONS, type HuntSheetAction } from './karta/HuntStartSheet';
import { GlassesShare, isMetaGlassesAvailable, type GlassesStateEvent } from './metaGlasses';
import { lockPageScroll, scrollAfterFullscreen, scrollToWhenLaidOut, uncoverTop } from './karta/fullscreen';
import { CenterDistanceOverlay, useCenterDistance } from './karta/CenterDistance';
import { COMPASS_TRANSITION, UPRIGHT_VAR, FASTIGHET_GRANSER_SLD, FASTIGHET_WMS_URL, formatDistance, getDistanceRings as getDistanceRingsFor, ringLabelPoint, ringOpacity, VITTRING_COLOR, vittringCone } from './karta/mapGeometry';
import { wmoWeatherEmoji, formatSunTimeLocal, windDirLabelSv } from './karta/weatherUtils';
import {
  GPS_TRACK_STORAGE_KEY,
  LAST_POS_STORAGE_KEY,
  GPS_TRACK_RETENTION_MS,
  GPS_TRACK_MAX_POINTS,
  GPS_SAMPLE_MIN_DIST_M,
  GPS_SAMPLE_MIN_INTERVAL_MS,
  type GpsStoredPoint,
  haversineMetersFlat,
  safeLocalStorageGet,
  safeLocalStorageSet,
  readGpsTrackPoints,
  writeGpsTrackPoints,
  mergeGpsTrackPoints,
  persistGpsTrackSample,
  persistLastKnownPosition,
  readLastKnownPosition,
} from './karta/gpsUtils';
import {
  TRANSCRIPT_DATA_V1,
  TRANSCRIPT_LIVE_V1,
  HUNT_CHAT_MSG_V1,
  HUNT_POSITION_V1,
  KARTA_PREFS_STORAGE_KEY,
  VILTSLAG_LIST,
  HIDDEN_GAME_FAMILIES,
  STATUS_CONFIG,
  PIN_CONFIG,
  pinConfig,
  toPinType,
  BASE_MAP_TYPES,
  type PinType,
  type BaseMapType,
} from './karta/constants';
import { escapePinLabelHtml, createPinIcon, parseGpx, wehuntTypeToPinType, mediaPublicUrl } from './karta/pinUtils';
import type { FalltViltEvent, MarkPin } from './karta/types';
import { FalltViltModal } from './karta/FalltViltModal';
import { pajDogMarkerHtml, pajDogPopupHtml, pajFreshness, PAJ_DOG_COLORS, type PajDogPosition, type PajDogTrack } from './karta/pajDogs';
import { ViltDetailsPanel } from './karta/ViltDetailsPanel';
import { ChatComposer } from './components/chat/ChatComposer';
import { useBottomInset } from './components/chat/useBottomInset';
import { setOpenChat, OPEN_HUNT_CHAT_EVENT } from './push';
import { holdMapFullscreen } from './nav/navState';
import { ReportMenu } from './components/moderation/ReportMenu';
import { modApi } from './components/moderation/moderationApi';
import { QHuntLog } from './karta/QHuntLog';
import { useHuntQ } from './karta/useHuntQ';
import { WalkieTalkieIcon } from './karta/WalkieTalkieIcon';
import { HuntVideoRecorder } from './karta/HuntVideoRecorder';
import {
  nativeHuntListenSupported, sendHuntChatMedia, startNativeHuntListen, startNativeVoiceRecording, startWebVoiceRecording,
  VOICE_MESSAGE_MIN_MS, type NativeHuntListen, type VoiceRecording,
} from './q/huntListen';
import { huntQApi, parseWakeWord, RADIO_ACTIVITY_MS, radioClosedByServer, type HuntSummary } from './q/huntQ';
import { followFirstFix } from './karta/initialView'; // MJ2
import { useHuntStatusReporter } from './karta/huntStatus'; // KA1
import { ParticipantStatusCard } from './karta/ParticipantStatusCard'; // KA1

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

interface HuntSession {
  roomName: string;
  livekitUrl: string;
  livekitToken: string;
  title: string;
  meetingId: string;
}

/** Rader i jaktlive-chatten (deltagare + Q-assistent). */
type HuntChatRow =
  | { kind: 'user'; id: string; senderName: string; text?: string; imageUrl?: string; ts: number; lat?: number; lng?: number; mine?: boolean;
      /** I2: röst- och videomeddelanden; pending medan det egna laddas upp. */
      audioUrl?: string; videoUrl?: string; durationMs?: number; pending?: boolean }
  | { kind: 'assistant'; id: string; text: string; inferredFrom: string; ts: number; savedToMark?: boolean; gameType?: string; isKill?: boolean; lat?: number; lng?: number; subcats?: string[];
      /** QV1: typ ur det som hördes och om viltet är skadskjutet (sparas då som obs med eftersök). */
      kon?: string; alder?: string; wounded?: boolean };

interface KartaTabProps {
  mark: {
    id: string;
    name: string;
    /** Lovligt vilt (JSON-array av strängar) — används i skjutmeny-modal. */
    gameTypesJson?: string | null;
    coordinatesJson?: string | null;
    mapLat?: number | null;
    mapLng?: number | null;
    /** Markens län (t.ex. "Södermanlands län") — styr länsspecifika jakttider. */
    county?: string | null;
  };
  isAdmin: boolean;
  /** S1: får rita och ändra såter (plattformsadmin eller markroll med canEditMark). Sparas via setMarkFastighet. */
  canEditSatar?: boolean;
  onSatarSaved?: (coordinatesJson: string) => void;
  token: string;
  onMarkUpdated: (data: { coordinatesJson?: string; mapLat?: number; mapLng?: number }) => Promise<void>;
  huntSession?: HuntSession | null;
  onStartHunt?: () => Promise<HuntSession | null>;
  onEndHunt?: (
    transcript: {speaker: string; text: string; ts: number; lat?: number; lng?: number}[],
    meta: { confirmedKills: {subcat: string; lat?: number; lng?: number; ts: number}[]; qSummary?: HuntSummary | null }
  ) => void;
  onLeaveHunt?: () => void;
  isJaktledare?: boolean;
  userName?: string;
  userAvatarUrl?: string;
  /** Jaktmarkens karta-flik: förenklad krom, Jakt-knapp, bottenblad för jaktverktyg. */
  variant?: 'default' | 'markDetail';
  /** Senaste hunt_started-mötes-id från gruppchat (om användaren inte redan är med i det mötet). */
  joinableHuntMeetingId?: string | null;
  onJoinOngoingHunt?: (meetingId: string) => Promise<void>;
  onPlanHunt?: () => void;
  /** UX5: såtplaneraren är öppen ovanpå kartan — lämna helskärmen så att den syns. */
  planOpen?: boolean;
  /** UX11: bottenmenyns Karta ber om helskärmskartan (ett nytt nummer per tryck). Annars öppnas marken med den vanliga kartan. */
  fullscreenRequest?: number;
  /** Anropas när `fullscreenRequest` har öppnat helskärmen, så att den inte öppnas igen när fliken visas på nytt. */
  onFullscreenRequestDone?: () => void;
  /** UX4: markerna man kan starta/planera jakt på. Fler än en ger ett markval först i jaktpopupen. */
  huntMarks?: { id: string; name: string }[];
  /** UX4: en annan mark valdes — öppna den och starta/planera där. */
  onPickHuntMark?: (markId: string, action: 'start' | 'plan') => void;
  /** Jaktmarkens karta-flik: innehåll under Starta jakt-kortet (t.ex. kommande händelser). */
  aside?: React.ReactNode;
  /** Called whenever a vilt registration is created from the hunt chat — lets App.tsx update its viltRegs state */
  onViltRegistered?: (reg: import('./api').MarkViltRegistration) => void;
}

/** Eget spår: heldraget, tjockt och rött så det syns mot satellitbilden och inte förväxlas med avståndscirklarna. */
const USER_TRAIL_STYLE = { color: '#E02424', weight: 5, opacity: 0.9, lineCap: 'round' as const, lineJoin: 'round' as const };

/** Spår under Spår i menyn: hur länge (minuter, 0 = av) och i vilken färg, per spårtyp. */
type TrailKind = 'mine' | 'hunters' | 'dogs';
type TrailPrefs = Record<TrailKind, { minutes: number; color: string }>;
const DEFAULT_TRAILS: TrailPrefs = {
  mine: { minutes: 0, color: '#E02424' },
  hunters: { minutes: 10, color: '#facc15' },
  dogs: { minutes: 120, color: '#3b82f6' },
};
/** Snabbval per spårtyp (som på förslaget); "…" fäller ut fler längder. */
const TRAIL_QUICK: Record<TrailKind, number[]> = { mine: [10, 60, 1440], hunters: [10, 240, 1440], dogs: [10, 120, 1440] };
const TRAIL_MORE = [30, 120, 240, 480, 720];
function trailLabel(minutes: number): string {
  if (!minutes) return 'Av';
  return minutes < 60 ? `${minutes} min` : `${Math.round(minutes / 60)} h`;
}
function parseTrailPrefs(raw: unknown, legacyShowUserTrail: unknown): TrailPrefs {
  const out: TrailPrefs = JSON.parse(JSON.stringify(DEFAULT_TRAILS));
  if (legacyShowUserTrail === true) out.mine.minutes = 180; // tidigare "Visa eget spår" (3 h)
  if (raw && typeof raw === 'object') {
    for (const k of ['mine', 'hunters', 'dogs'] as TrailKind[]) {
      const v = (raw as any)[k];
      if (v && typeof v.minutes === 'number' && v.minutes >= 0) out[k].minutes = Math.min(1440, Math.round(v.minutes));
      if (v && typeof v.color === 'string' && TRAIL_COLORS.includes(v.color)) out[k].color = v.color;
    }
  }
  return out;
}


/** UX14: följningen i kompassläget återupptas så här länge efter att man slutat dra kartan. */
const FOLLOW_RESUME_MS = 20000;

export function KartaTab({
  mark, isAdmin, canEditSatar, onSatarSaved, token, onMarkUpdated, huntSession, onStartHunt, onEndHunt, onLeaveHunt, isJaktledare, userName, userAvatarUrl,
  variant = 'default',
  joinableHuntMeetingId = null,
  onJoinOngoingHunt,
  onPlanHunt,
  planOpen = false,
  fullscreenRequest,
  onFullscreenRequestDone,
  huntMarks,
  onPickHuntMark,
  aside,
  onViltRegistered,
}: KartaTabProps) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const drawnLayersRef = useRef<any>(null);
  const pinsLayerRef = useRef<any>(null);
  const [pins, setPins] = useState<MarkPin[]>([]);
  const [cameras, setCameras] = useState<MarkCamera[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<MarkCamera | null>(null);
  const camerasLayerRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [placingPinType, setPlacingPinType] = useState<PinType | null>(null);
  const [drawingPolygon, setDrawingPolygon] = useState(false);
  // S1: såter – panelen och dagens beläggning (andra jakter/bokningar i samma eller angränsande såter)
  const [showSatPanel, setShowSatPanel] = useState(false);
  const [satOcc, setSatOcc] = useState<SatOccupancy[]>([]);

  const mapPrefsInitial = useMemo(() => {
    try {
      const raw = safeLocalStorageGet(KARTA_PREFS_STORAGE_KEY);
      if (!raw) return {} as Record<string, unknown>;
      const j = JSON.parse(raw);
      return j?.v === 1 && typeof j === 'object' ? (j as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }, []);

  const [mapType, setMapType] = useState<BaseMapType>(() => {
    const t = mapPrefsInitial.mapType;
    return t === 'satellite' || t === 'terrain' || t === 'mapnik' ? (t as BaseMapType) : 'mapnik';
  });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const IS_CAP_NATIVE = !!(window as any).Capacitor?.isNativePlatform?.();
  // UX11: aldrig helskärm av sig själv när marken öppnas (förr i appen alltid) – bara knappen, bottenmenyns Karta eller en jakt.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreenRequest) return;
    setFullscreen(true);
    onFullscreenRequestDone?.();
  }, [fullscreenRequest]); // eslint-disable-line react-hooks/exhaustive-deps
  // Bottenmenyn göms medan kartan är i helskärm.
  useEffect(() => (fullscreen ? holdMapFullscreen() : undefined), [fullscreen]);
  // UX5: helskärmen (z 9990) låg över såtplaneraren, så planeringen öppnades osynligt bakom kartan.
  useEffect(() => { if (planOpen) setFullscreen(false); }, [planOpen]);
  const [pendingPolygon, setPendingPolygon] = useState<any>(null);
  const [newPinName, setNewPinName] = useState('');
  const [showPinModal, setShowPinModal] = useState<{lat: number; lng: number; type: PinType} | null>(null);
  const [showJaktMenu, setShowJaktMenu] = useState(false);
  const [jaktMenuBusy, setJaktMenuBusy] = useState(false);
  /** UX4: vald mark i jaktpopupen (null = välj först, om det finns flera). */
  const [huntMarkChoice, setHuntMarkChoice] = useState<string | null>(null);
  const huntMarkList = useMemo(() => {
    const list = (huntMarks ?? []).filter(m => m.id !== mark.id);
    return [{ id: mark.id, name: mark.name, current: true }, ...list];
  }, [huntMarks, mark.id, mark.name]);
  const openJaktMenu = () => { setHuntMarkChoice(huntMarkList.length > 1 ? null : mark.id); setShowJaktMenu(true); };
  const [showHuntCloseModal, setShowHuntCloseModal] = useState(false);
  const huntLocalStartRef = useRef<number>(0);
  const isMarkDetail = variant === 'markDetail';
  const mob = useIsMobile();
  /** Karta-fliken i normalläge (DR1 omgång 2): kartan till vänster, Starta jakt-kortet i en högerkolumn (under på mobil). */
  const markDetailCard = isMarkDetail && !fullscreen;
  const canOfferChatJoin = Boolean(
    joinableHuntMeetingId && onJoinOngoingHunt && !huntSession,
  );
  const [showMapSettings, setShowMapSettings] = useState(false);
  /** Utfällbar sektion "Ändra kartan" — standard infälld så admin-knappar inte tar plats. */
  const [mapDrawerEditExpanded, setMapDrawerEditExpanded] = useState(false);
  const [showFastighetsgranser, setShowFastighetsgranser] = useState(() => mapPrefsInitial.showFastighetsgranser === true);
  const [showVittring, setShowVittring] = useState(() => mapPrefsInitial.showVittring === true);
  const [vittringWindDir, setVittringWindDir] = useState<number | null>(null);
  const [vittringWindSpd, setVittringWindSpd] = useState<number | null>(null);
  const [showWeather, setShowWeather] = useState(() => mapPrefsInitial.showWeather === true);
  /** Jägarhälsa: puls (från klockan) och dagens steg som en rad under vädret. */
  const [showHunterHealth, setShowHunterHealth] = useState(() => mapPrefsInitial.showHunterHealth !== false);
  const [weatherSnap, setWeatherSnap] = useState<{
    tempC: number;
    code: number;
    windMs: number;
    windDeg: number;
    sunrise: string;
    sunset: string;
  } | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [selectedPin, setSelectedPin] = useState<MarkPin | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showFalltViltModal, setShowFalltViltModal] = useState<{lat: number; lng: number} | null>(null);
  const [selectedViltPin, setSelectedViltPin] = useState<MarkPin | null>(null);
  const tileLayerRef = useRef<any>(null);
  const fastighetsgranserLayerRef = useRef<any>(null);
  const fastighetsTextLayerRef = useRef<any>(null);
  const [showDistanceRings, setShowDistanceRings] = useState(() => mapPrefsInitial.showDistanceRings !== false);
  const [trailPrefs, setTrailPrefs] = useState<TrailPrefs>(() => parseTrailPrefs(mapPrefsInitial.trails, mapPrefsInitial.showUserTrail));
  /** Eget spår visas (längd > 0) — styr även bakgrundsspårningen och klockans spår. */
  const showUserTrail = trailPrefs.mine.minutes > 0;
  /** Ökas när spårpunkter från bakgrunden (skärmlåset på) lagts in — då ritas spåret om. */
  const [trailVersion, setTrailVersion] = useState(0);
  const [showObservationPins, setShowObservationPins] = useState(() => mapPrefsInitial.showObservationPins !== false);
  const [showSkjutetViltPins, setShowSkjutetViltPins] = useState(() => mapPrefsInitial.showSkjutetViltPins !== false);
  const [showPassPins, setShowPassPins] = useState(() => mapPrefsInitial.showPassPins !== false);
  const [showPassPinLabels, setShowPassPinLabels] = useState(() => mapPrefsInitial.showPassPinLabels !== false);
  const userTrailPolylineRef = useRef<any>(null);
  const trailCoordsRef = useRef<[number, number][]>([]);
  const lastTrailAppendRef = useRef<{ lat: number; lng: number } | null>(null);
  const gpxDrawerInputRef = useRef<HTMLInputElement>(null);

  const { mode } = useThemeMode();
  const appTheme = useMemo(() => getTheme(mode), [mode]);
  /** Samma palett som AppInner (`theme.ts`) — bakgrunder ska matcha marketplace / första sidan. */
  const T = useMemo(() => ({
    forest: appTheme.forest,
    amber: appTheme.amber,
    amberDim: appTheme.amberDim,
    success: appTheme.success,
    bg: appTheme.bg,
    surface: appTheme.surface,
    border: appTheme.border,
    overlay: appTheme.overlay,
    // Halvgenomskinlig bakgrund bakom dialoger, så att kartan syns (overlay är nästan ogenomskinlig).
    scrim: appTheme.scrim,
    text: appTheme.text,
    textSub: appTheme.textSub,
    textMuted: appTheme.textMuted,
  }), [appTheme]);

  // ── LiveKit Hunt Controls ──
  const lkRoomRef = useRef<Room | null>(null);
  const [huntStarting, setHuntStarting] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const micOnRef = useRef(false); // stable ref for use inside closures/event handlers
  const transcribingRef = useRef(false); // true whenever speech/recorder should be running (independent of LiveKit mic)
  const [speakerOn, setSpeakerOn] = useState(true);
  const speakerOnRef = useRef(true);
  useEffect(() => { speakerOnRef.current = speakerOn; }, [speakerOn]);
  const [camOn, setCamOn] = useState(false);
  const camOnRef = useRef(false); // stable ref for use inside closures/event handlers
  const [showOwnCam, setShowOwnCam] = useState(false);
  /** MG1: Meta-glasögonens kamera delas som jägarens kamera (bara i glasögonvarianten av iOS-appen). */
  const glassesAvailable = useMemo(() => isMetaGlassesAvailable(), []);
  const [glassesOn, setGlassesOn] = useState(false);
  const [glassesMsg, setGlassesMsg] = useState<string | null>(null);
  const [glassesDiag, setGlassesDiag] = useState<string | null>(null);
  const [glassesError, setGlassesError] = useState(false);
  const glassesShareRef = useRef<GlassesShare | null>(null);
  const glassesVideoRef = useRef<HTMLVideoElement | null>(null);
  /** Bakkamera som standard i jakt; användaren kan växla till selfie i förhandsgranskningen. */
  const [camFacingMode, setCamFacingMode] = useState<'environment' | 'user'>('environment');
  const camFacingModeRef = useRef(camFacingMode);
  useEffect(() => { camFacingModeRef.current = camFacingMode; }, [camFacingMode]);
  const camTogglingRef = useRef(false); // guard against rapid toggle
  // Röstkommandot "filma"/"sluta filma" körs från transkriberingens återanrop → senaste toggleCam via ref.
  const toggleCamRef = useRef<(force?: boolean) => Promise<void>>(async () => {});
  /** RQ1: bara efter väckningsordet ("Q, filma"), annars startade vanligt prat om att filma radion för alla. */
  const applyCameraVoiceCommand = (text: string): boolean => {
    const cmd = parseCameraCommand(text);
    if (!cmd) return false;
    const wantOn = cmd === 'start';
    if (wantOn === camOnRef.current) return true;
    console.log('[QVENARI-MEET] Röstkommando kamera:', cmd);
    void toggleCamRef.current(wantOn);
    return true;
  };
  /** Identity för vald fjärrvideo (PiP), inte visningsnamn — undviker krock om två heter likadant. */
  const [viewingParticipantId, setViewingParticipantId] = useState<string | null>(null);
  const [leaderBroadcasting, setLeaderBroadcasting] = useState(false);
  /** Fjärrkamera som LiveKit-track (använd .attach/.detach — inte rå MediaStreamTrack). */
  const [leaderVideoTrack, setLeaderVideoTrack] = useState<RemoteVideoTrack | null>(null);
  /** Visningsnamn för den som visas i nedersta livesändningsstripen (jaktledare prioriteras). */
  const [featuredBroadcastName, setFeaturedBroadcastName] = useState<string | null>(null);
  const [remoteParticipants, setRemoteParticipants] = useState<{identity:string;name:string;camOn:boolean}[]>([]);
  const [remotePositions, setRemotePositions] = useState<Record<string, {lat:number;lng:number;name:string;avatarUrl?:string;ts:number}>>({});
  const remotePositionMarkersRef = useRef<Record<string, any>>({});
  /** KA1: deltagaren vars status visas (markörens nyckel i mapPositions). */
  const [statusFor, setStatusFor] = useState<string | null>(null);
  /** Deltagare som bara syns via servern — Apple Watch utan telefonen i LiveKit. */
  const [watchPositions, setWatchPositions] = useState<HuntPosition[]>([]);
  /** Min egen klocka är med i jakten (syns som min klockrad innan telefonen rapporterat). */
  const [myWatchInHunt, setMyWatchInHunt] = useState(false);
  /** Hundar med en Apple Watch i hundläge (position + skall). */
  const [watchDogs, setWatchDogs] = useState<HuntDogPosition[]>([]);
  const watchDogMarkersRef = useRef<Record<string, any>>({});
  /** Jakten är kopplad till marken på servern (joinMarkHunt) — då speglas positioner och chatt dit. */
  const huntServerReadyRef = useRef(false);
  // ownVideoElemRef holds the actual DOM element for detach on cam-off
  const ownVideoElemRef = useRef<HTMLVideoElement | null>(null);
  // callback-ref: fires the INSTANT React mounts the <video> element.
  // Retries up to 5 times in case the track isn't published yet when the element mounts.
  const ownVideoRef = useCallback((el: HTMLVideoElement | null) => {
    ownVideoElemRef.current = el;
    if (!el) return;

    const tryAttach = (attempt = 0) => {
      const room = lkRoomRef.current;
      // Abort if element was unmounted or room gone
      if (!room || ownVideoElemRef.current !== el) return;
      const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      if (pub?.track) {
        console.log('[QVENARI-MEET] callback-ref: attaching camera track (attempt', attempt, ')');
        try { pub.track.detach(el); } catch {}
        try { pub.track.attach(el); } catch (e) { console.error('[QVENARI-MEET] attach failed', e); }
      } else if (attempt < 5) {
        // Track not published yet — retry with backoff
        setTimeout(() => tryAttach(attempt + 1), 150 * (attempt + 1));
      } else {
        console.warn('[QVENARI-MEET] callback-ref: no camera track after 5 attempts');
      }
    };

    tryAttach();
  }, []);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  /** PiP: vilken fjärrvideo som är attachad (för detach vid stäng / byte). */
  const remotePiPVideoTrackRef = useRef<RemoteVideoTrack | null>(null);
  const leaderVideoRef = useRef<HTMLVideoElement>(null);
  const [lkConnected, setLkConnected] = useState(false);
  const [lkError, setLkError] = useState<string|null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteQR, setInviteQR] = useState<string>('');
  const [inviteJoinUrl, setInviteJoinUrl] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  // ── Transcription ──
  const speechRef = useRef<any>(null); // SpeechRecognition instance (desktop fallback)
  const mediaRecRef = useRef<MediaRecorder | null>(null); // MediaRecorder for iOS/WebRTC fallback
  const mediaRecChunksRef = useRef<Blob[]>([]); // audio chunks
  const mediaRecIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [transcript, setTranscript] = useState<{speaker: string; text: string; ts: number; lat?: number; lng?: number}[]>([]);
  const transcriptRef = useRef<{speaker: string; text: string; ts: number; lat?: number; lng?: number}[]>([]); // always-current ref for closures
  /** Kills confirmed via hunt-chat subcat buttons during this hunt session */
  const confirmedKillsRef = useRef<{subcat: string; lat?: number; lng?: number; ts: number}[]>([]);
  const [liveText, setLiveText] = useState('');
  /** Interim-rader från andra deltagare (identity → { name, text }). */
  const [remoteLiveLines, setRemoteLiveLines] = useState<Record<string, { name: string; text: string }>>({});
  const transcriptLiveThrottleRef = useRef(0);
  /** Jaktchatt-panel (sparad som showTranscript i localStorage för äldre klienter). */
  const [showHuntChat, setShowHuntChat] = useState(() => mapPrefsInitial.showTranscript === true);
  // Q under jakt (knappen längst till vänster i bottenmenyn). Bara för inloggade.
  const [showQ, setShowQ] = useState(false);
  const [qListen, setQListen] = useState(0);
  const qUser = useMemo(() => (token ? auth.getUser() : null), [token]);
  // RQ4: "Q, …" i det jakten hör är en fråga till Q. Den hamnar i Q-flikens samtal och svaret läses upp med vald röst
  // utan att fliken öppnas. Raden går inte till jaktloggen, servern eller walkie-talkien. "Q, filma" styr kameran.
  const [qAsk, setQAsk] = useState<{ id: number; text: string } | null>(null);
  const handleWakeRef = useRef<(text: string) => boolean>(() => false);
  handleWakeRef.current = (text: string) => {
    const q = parseWakeWord(text);
    if (q == null) return false;
    if (q && applyCameraVoiceCommand(q)) return true;
    if (!qUser) return false;
    console.log('[QVENARI-Q] Väckningsord:', q || '(lyssnar)');
    setQAsk(a => ({ id: (a?.id ?? 0) + 1, text: q }));
    return true;
  };
  // QJ1–QJ3/MT1: Q i jakten (karta/useHuntQ.ts). Det telefonerna hör samlas på servern och visas i Q-fliken;
  // "Lasse till Ola …" blir röstmeddelanden. Raderna läggs i transkriptionen en gång (samma talare, tid och text).
  const appendTranscript = useCallback((entries: {speaker: string; text: string; ts: number; lat?: number; lng?: number}[]) => {
    setTranscript(prev => {
      const seen = new Set(prev.map(e => `${e.speaker}|${e.ts}|${e.text}`));
      const fresh = entries.filter(e => !seen.has(`${e.speaker}|${e.ts}|${e.text}`));
      if (!fresh.length) return prev;
      const next = [...prev, ...fresh].sort((a, b) => a.ts - b.ts);
      transcriptRef.current = next;
      return next;
    });
  }, []);
  const walkiePauseRef = useRef(false);
  const [qSummarizing, setQSummarizing] = useState(false);
  const huntQ = useHuntQ({
    meetingId: huntSession?.meetingId ?? null,
    enabled: !!token && !!huntSession && !isGuestHunt(huntSession.meetingId),
    speakerOn,
    onRemoteHeard: lines => appendTranscript(lines.map(l => ({ speaker: l.speaker, text: l.text, ts: l.ts, lat: l.lat ?? undefined, lng: l.lng ?? undefined }))),
    // Medan ett röstmeddelande spelas lyssnar telefonen inte, så att det inte transkriberas som eget tal.
    onPlayback: playing => {
      if (playing && transcribingRef.current) { walkiePauseRef.current = true; transcribingRef.current = false; huntAudioRef.current.stopSpeech(); }
      else if (!playing && walkiePauseRef.current) { walkiePauseRef.current = false; transcribingRef.current = true; huntAudioRef.current.startSpeech(huntAudioRef.current.userName || 'Jägare'); }
    },
  });
  const huntQRef = useRef(huntQ);
  huntQRef.current = huntQ;
  // MT1: radion (LiveKit) ansluts först när någon trycker på walkie-talkien eller kameran. Gäster ansluter direkt som förut.
  // RQ1: önskan och den färska token gäller en viss jakt (mötes-id). Som boolean levde de kvar från förra jakten,
  // och vid nästa jaktstart hann LiveKit-effekten ansluta (med förra jaktens token) innan de nollställdes.
  const [radioFor, setRadioFor] = useState<string | null>(null);
  const radioForRef = useRef<string | null>(null);
  const [lkCredsState, setLkCreds] = useState<{ meetingId: string; token: string; url: string } | null>(null);
  const lkCreds = lkCredsState && huntSession && lkCredsState.meetingId === huntSession.meetingId ? lkCredsState : null;
  const radioAuto = !!huntSession && (!token || isGuestHunt(huntSession.meetingId));
  const radioOn = !!huntSession && (radioFor === huntSession.meetingId || radioAuto);
  /** RQ2: senaste egna trycket på walkie-talkien eller kameran (en stängning från servern strax innan räknas inte). */
  const radioPressRef = useRef(0);
  const lkConnectedRef = useRef(false);
  // Siri-genvägen under jakt: jaktens lilla Q-kort öppnas och lyssnar (i stället för toppradens Q).
  useEffect(() => {
    if (!huntSession || !qUser) return;
    return onQLaunch(() => { setShowHuntChat(false); setShowQ(true); setQListen(n => n + 1); });
  }, [!!huntSession, !!qUser]); // eslint-disable-line react-hooks/exhaustive-deps
  // Q:s sammanhang under jakt (q/qField.ts): jaktens GPS-position (annars enhetens), marken och jakten.
  const qContext = useCallback(async () => {
    const own = userPositionRef.current;
    const position = own ? { lat: own.lat, lng: own.lng, at: new Date().toISOString() } : await devicePosition();
    return {
      ...(position ? { position } : {}),
      hunt: { markId: mark.id, markName: mark.name, title: huntSessionRef.current?.title, ...(huntLocalStartRef.current ? { startedAt: new Date(huntLocalStartRef.current).toISOString() } : {}) },
    };
  }, [mark.id, mark.name]);
  // "Titta": stillbild från källan som är vald i kameraväljaren (glasögonens ström, annars telefonens kamera,
  // bakre eller selfie). Ingen källa vald: ingen bild (Q säger att kameran är av).
  const camSourceRef = useRef<CameraSource | null>(null);
  const qLook = useCallback(() => {
    const src = camSourceRef.current;
    if (!src) return Promise.resolve(null);
    return lookThrough(src === 'glasses'
      ? glassesShareRef.current?.localTrack?.mediaStreamTrack
      : lkRoomRef.current?.localParticipant.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack);
  }, []);
  const huntSessionRef = useRef(huntSession);
  huntSessionRef.current = huntSession;
  const [huntChatRows, setHuntChatRows] = useState<HuntChatRow[]>([]);
  const huntChatRowsRef = useRef<HuntChatRow[]>([]);
  const [userChatCount, setUserChatCount] = useState(0); // increments on user messages only, triggers insight re-analysis
  useEffect(() => { huntChatRowsRef.current = huntChatRows; }, [huntChatRows]);
  // AS1c: radions meddelanden (LiveKit) bär bara namnet – dölj dem från blockerade användare på namnet.
  // Historiken från servern filtreras redan i API:t.
  const blockedNamesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!huntSession?.meetingId) return;
    modApi.blocks().then(b => { blockedNamesRef.current = new Set(b.map(x => x.name.trim().toLowerCase())); }).catch(() => {});
  }, [huntSession?.meetingId]);
  const assistantInsightIdsRef = useRef<Set<string>>(new Set());
  const [huntChatDraft, setHuntChatDraft] = useState('');
  // I2: röst- och videomeddelanden till jaktchatten (håll in walkie-talkien, kameramenyn eller + i chatten).
  const [voiceRec, setVoiceRec] = useState<{ startedAt: number; hold: boolean } | null>(null);
  const [voiceRecNow, setVoiceRecNow] = useState(0);
  const voiceRecRef = useRef<{ rec: VoiceRecording; resume: boolean } | null>(null);
  const voiceRecStartingRef = useRef<{ stop: 'send' | 'cancel' | null } | null>(null);
  const [showVideoRec, setShowVideoRec] = useState(false);
  const videoRecResumeRef = useRef(false);
  const [mediaNotice, setMediaNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!voiceRec) return;
    const t = setInterval(() => setVoiceRecNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [voiceRec]);
  useEffect(() => {
    if (!mediaNotice) return;
    const t = setTimeout(() => setMediaNotice(null), 4500);
    return () => clearTimeout(t);
  }, [mediaNotice]);
  const [chatTtsEnabled, setChatTtsEnabled] = useState(false);
  const chatTtsEnabledRef = useRef(false);
  useEffect(() => { chatTtsEnabledRef.current = chatTtsEnabled; }, [chatTtsEnabled]);
  const huntChatFileInputRef = useRef<HTMLInputElement>(null);
  const huntChatBottomInset = useBottomInset('[data-hunt-sheet]', !!huntSession && showHuntChat);
  // Push: ingen notis om jaktchatten medan den är öppen, och tryck på en sådan notis öppnar den.
  const openHuntChatMeetingId = huntSession && showHuntChat ? huntSession.meetingId : null;
  useEffect(() => {
    if (!openHuntChatMeetingId) return;
    setOpenChat(`hunt:${openHuntChatMeetingId}`);
    return () => setOpenChat(null);
  }, [openHuntChatMeetingId]);
  useEffect(() => {
    const open = () => setShowHuntChat(true);
    window.addEventListener(OPEN_HUNT_CHAT_EVENT, open);
    return () => window.removeEventListener(OPEN_HUNT_CHAT_EVENT, open);
  }, []);
  const huntChatScrollRef = useRef<HTMLDivElement>(null);
  const [huntChatSavingId, setHuntChatSavingId] = useState<string | null>(null);
  const [showSkjutmenyModal, setShowSkjutmenyModal] = useState(false);
  const [skjutmenyLoading, setSkjutmenyLoading] = useState(false);
  /** Skjutmenyn från API:t (V2): jakttid, tid på dygnet, plan och pris för mig. */
  const [skjutmeny, setSkjutmeny] = useState<MarkSkjutmeny | null>(null);
  const [skjutmenyError, setSkjutmenyError] = useState<string | null>(null);
  /** Räknas upp för att hämta om skjutmenyn (varje minut när den är öppen, och efter en fällning). */
  const [skjutmenyTick, setSkjutmenyTick] = useState(0);
  const [skjutmenyOpenRow, setSkjutmenyOpenRow] = useState<string | null>(null);
  const [micLevel, setMicLevel] = useState(0); // 0-100 audio input level
  const micAnalyserRef = useRef<{ ctx: AudioContext; analyser: AnalyserNode; data: Uint8Array; animId: number } | null>(null);
  const [speechSupported, setSpeechSupported] = useState<boolean | null>(null);
  const [transcribeMode, setTranscribeMode] = useState<'speech'|'recorder'|'none'|'native'>('speech');
  const initialTranscribeLang: 'sv' | 'en' = mapPrefsInitial.transcribeLang === 'en' ? 'en' : 'sv';
  const [transcribeLang, setTranscribeLang] = useState<'sv' | 'en'>(initialTranscribeLang);
  const transcribeLangRef = useRef<'sv' | 'en'>(initialTranscribeLang);
  useEffect(() => { transcribeLangRef.current = transcribeLang; }, [transcribeLang]);

  // Load map prefs from server on mount (overrides localStorage if newer)
  useEffect(() => {
    if (!token) return;
    api.getMapPreferences().then(serverPrefs => {
      if (!serverPrefs) return;
      const apply = (key: string, val: unknown) => {
        if (val === undefined || val === null) return;
        safeLocalStorageSet(KARTA_PREFS_STORAGE_KEY, JSON.stringify({ ...serverPrefs, v: 1 }));
        // Apply to state
        if (key === 'mapType' && (val === 'mapnik' || val === 'satellite' || val === 'terrain')) setMapType(val);
        if (key === 'showFastighetsgranser' && typeof val === 'boolean') setShowFastighetsgranser(val);
        if (key === 'showVittring' && typeof val === 'boolean') setShowVittring(val);
        if (key === 'showWeather' && typeof val === 'boolean') setShowWeather(val);
        if (key === 'showHunterHealth' && typeof val === 'boolean') setShowHunterHealth(val);
        if (key === 'showDistanceRings' && typeof val === 'boolean') setShowDistanceRings(val);
        if (key === 'trails' || key === 'showUserTrail') setTrailPrefs(parseTrailPrefs(serverPrefs.trails, serverPrefs.showUserTrail));
        if (key === 'showObservationPins' && typeof val === 'boolean') setShowObservationPins(val);
        if (key === 'showSkjutetViltPins' && typeof val === 'boolean') setShowSkjutetViltPins(val);
        if (key === 'showPassPins' && typeof val === 'boolean') setShowPassPins(val);
        if (key === 'showPassPinLabels' && typeof val === 'boolean') setShowPassPinLabels(val);
        if (key === 'transcribeLang' && (val === 'sv' || val === 'en')) { setTranscribeLang(val); transcribeLangRef.current = val; }
      };
      for (const [k, v] of Object.entries(serverPrefs)) apply(k, v);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const savePrefsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prefs = {
      v: 1,
      mapType,
      showFastighetsgranser,
      showVittring,
      showWeather,
      showHunterHealth,
      showDistanceRings,
      showUserTrail,
      trails: trailPrefs,
      showObservationPins,
      showSkjutetViltPins,
      showPassPins,
      showPassPinLabels,
      showTranscript: showHuntChat,
      transcribeLang,
    };
    safeLocalStorageSet(KARTA_PREFS_STORAGE_KEY, JSON.stringify(prefs));
    // Debounce server save to avoid hammering on every toggle
    if (savePrefsTimerRef.current) clearTimeout(savePrefsTimerRef.current);
    savePrefsTimerRef.current = setTimeout(() => {
      if (token) api.saveMapPreferences(prefs);
    }, 2000);
  }, [
    mapType,
    showFastighetsgranser,
    showVittring,
    showWeather,
    showHunterHealth,
    showDistanceRings,
    trailPrefs,
    showObservationPins,
    showSkjutetViltPins,
    showPassPins,
    showPassPinLabels,
    showHuntChat,
    transcribeLang,
  ]);

  const skjutmenyFamilies = React.useMemo(() => groupByFamily(skjutmeny?.rows ?? []), [skjutmeny]);

  // Pågående jakt → appen laddar inte om sig vid ny webbversion (appUpdate.ts).
  useEffect(() => {
    if (!huntSession) return;
    setHuntActive(true);
    return () => setHuntActive(false);
  }, [!!huntSession]); // eslint-disable-line

  // Connect/disconnect LiveKit room when huntSession changes
  useEffect(() => {
    if (!huntSession) {
      if (lkRoomRef.current) {
        lkRoomRef.current.disconnect();
        lkRoomRef.current = null;
      }
      setLkConnected(false);
      setMicOn(false); micOnRef.current = false;
      setCamOn(false); camOnRef.current = false;
      setShowOwnCam(false);
      setCamFacingMode('environment');
      void glassesShareRef.current?.stop();
      glassesShareRef.current = null;
      setViewingParticipantId(null);
      {
        const el = remoteVideoRef.current;
        if (el && remotePiPVideoTrackRef.current) {
          try { remotePiPVideoTrackRef.current.detach(el); } catch { /* noop */ }
          remotePiPVideoTrackRef.current = null;
        }
        if (el) el.srcObject = null;
      }
      setRemoteLiveLines({});
      setLeaderBroadcasting(false);
      setLeaderVideoTrack(null);
      setFeaturedBroadcastName(null);
      setRemoteParticipants([]);
      setShowSkjutmenyModal(false);
      setSkjutmeny(null);
      setHuntChatRows([]);
      assistantInsightIdsRef.current.clear();
      setHuntChatDraft('');
      return;
    }
    if (!radioOn) return; // MT1: inget webbmöte förrän någon tryckt på walkie-talkien eller kameran
    const lkToken = lkCreds?.token ?? huntSession.livekitToken;
    // adaptiveStream: false — prevents LiveKit from pausing the local camera track when viewport is small
    // stopMicTrackOnMute: avstängd mikrofon släpper den på riktigt (annars fångar WebKit vidare och Q hör inget, QB1)
    const room = new Room({ adaptiveStream: false, dynacast: false, publishDefaults: { stopMicTrackOnMute: true } });
    lkRoomRef.current = room;

    const updateParticipants = () => {
      const rp: { identity: string; name: string; camOn: boolean }[] = [];
      let leaderCamVideo: RemoteVideoTrack | null = null;
      let leaderCamDisplayName: string | null = null;
      let anyCamVideo: RemoteVideoTrack | null = null;
      let anyCamDisplayName: string | null = null;

      room.remoteParticipants.forEach((p: RemoteParticipant) => {
        const camPub = Array.from(p.trackPublications.values()).find(
          (t: RemoteTrackPublication) => t.source === Track.Source.Camera && !t.isMuted,
        );
        const hasCam = !!camPub;
        rp.push({ identity: p.identity, name: p.name || p.identity, camOn: hasCam });

        if (camPub && !camPub.track) {
          try {
            (camPub as RemoteTrackPublication & { setSubscribed?: (v: boolean) => void }).setSubscribed?.(true);
          } catch { /* noop */ }
        }

        p.trackPublications.forEach((pub: RemoteTrackPublication) => {
          if (pub.track?.source === Track.Source.Microphone && pub.track.mediaStreamTrack) {
            pub.track.mediaStreamTrack.enabled = speakerOnRef.current;
          }
        });

        const vt = camPub?.track;
        if (!vt || vt.kind !== Track.Kind.Video) return;
        const remoteVt = vt as RemoteVideoTrack;
        if (!anyCamVideo) {
          anyCamVideo = remoteVt;
          anyCamDisplayName = p.name || p.identity;
        }
        let isLeader = false;
        try {
          const m = JSON.parse(p.metadata || '{}');
          isLeader = m.role === 'jaktledare';
        } catch { /* ignore */ }
        if (isLeader) {
          leaderCamVideo = remoteVt;
          leaderCamDisplayName = p.name || p.identity;
        }
      });

      setRemoteParticipants(rp);
      const featured = leaderCamVideo || anyCamVideo;
      if (featured) {
        setLeaderBroadcasting(true);
        setLeaderVideoTrack(featured);
        setFeaturedBroadcastName(leaderCamVideo ? leaderCamDisplayName : anyCamDisplayName);
      } else {
        setLeaderBroadcasting(false);
        setLeaderVideoTrack(null);
        setFeaturedBroadcastName(null);
      }
    };

    const onTrackSubscribed = (track: any, _pub: unknown, participant: unknown) => {
      try {
        if (participant && track?.kind === Track.Kind.Audio) {
          track.attach?.();
          room.startAudio().catch(() => {});
        }
      } catch (e) {
        console.warn('[QVENARI-MEET] remote audio attach', e);
      }
      updateParticipants();
    };

    const onConnected = () => {
      setLkConnected(true);
      room.startAudio().catch((e) => console.warn('[QVENARI-MEET] startAudio', e));
      updateParticipants();
    };
    const onDisconnected = () => setLkConnected(false);
    const onParticipantDisconnected = () => { updateParticipants(); };
    const onMediaDevicesError = (err: Error) => {
      console.warn('[QVENARI-MEET] MediaDevicesError:', err?.message || err);
    };
    const onSignalConnected = () => {
      console.log('[QVENARI-MEET] Signal connected');
    };
    const onRoomError = (err: any) => {
      console.warn('[QVENARI-MEET] Room error event:', err?.message || String(err));
    };
    const onLocalTrackPublished = (pub: { source: Track.Source; track?: { detach: (el: HTMLVideoElement) => void; attach: (el: HTMLVideoElement) => void } } | any) => {
      if (pub.source === Track.Source.Camera && pub.track && ownVideoElemRef.current) {
        try { pub.track.detach(ownVideoElemRef.current); } catch {}
        pub.track.attach(ownVideoElemRef.current);
      }
    };
    const onLocalTrackUnpublished = (pub: { source: Track.Source } | any) => {
      if (pub.source === Track.Source.Camera && camOnRef.current) {
        setTimeout(async () => {
          if (!lkRoomRef.current || !camOnRef.current) return;
          try {
            const newPub = await lkRoomRef.current.localParticipant.setCameraEnabled(true, { facingMode: camFacingModeRef.current });
            if (newPub?.track && ownVideoElemRef.current) {
              try { newPub.track.detach(ownVideoElemRef.current); } catch {}
              newPub.track.attach(ownVideoElemRef.current);
            }
          } catch (e) { console.warn('[QVENARI-MEET] camera re-publish failed', e); }
        }, 1500);
      }
    };

    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, updateParticipants);
    room.on(RoomEvent.TrackMuted, updateParticipants);
    room.on(RoomEvent.TrackUnmuted, updateParticipants);
    room.on(RoomEvent.ParticipantConnected, updateParticipants);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
    const onDataReceived = (payload: Uint8Array, participant?: RemoteParticipant | LocalParticipant) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload)) as {
          type?: string; speaker?: string; senderIdentity?: string; text?: string; ts?: number; lat?: number; lng?: number;
          chatId?: string; fromName?: string; imageUrl?: string; name?: string; avatarUrl?: string;
          audioUrl?: string; videoUrl?: string; durationMs?: number;
        };
        const myId = room.localParticipant.identity;
        if (msg.type === HUNT_POSITION_V1) {
          const sid = (participant as RemoteParticipant | undefined)?.identity || msg.senderIdentity;
          if (!sid || sid === myId) return;
          if (typeof msg.lat !== 'number' || typeof msg.lng !== 'number') return;
          const pname = String(msg.name || (participant as RemoteParticipant | undefined)?.name || sid);
          setRemotePositions(prev => ({
            ...prev,
            [sid]: { lat: msg.lat as number, lng: msg.lng as number, name: pname, avatarUrl: msg.avatarUrl, ts: Number(msg.ts) || Date.now() },
          }));
          return;
        }
        if (msg.type === HUNT_CHAT_MSG_V1 && msg.chatId) {
          const sid = (participant as RemoteParticipant | undefined)?.identity || msg.senderIdentity;
          if (sid === myId) return;
          const incomingSender = String(msg.fromName || (participant as RemoteParticipant | undefined)?.name || 'Jägare');
          if (blockedNamesRef.current.has(incomingSender.trim().toLowerCase())) return;
          const incomingText = msg.text ? String(msg.text) : undefined;
          setHuntChatRows(prev => {
            if (prev.some(r => r.kind === 'user' && r.id === msg.chatId)) return prev;
            return [...prev, {
              kind: 'user',
              id: String(msg.chatId),
              senderName: incomingSender,
              text: incomingText,
              imageUrl: msg.imageUrl ? String(msg.imageUrl) : undefined,
              audioUrl: msg.audioUrl && /^https?:\/\//.test(String(msg.audioUrl)) ? String(msg.audioUrl) : undefined,
              videoUrl: msg.videoUrl && /^https?:\/\//.test(String(msg.videoUrl)) ? String(msg.videoUrl) : undefined,
              durationMs: typeof msg.durationMs === 'number' ? msg.durationMs : undefined,
              ts: Number(msg.ts) || Date.now(),
              lat: typeof msg.lat === 'number' ? msg.lat : undefined,
              lng: typeof msg.lng === 'number' ? msg.lng : undefined,
            }];
          });
          setUserChatCount(c => c + 1);
          if (incomingText && chatTtsEnabledRef.current && window.speechSynthesis) {
            window.speechSynthesis.speak(new SpeechSynthesisUtterance(`${incomingSender} — ${incomingText}`));
          }
          return;
        }
        if (msg.type === TRANSCRIPT_LIVE_V1) {
          const sid = (participant as RemoteParticipant | undefined)?.identity || msg.senderIdentity;
          if (!sid || sid === myId) return;
          setRemoteLiveLines(prev => ({
            ...prev,
            [sid]: {
              name: String(msg.speaker || (participant as RemoteParticipant | undefined)?.name || sid),
              text: String(msg.text || ''),
            },
          }));
          return;
        }
        if (msg.type !== TRANSCRIPT_DATA_V1 || !msg.text?.trim()) return;
        const sid = (participant as RemoteParticipant | undefined)?.identity || msg.senderIdentity;
        if (!sid || sid === myId) return;
        setRemoteLiveLines(prev => {
          const n = { ...prev };
          delete n[sid];
          return n;
        });
        const entry = {
          speaker: String(msg.speaker || (participant as RemoteParticipant | undefined)?.name || sid),
          text: String(msg.text).trim(),
          ts: Number(msg.ts) || Date.now(),
          lat: typeof msg.lat === 'number' ? msg.lat : undefined,
          lng: typeof msg.lng === 'number' ? msg.lng : undefined,
        };
        appendTranscript([entry]); // samma rad kan också komma via servern (huntQ)
      } catch { /* ignore malformed */ }
    };
    room.on(RoomEvent.DataReceived, onDataReceived);

    room.on(RoomEvent.SignalConnected as any, onSignalConnected);
    (room as any).on?.('error', onRoomError);

    room.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    room.on(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);

    setLkError(null);
    const lkConnectUrl = coerceLivekitUrlForBrowser(lkCreds?.url ?? huntSession.livekitUrl);
    const doConnect = async (attempt: number) => {
      console.log(`[QVENARI-MEET] Attempting room.connect() attempt=${attempt}`, { url: lkConnectUrl, tokenLen: lkToken?.length });
      try {
        await room.connect(lkConnectUrl, lkToken, {
          autoSubscribe: true,
        });
        console.log('[QVENARI-MEET] KartaTab connected OK — room.state =', room.state, '— mic/cam OFF by default');
        if (lkRoomRef.current !== room) return; // RQ1: rummet hann stängas medan det anslöt
        setLkConnected(true);
        setLkError(null);
        if (isJaktledare) {
          try { await room.localParticipant.setMetadata(JSON.stringify({ role: 'jaktledare' })); } catch {}
        }
      } catch (err: any) {
        // LiveKit WebSocket failures return Event objects, not Errors
        const msg = err instanceof Event ? 'WebSocket connection failed' : (err?.message || String(err));
        console.error(`[QVENARI-MEET] LiveKit connect FAILED (attempt ${attempt}):`, msg, err);
        setLkConnected(false);
        // Auto-retry up to 3 times with backoff
        if (attempt < 3 && lkRoomRef.current === room) {
          const delay = 2000 * attempt;
          setLkError(`Ansluter... (${attempt}/3)`);
          console.log(`[QVENARI-MEET] Retrying connect in ${delay}ms...`);
          setTimeout(() => {
            if (lkRoomRef.current === room) doConnect(attempt + 1);
          }, delay);
        } else {
          setLkError(`Anslutning misslyckades: ${msg}`);
        }
      }
    };
    doConnect(1);

    // RQ2: någon pratar (eller sänder video) i radion → servern, som stänger den efter 15 min tystnad.
    let spoke = false;
    const onActiveSpeakers = (speakers: unknown[]) => { if (speakers.length) spoke = true; };
    room.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakers);
    const activityMeetingId = huntSession.meetingId;
    const activityTimer = token && !isGuestHunt(activityMeetingId) ? setInterval(() => {
      if (room.state !== 'connected') return;
      if (!spoke && !camOnRef.current && !glassesShareRef.current) return;
      spoke = false;
      huntQApi.radioActivity(activityMeetingId).catch(() => {});
    }, RADIO_ACTIVITY_MS) : null;

    // Safety net: if still not connected after 15s, show a visible error
    const connectWatchdog = setTimeout(() => {
      if (lkRoomRef.current === room && room.state !== 'connected') {
        console.error('[QVENARI-MEET] Connection watchdog: still not connected after 15s', { roomState: room.state, url: lkConnectUrl });
        setLkError(`Kan inte nå LiveKit (${lkConnectUrl}) — kontrollera att WSS-proxyn körs`);
      }
    }, 15000);

    return () => {
      clearTimeout(connectWatchdog);
      if (activityTimer) clearInterval(activityTimer);
      room.off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakers);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, updateParticipants);
      room.off(RoomEvent.TrackMuted, updateParticipants);
      room.off(RoomEvent.TrackUnmuted, updateParticipants);
      room.off(RoomEvent.ParticipantConnected, updateParticipants);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
      room.off(RoomEvent.DataReceived, onDataReceived);
      room.off(RoomEvent.SignalConnected as any, onSignalConnected);
      (room as any).off?.('error', onRoomError);
      room.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
      room.off(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
      room.disconnect();
      lkRoomRef.current = null;
      setLkConnected(false);
    };
  }, [huntSession?.livekitToken, huntSession?.livekitUrl, radioOn, lkCreds?.token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { lkConnectedRef.current = lkConnected; }, [lkConnected]);

  /**
   * MT1: startar radion och väntar på rummet. press: ett tryck på walkie-talkien eller kameran (startar radion för
   * alla och håller den igång, RQ2); utan press ansluter telefonen för att någon annan startat den.
   */
  const ensureRoom = async (press = true): Promise<Room | null> => {
    const hs = huntSessionRef.current;
    if (!hs) return null;
    const member = !!token && !isGuestHunt(hs.meetingId);
    if (press && member) {
      radioPressRef.current = Date.now();
      huntQApi.startRadio(hs.meetingId).catch(() => {});
    }
    if (lkRoomRef.current) return lkRoomRef.current;
    if (radioForRef.current !== hs.meetingId) {
      radioForRef.current = hs.meetingId;
      if (member) {
        // Färsk LiveKit-token: jaktens kan vara flera timmar gammal när radion startas.
        try {
          const fresh = await api.joinMarkHunt(mark.id, hs.meetingId, true);
          if (fresh.livekitToken && radioForRef.current === hs.meetingId) setLkCreds({ meetingId: hs.meetingId, token: fresh.livekitToken, url: fresh.livekitUrl ?? hs.livekitUrl });
        } catch { /* den gamla får duga */ }
      }
      if (radioForRef.current === hs.meetingId) setRadioFor(hs.meetingId);
    }
    for (let i = 0; i < 50 && !lkRoomRef.current; i++) await new Promise(r => setTimeout(r, 100));
    return lkRoomRef.current;
  };
  useEffect(() => {
    if (huntQ.radioStartedAt != null && huntSession && radioForRef.current !== huntSession.meetingId) void ensureRoom(false);
  }, [huntQ.radioStartedAt, huntSession?.meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** RQ2: servern stängde radion efter 15 min tystnad – rummet kopplas ner här som hos alla andra. */
  const closeRadio = () => {
    const wasMic = micOnRef.current;
    radioForRef.current = null;
    setRadioFor(null);
    setLkCreds(null);
    setMicOn(false); micOnRef.current = false;
    stopAudioMeter();
    setCamOn(false); camOnRef.current = false;
    setShowOwnCam(false);
    if (glassesShareRef.current) {
      const share = glassesShareRef.current;
      glassesShareRef.current = null;
      void share.stop();
      setGlassesOn(false);
      setGlassesMsg(null);
    }
    setRemoteParticipants([]);
    setLeaderBroadcasting(false);
    setLeaderVideoTrack(null);
    setFeaturedBroadcastName(null);
    // Medan radiomikrofonen var på lyssnade jakten via Groq (regel 1 i QVoicePlugin.swift); telefonen tar över igen.
    if (wasMic && transcribingRef.current && nativeHuntListenSupported()) {
      stopSpeech();
      setTimeout(() => { if (transcribingRef.current && !micOnRef.current) startSpeech(userName || 'Jägare'); }, 300);
    }
    setMediaNotice('Radion stängdes efter 15 minuter utan prat. Tryck på walkie-talkien eller kameran för att starta den igen.');
  };
  const prevRadioStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevRadioStartedAtRef.current;
    prevRadioStartedAtRef.current = huntQ.radioStartedAt;
    if (!radioClosedByServer(prev, huntQ.radioStartedAt)) return;
    if (!huntSession || radioForRef.current !== huntSession.meetingId) return;
    if (Date.now() - radioPressRef.current < 10_000) return; // nyss tryckt: startRadio är på väg till servern
    closeRadio();
  }, [huntQ.radioStartedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Transcription: MediaRecorder → backend Whisper (works on iOS + WebRTC) ──
  // iOS Safari blocks MediaRecorder on WebRTC-owned tracks. Fix: route audio
  // via AudioContext → MediaStreamDestination. iOS allows recording that stream.
  const mediaRecAudioCtxRef = useRef<AudioContext | null>(null);

  // Recording stream — either from getUserMedia (preferred) or AudioContext destination
  const mediaRecDestRef = useRef<MediaStream | null>(null);
  const mediaRecGumRef = useRef<MediaStream | null>(null); // getUserMedia stream (stop tracks on cleanup)
  const mediaRecMimeRef = useRef<string>('');
  const mediaRecActiveRef = useRef<boolean>(false); // true while cycling is running
  const vadRef = useRef<ReturnType<typeof startVoiceActivityMeter>>(null); // hoppar över tysta bitar

  /**
   * En rad som den här telefonen hört (Groq-biten eller I2: telefonens egen taligenkänning) → jaktloggen, Q-fliken,
   * walkie-anrop ("Lasse till Ola …", ljudet hämtas bara då) och radion om den är på.
   */
  const publishHeard = useCallback((speakerName: string, text: string, ts: number, audio: Blob | null | (() => Promise<Blob | null>)) => {
    if (handleWakeRef.current(text)) return;
    const entry = { speaker: speakerName, text, ts, lat: userPositionRef.current?.lat, lng: userPositionRef.current?.lng };
    setTranscript(prev => {
      const next = [...prev, entry];
      transcriptRef.current = next;
      return next;
    });
    huntQRef.current.reportHeard(entry);
    huntQRef.current.routeWalkie(entry.text, audio);
    const r = lkRoomRef.current;
    if (r?.state === 'connected') {
      const payload = {
        type: TRANSCRIPT_DATA_V1,
        speaker: speakerName,
        senderIdentity: r.localParticipant.identity,
        text: entry.text,
        ts: entry.ts,
        lat: entry.lat,
        lng: entry.lng,
      };
      r.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(payload)), { reliable: true }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendBlobToGroq = useCallback(async (blob: Blob, speakerName: string) => {
    if (blob.size < 500) { console.warn('[QVENARI-TRANSCRIPT] Chunk too small, skipping'); return; }
    console.log('[QVENARI-TRANSCRIPT] Sending chunk:', blob.size, 'bytes, type:', blob.type);
    try {
      const fd = new FormData();
      const ext = mediaRecMimeRef.current.includes('mp4') ? '.mp4' : '.webm';
      fd.append('audio', blob, `chunk${ext}`);
      const res = await fetch(resolveUrl(`/api/transcribe?lang=${transcribeLangRef.current}`), { method: 'POST', body: fd });
      const json = await res.json();
      console.log('[QVENARI-TRANSCRIPT] API response:', json);
      if (res.ok && json.text && json.text.trim()) {
        publishHeard(speakerName, json.text.trim(), Date.now(), blob); // QJ3: walkie-anrop skickar biten som röstmeddelande
      }
    } catch (err) {
      console.warn('[QVENARI-TRANSCRIPT] chunk send error:', err);
    }
  }, [publishHeard]);

  // Skott hört av ljudmätningen → händelse med jägarens position, som en transkriberad rad.
  // Den delas med de andra jägarna och blir en Q-assistentrad ("Skott hört vid …") i jaktchatten.
  const recordShot = useCallback((speakerName: string) => {
    const entry = { speaker: speakerName, text: SHOT_EVENT_TEXT, ts: Date.now(), lat: userPositionRef.current?.lat, lng: userPositionRef.current?.lng };
    console.log('[QVENARI-TRANSCRIPT] Skott hört', entry);
    setTranscript(prev => {
      const next = [...prev, entry];
      transcriptRef.current = next;
      return next;
    });
    huntQRef.current.reportHeard(entry);
    const r = lkRoomRef.current;
    if (r?.state === 'connected') {
      const payload = { type: TRANSCRIPT_DATA_V1, speaker: speakerName, senderIdentity: r.localParticipant.identity, text: entry.text, ts: entry.ts, lat: entry.lat, lng: entry.lng };
      r.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(payload)), { reliable: true }).catch(() => {});
    }
  }, []);

  // Start one 15-second recording session; on onstop send blob then restart if still active
  const startOneCycle = useCallback((speakerName: string) => {
    if (!mediaRecActiveRef.current || !mediaRecDestRef.current) return;
    const dest = mediaRecDestRef.current;
    const mimeType = mediaRecMimeRef.current;
    // Resume AudioContext if used (iOS suspends it)
    if (mediaRecAudioCtxRef.current && mediaRecAudioCtxRef.current.state !== 'running') {
      mediaRecAudioCtxRef.current.resume().catch(() => {});
    }
    let mr: MediaRecorder;
    try {
      mr = new MediaRecorder(dest, mimeType ? { mimeType } : {});
    } catch (err) {
      console.error('[QVENARI-TRANSCRIPT] Failed to create MediaRecorder for cycle:', err);
      return;
    }
    const chunks: Blob[] = [];
    vadRef.current?.takeCycle(); // nollställ — mät bara den här biten
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    mr.onerror = (e: any) => {
      console.warn('[QVENARI-TRANSCRIPT] MediaRecorder onerror:', e?.error?.message || String(e?.error || e));
    };
    mr.onstop = () => {
      // Wrap async work in a self-contained promise so unhandled rejections never bubble up
      (async () => {
        try {
          const vad = vadRef.current?.takeCycle();
          if (chunks.length > 0 && (!vad || shouldSendChunk(vad))) {
            const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
            await sendBlobToGroq(blob, speakerName);
          } else if (vad) {
            console.log('[QVENARI-TRANSCRIPT] Tyst bit, skickas inte (röst', vad.voicedMs, 'ms)');
          }
          // Restart next cycle immediately if still active
          if (mediaRecActiveRef.current) startOneCycle(speakerName);
        } catch (e) {
          console.error('[QVENARI-TRANSCRIPT] onstop handler error:', e);
        }
      })();
    };
    mr.start(); // no timeslice — collect full segment
    mediaRecRef.current = mr;
    // Stop after 15 seconds — longer chunks = more context for Whisper = better transcription
    mediaRecIntervalRef.current = setTimeout(() => {
      if (mr.state === 'recording') mr.stop();
    }, 15000) as any;
  }, [sendBlobToGroq]);

  const startMediaRecorder = useCallback(async (speakerName: string) => {
    // Always use getUserMedia directly — transcription runs independently of LiveKit mic state.
    // iOS Safari suspends AudioContext automatically → silence → Groq gets 0 audio.
    // getUserMedia gives a "live" stream iOS never suspends.
    let recordingStream: MediaStream | null = null;

    try {
      const gum = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (mediaRecGumRef.current) {
        mediaRecGumRef.current.getTracks().forEach(t => t.stop());
      }
      mediaRecGumRef.current = gum;
      recordingStream = gum;
      console.log('[QVENARI-TRANSCRIPT] Using direct getUserMedia stream (iOS-safe)');
    } catch (gumErr) {
      // Fallback: route LiveKit mic track via AudioContext
      console.warn('[QVENARI-TRANSCRIPT] getUserMedia failed, falling back to AudioContext:', gumErr);
      try {
        const room = lkRoomRef.current;
        const pub = room?.localParticipant.getTrackPublication(Track.Source.Microphone);
        const mst = (pub?.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
        if (!mst) { console.error('[QVENARI-TRANSCRIPT] No mic source available'); return; }
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (mediaRecAudioCtxRef.current) { try { mediaRecAudioCtxRef.current.close(); } catch {} }
        const ctx = new AudioCtx();
        await ctx.resume();
        mediaRecAudioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(new MediaStream([mst]));
        const dest = ctx.createMediaStreamDestination();
        source.connect(dest);
        recordingStream = dest.stream;
        console.log('[QVENARI-TRANSCRIPT] Using AudioContext routing, state:', ctx.state);
      } catch (err) {
        console.error('[QVENARI-TRANSCRIPT] AudioContext routing failed:', err);
        return;
      }
    }

    mediaRecDestRef.current = recordingStream;
    vadRef.current?.stop();
    vadRef.current = startVoiceActivityMeter(recordingStream, { onShot: () => recordShot(speakerName) });

    // Pick best mimeType — iOS prefers mp4, desktop prefers webm
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (isIOS) {
      mediaRecMimeRef.current = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : '';
    } else {
      mediaRecMimeRef.current = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : '';
    }

    mediaRecActiveRef.current = true;
    setTranscribeMode('recorder');
    console.log('[QVENARI-TRANSCRIPT] Starting stop-restart cycle, mimeType:', mediaRecMimeRef.current);
    startOneCycle(speakerName);
  }, [startOneCycle, recordShot]);

  const stopMediaRecorder = useCallback(() => {
    mediaRecActiveRef.current = false; // prevent next cycle from starting
    vadRef.current?.stop(); vadRef.current = null;
    if (mediaRecIntervalRef.current) { clearTimeout(mediaRecIntervalRef.current as any); mediaRecIntervalRef.current = null; }
    if (mediaRecRef.current) { try { mediaRecRef.current.stop(); } catch {} mediaRecRef.current = null; }
    if (mediaRecGumRef.current) { mediaRecGumRef.current.getTracks().forEach(t => t.stop()); mediaRecGumRef.current = null; }
    if (mediaRecAudioCtxRef.current) { try { mediaRecAudioCtxRef.current.close(); } catch {} mediaRecAudioCtxRef.current = null; }
    mediaRecDestRef.current = null;
    mediaRecChunksRef.current = [];
  }, []);

  // ── Start / Stop Web Speech API transcription (desktop fallback) ──
  const startWebSpeech = useCallback((speakerName: string) => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      console.warn('[QVENARI-TRANSCRIPT] SpeechRecognition not supported — using MediaRecorder');
      setSpeechSupported(false);
      setTimeout(() => startMediaRecorder(speakerName), 400);
      return;
    }
    setSpeechSupported(true);
    if (speechRef.current) { try { speechRef.current.stop(); } catch {} }

    const sr: any = new SR();
    sr.lang = transcribeLangRef.current === 'en' ? 'en-US' : 'sv-SE';
    sr.continuous = true;
    sr.interimResults = true;
    sr.maxAlternatives = 1;

    // If no result arrives within 8s, fall back to MediaRecorder
    let gotResult = false;
    const fallbackTimer = setTimeout(() => {
      if (!gotResult && transcribingRef.current) {
        console.warn('[QVENARI-TRANSCRIPT] No speech results — falling back to MediaRecorder');
        try { sr.stop(); } catch {}
        speechRef.current = null;
        startMediaRecorder(speakerName);
      }
    }, 8000);

    sr.onresult = (e: any) => {
      gotResult = true;
      clearTimeout(fallbackTimer);
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          if (handleWakeRef.current(t.trim())) { setLiveText(''); continue; }
          const entry = { speaker: speakerName, text: t.trim(), ts: Date.now(), lat: userPositionRef.current?.lat, lng: userPositionRef.current?.lng };
          setTranscript(prev => {
            const next = [...prev, entry];
            transcriptRef.current = next;
            return next;
          });
          huntQRef.current.reportHeard(entry);
          huntQRef.current.routeWalkie(entry.text, null); // utan ljud: texten läses upp hos mottagaren
          setLiveText('');
          const r = lkRoomRef.current;
          if (r?.state === 'connected') {
            const payload = {
              type: TRANSCRIPT_DATA_V1,
              speaker: speakerName,
              senderIdentity: r.localParticipant.identity,
              text: entry.text,
              ts: entry.ts,
              lat: entry.lat,
              lng: entry.lng,
            };
            r.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(payload)), { reliable: true }).catch(() => {});
          }
        } else {
          interim += t;
        }
      }
      if (interim) {
        setLiveText(interim);
        const r = lkRoomRef.current;
        if (r?.state === 'connected' && interim.trim()) {
          const now = Date.now();
          if (now - transcriptLiveThrottleRef.current > 450) {
            transcriptLiveThrottleRef.current = now;
            r.localParticipant.publishData(
              new TextEncoder().encode(JSON.stringify({
                type: TRANSCRIPT_LIVE_V1,
                speaker: speakerName,
                senderIdentity: r.localParticipant.identity,
                text: interim.slice(-800),
              })),
              { reliable: false },
            ).catch(() => {});
          }
        }
      }
    };

    sr.onerror = (e: any) => {
      clearTimeout(fallbackTimer);
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setSpeechSupported(false);
        speechRef.current = null;
        console.warn('[QVENARI-TRANSCRIPT] Not allowed — trying MediaRecorder');
        setTimeout(() => { if (transcribingRef.current) startMediaRecorder(speakerName); }, 400);
        return;
      }
      if (e.error === 'audio-capture') {
        setSpeechSupported(false);
        speechRef.current = null;
        console.warn('[QVENARI-TRANSCRIPT] audio-capture conflict — falling back to MediaRecorder');
        setTimeout(() => { if (transcribingRef.current) startMediaRecorder(speakerName); }, 400);
        return;
      }
      console.warn('[QVENARI-TRANSCRIPT] SpeechRecognition error:', e.error);
    };

    sr.onend = () => {
      clearTimeout(fallbackTimer);
      // Auto-restart if mic is still on AND we haven't switched to MediaRecorder
      if (speechRef.current === sr && transcribingRef.current && transcribeMode !== 'recorder') {
        try { sr.start(); } catch {}
      }
    };

    sr.start();
    speechRef.current = sr;
    setTranscribeMode('speech');
  }, [startMediaRecorder]);

  // ── I2: telefonens egen taligenkänning (src/q/huntListen.ts, QHuntListenPlugin i QVoicePlugin.swift) ──
  // I iOS-appen tolkas talet i telefonen i stället för att 15 s-bitar skickas till Groq. Groq (startWebSpeech) används
  // när det inte går, och medan LiveKit-mikrofonen är på: då äger WebKit mikrofonen (regel 1 i QVoicePlugin.swift).
  const nativeListenRef = useRef<NativeHuntListen | null>(null);
  const nativeListenGenRef = useRef(0);
  const startSpeech = useCallback((speakerName: string) => {
    if (!nativeHuntListenSupported() || micOnRef.current) { startWebSpeech(speakerName); return; }
    const gen = ++nativeListenGenRef.current;
    let session: NativeHuntListen | null = null;
    void startNativeHuntListen(transcribeLangRef.current, {
      onHeard: e => publishHeard(speakerName, e.text, e.ts, () => session ? session.audio(e.id) : Promise.resolve(null)),
      onPartial: text => setLiveText(text),
      onShot: () => recordShot(speakerName),
      onLost: reason => {
        console.warn('[QVENARI-TRANSCRIPT] Telefonens lyssning slutade (' + reason + ') – byter till Groq');
        if (nativeListenRef.current === session) nativeListenRef.current = null;
        if (transcribingRef.current && gen === nativeListenGenRef.current) startWebSpeech(speakerName);
      },
    }).then(s => {
      session = s;
      if (gen !== nativeListenGenRef.current || !transcribingRef.current) { void s?.stop(); return; }
      if (!s) { startWebSpeech(speakerName); return; }
      console.log('[QVENARI-TRANSCRIPT] Lyssnar i telefonen (lokal taligenkänning)');
      nativeListenRef.current = s;
      setSpeechSupported(true);
      setTranscribeMode('native');
    });
  }, [startWebSpeech, publishHeard, recordShot]);

  const stopSpeech = useCallback(() => {
    nativeListenGenRef.current++;
    const native = nativeListenRef.current;
    nativeListenRef.current = null;
    void native?.stop();
    if (speechRef.current) { try { speechRef.current.stop(); } catch {} speechRef.current = null; }
    stopMediaRecorder();
    setLiveText('');
    setTranscribeMode('speech');
  }, [stopMediaRecorder]);

  // ── Audio level meter ──
  const startAudioMeter = useCallback(() => {
    const room = lkRoomRef.current;
    if (!room) return;
    const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
    const mediaStreamTrack = (pub?.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
    if (!mediaStreamTrack) { console.warn('[QVENARI-MIC] No MediaStreamTrack for audio meter'); return; }
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const animRef = { id: 0 };
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setMicLevel(Math.min(100, avg * 2.8));
        animRef.id = requestAnimationFrame(tick);
        if (micAnalyserRef.current) micAnalyserRef.current.animId = animRef.id;
      };
      animRef.id = requestAnimationFrame(tick);
      micAnalyserRef.current = { ctx, analyser, data, animId: animRef.id };
      console.log('[QVENARI-MIC] Audio meter started');
    } catch (e) { console.warn('[QVENARI-MIC] AudioContext failed', e); }
  }, []);

  const stopAudioMeter = useCallback(() => {
    if (!micAnalyserRef.current) return;
    cancelAnimationFrame(micAnalyserRef.current.animId);
    try { micAnalyserRef.current.ctx.close(); } catch {}
    micAnalyserRef.current = null;
    setMicLevel(0);
  }, []);


  // Toggle mic — guarded
  const micTogglingRef = useRef(false);
  const toggleMic = async () => {
    const room = await ensureRoom();
    console.log('[QVENARI-MEET] toggleMic called', { hasRoom: !!room, roomState: room?.state, micTogglingLocked: micTogglingRef.current, micOn });
    if (!room) {
      console.error('[QVENARI-MEET] toggleMic: no room object');
      return;
    }
    if (micTogglingRef.current) { console.warn('[QVENARI-MEET] toggleMic: LOCKED by micTogglingRef — skipping'); return; }
    micTogglingRef.current = true;
    const next = !micOn;
    try {
      // If not connected yet, wait briefly for connection
      if (room.state !== 'connected') {
        console.log('[QVENARI-MEET] toggleMic: waiting for connection...', room.state);
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Timeout waiting for LiveKit connection')), 5000);
          if (room.state === 'connected') { clearTimeout(timeout); resolve(); return; }
          room.once(RoomEvent.Connected, () => { clearTimeout(timeout); resolve(); });
          room.once(RoomEvent.Disconnected, () => { clearTimeout(timeout); reject(new Error('Disconnected')); });
        });
      }
      // I2: WebKit tar mikrofonen för radion – telefonens egen lyssning släpper den först och jaktloggen går via
      // Groq medan radion sänder; när radion tystnar tar telefonen över igen (regel 1 i QVoicePlugin.swift).
      const swapListening = transcribingRef.current && nativeHuntListenSupported();
      if (next && swapListening && nativeListenRef.current) { stopSpeech(); await new Promise(r => setTimeout(r, 300)); }
      await room.localParticipant.setMicrophoneEnabled(next);
      setMicOn(next);
      micOnRef.current = next;
      if (swapListening) {
        stopSpeech();
        setTimeout(() => { if (transcribingRef.current && micOnRef.current === next) startSpeech(userName || 'Jägare'); }, next ? 0 : 300);
      }
      if (next) {
        setTimeout(() => startAudioMeter(), 350); // let LiveKit publish track first
      } else {
        stopAudioMeter();
      }
    } catch (err: any) {
      const msg = err instanceof Event ? `WebRTC event: ${(err as any).type || 'unknown'}` : (err?.message || String(err));
      console.error('[QVENARI-MEET] toggleMic FAILED', msg, err);
    } finally {
      setTimeout(() => { micTogglingRef.current = false; }, 300);
    }
  };

  // Toggle speaker (mute all remote audio)
  const toggleSpeaker = () => {
    const room = lkRoomRef.current;
    console.log('[QVENARI-MEET] toggleSpeaker called', { hasRoom: !!room, roomState: room?.state, speakerOn, lkConnected });
    if (!room) {
      // MT1: ingen radio än – högtalaren styr ändå uppspelningen av röstmeddelanden via Q.
      setSpeakerOn(!speakerOn);
      return;
    }
    if (room.state !== 'connected') {
      console.warn('[QVENARI-MEET] toggleSpeaker: room not connected yet, state =', room.state);
    }
    const next = !speakerOn;
    let tracksToggled = 0;
    room.remoteParticipants.forEach((p: RemoteParticipant) => {
      p.trackPublications.forEach((pub: RemoteTrackPublication) => {
        if (pub.track?.source === Track.Source.Microphone) {
          if (next) pub.track.mediaStreamTrack.enabled = true;
          else pub.track.mediaStreamTrack.enabled = false;
          tracksToggled++;
        }
      });
    });
    setSpeakerOn(next);
    console.log('[QVENARI-MEET] toggleSpeaker result', { newState: next ? 'on' : 'muted', tracksToggled, remoteParticipants: room.remoteParticipants.size });
  };

  // Toggle cam (own camera) — guarded against rapid toggling
  /** force: true/false slår på/av oavsett förhandsvisning (röstkommando); utan force växlar knappen. */
  const toggleCam = async (force?: boolean) => {
    const room = force === false ? lkRoomRef.current : await ensureRoom();
    console.log('[QVENARI-MEET] toggleCam called', {
      hasRoom: !!room,
      roomState: room?.state,
      camTogglingLocked: camTogglingRef.current,
      camOn,
      showOwnCam,
    });

    if (!room) {
      console.error('[QVENARI-MEET] toggleCam: NO ROOM — LiveKit not connected');
      return;
    }
    if (camTogglingRef.current) {
      console.warn('[QVENARI-MEET] toggleCam: LOCKED by camTogglingRef — skipping');
      return;
    }

    // If camera is already on but preview was hidden (user pressed X), just re-show preview
    if (force === undefined && camOn && !showOwnCam) {
      setShowOwnCam(true);
      if (ownVideoElemRef.current) {
        const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
        if (pub?.track) {
          try { pub.track.detach(ownVideoElemRef.current); } catch {}
          pub.track.attach(ownVideoElemRef.current);
        }
      }
      return;
    }

    const next = force ?? !camOn;
    if (next === camOn) return;
    camTogglingRef.current = true;
    console.log('[QVENARI-MEET] toggleCam start setCameraEnabled', { next, roomState: room.state });
    try {
      // If not connected yet, wait briefly for connection
      if (room.state !== 'connected') {
        console.log('[QVENARI-MEET] toggleCam: waiting for connection...', room.state);
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Timeout waiting for LiveKit connection')), 5000);
          if (room.state === 'connected') { clearTimeout(timeout); resolve(); return; }
          room.once(RoomEvent.Connected, () => { clearTimeout(timeout); resolve(); });
          room.once(RoomEvent.Disconnected, () => { clearTimeout(timeout); reject(new Error('Disconnected')); });
        });
      }
      const pub = await room.localParticipant.setCameraEnabled(
        next,
        next ? { facingMode: camFacingModeRef.current } : undefined,
      );
      console.log('[QVENARI-MEET] setCameraEnabled resolved', { next, trackSid: pub?.trackSid, hasTrack: !!pub?.track });
      setCamOn(next);
      camOnRef.current = next;
      if (next) {
        setShowOwnCam(true);
        // Attach with retry — DOM element may not be ready yet
        const doAttach = (attempt: number) => {
          const el = ownVideoElemRef.current;
          const track = pub?.track || room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
          if (el && track) {
            try { track.detach(el); } catch {}
            track.attach(el);
            console.log('[QVENARI-MEET] camera attached to video element (attempt', attempt, ')');
          } else if (attempt < 8) {
            setTimeout(() => doAttach(attempt + 1), 150);
          } else {
            console.warn('[QVENARI-MEET] camera attach gave up after 8 attempts');
          }
        };
        doAttach(0);
      } else {
        setShowOwnCam(false);
        setCamFacingMode('environment');
        camFacingModeRef.current = 'environment';
        const el = ownVideoElemRef.current;
        if (el) {
          const offPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
          if (offPub?.track) offPub.track.detach(el);
          el.srcObject = null;
        }
      }
    } catch (err: any) {
      const msg = err instanceof Event ? `WebRTC event: ${(err as any).type || 'unknown'}` : (err?.message || String(err));
      console.error('[QVENARI-MEET] toggleCam FAILED', msg, err);
    } finally {
      setTimeout(() => { camTogglingRef.current = false; }, 300);
    }
  };
  toggleCamRef.current = toggleCam;

  /** MG1: dela glasögonkameran i stället för telefonens kamera (samma kameraplats i rummet). */
  const toggleGlasses = async () => {
    const room = lkRoomRef.current;
    if (glassesShareRef.current) {
      const share = glassesShareRef.current;
      glassesShareRef.current = null;
      await share.stop();
      return;
    }
    const lkRoom = room ?? await ensureRoom(); // MT1: glasögonkameran startar radion
    if (!lkRoom) { setGlassesMsg('Väntar på anslutning till jaktmötet'); return; }
    if (camOnRef.current) await toggleCam(false);
    setGlassesError(false);
    setGlassesDiag(null);
    const share = new GlassesShare((e: GlassesStateEvent) => {
      if (glassesShareRef.current !== share) return;
      if (e.diag) setGlassesDiag(e.diag);
      if (e.state === 'streaming') {
        setGlassesMsg(null);
        const el = glassesVideoRef.current;
        if (el && share.localTrack) share.localTrack.attach(el);
      } else if (e.state === 'stopped') {
        glassesShareRef.current = null;
        setGlassesOn(false);
      } else if (e.state === 'paused') {
        setGlassesMsg('Pausad på glasögonen. Tryck på glasögonens sida för att fortsätta.');
      } else if (e.state === 'error' && e.message) {
        setGlassesMsg(e.message);
        setGlassesError(true);
      } else if (e.message) {
        setGlassesMsg(e.message);
      }
    });
    glassesShareRef.current = share;
    setGlassesOn(true);
    setGlassesMsg('Ansluter till glasögonen');
    try {
      await share.start(lkRoom);
    } catch (err: any) {
      glassesShareRef.current = null;
      setGlassesOn(false);
      setGlassesError(true);
      setGlassesMsg(err?.message || 'Kunde inte dela glasögonkameran');
      if (err?.diag) setGlassesDiag(err.diag);
    }
  };

  /** Videoruta i helskärm: samma ström som rutan man tryckte på. */
  const [videoFull, setVideoFull] = useState<{ stream: MediaStream; mirror: boolean } | null>(null);
  const openVideoFull = (e: React.MouseEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (v.srcObject instanceof MediaStream) setVideoFull({ stream: v.srcObject, mirror: v.style.transform.includes('scaleX(-1)') });
  };
  const closeVideoFull = useCallback(() => setVideoFull(null), []);

  /** Kameraväljaren: vilken källa som delas just nu. */
  const [showCamPicker, setShowCamPicker] = useState(false);
  const camSource: CameraSource | null = glassesOn ? 'glasses' : camOn ? (camFacingMode === 'user' ? 'selfie' : 'back') : null;
  camSourceRef.current = camSource;
  /** Byter kamerakälla utan att lämna rummet: den gamla stoppas, den nya publiceras som kameraspår. */
  const selectCamSource = async (src: CameraSource) => {
    if (src === 'drone') return; // I3: inte byggt ännu
    if (src === 'glasses') { if (!glassesShareRef.current) await toggleGlasses(); return; }
    const facing: 'environment' | 'user' = src === 'selfie' ? 'user' : 'environment';
    if (glassesShareRef.current) {
      const share = glassesShareRef.current;
      glassesShareRef.current = null;
      await share.stop();
      setGlassesOn(false);
      setGlassesMsg(null);
    }
    if (camOnRef.current) {
      if (camFacingModeRef.current !== facing) await flipCameraFacing();
      else setShowOwnCam(true);
      return;
    }
    camFacingModeRef.current = facing;
    setCamFacingMode(facing);
    await toggleCam(true);
  };
  const stopCamSource = async () => {
    if (glassesShareRef.current) {
      const share = glassesShareRef.current;
      glassesShareRef.current = null;
      await share.stop();
      setGlassesOn(false);
      setGlassesMsg(null);
    }
    if (camOnRef.current) await toggleCam(false);
  };

  /** Växla mellan bak- och selfiekamera medan sändningen är på. */
  const flipCameraFacing = useCallback(async () => {
    const room = lkRoomRef.current;
    if (!room || camTogglingRef.current || !camOnRef.current) return;
    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const track = pub?.track;
    if (!track || track.kind !== Track.Kind.Video) return;
    const v = track as LocalVideoTrack;
    if (typeof v.restartTrack !== 'function') return;
    camTogglingRef.current = true;
    const nextMode: 'environment' | 'user' = camFacingModeRef.current === 'environment' ? 'user' : 'environment';
    try {
      await v.restartTrack({ facingMode: nextMode });
      setCamFacingMode(nextMode);
      camFacingModeRef.current = nextMode;
      const el = ownVideoElemRef.current;
      if (el) {
        try { v.detach(el); } catch { /* noop */ }
        v.attach(el);
      }
    } catch (err) {
      console.warn('[QVENARI-MEET] flipCameraFacing failed', err);
    } finally {
      setTimeout(() => { camTogglingRef.current = false; }, 400);
    }
  }, []);

  // View a remote participant's camera
  const viewRemoteCam = (identity: string) => {
    const room = lkRoomRef.current;
    if (!room) return;
    const p = room.remoteParticipants.get(identity);
    if (!p) return;
    const camPub = Array.from(p.trackPublications.values()).find(
      (t: RemoteTrackPublication) => t.source === Track.Source.Camera && !t.isMuted,
    );
    const vt = camPub?.track;
    const el = remoteVideoRef.current;
    if (!vt || vt.kind !== Track.Kind.Video || !el) return;
    const rv = vt as RemoteVideoTrack;
    if (remotePiPVideoTrackRef.current && remotePiPVideoTrackRef.current !== rv) {
      try { remotePiPVideoTrackRef.current.detach(el); } catch { /* noop */ }
    }
    remotePiPVideoTrackRef.current = rv;
    try { rv.detach(el); } catch { /* noop */ }
    try { rv.attach(el); } catch (e) { console.warn('[QVENARI-MEET] PiP video attach', e); }
    setViewingParticipantId(p.identity);
  };

  const detachRemotePiP = () => {
    const el = remoteVideoRef.current;
    if (el && remotePiPVideoTrackRef.current) {
      try { remotePiPVideoTrackRef.current.detach(el); } catch { /* noop */ }
      remotePiPVideoTrackRef.current = null;
    }
    if (el) el.srcObject = null;
  };

  // Leader / featured fjärrvideo: LiveKit attach (fungerar med simulcast / intern uppspelning)
  useEffect(() => {
    const el = leaderVideoRef.current;
    if (!el) return;
    const t = leaderVideoTrack;
    if (t) {
      try {
        t.attach(el);
      } catch (e) {
        console.warn('[QVENARI-MEET] featured remote video attach', e);
      }
    } else {
      el.srcObject = null;
    }
    return () => {
      if (t) try { t.detach(el); } catch { /* noop */ }
    };
  }, [leaderVideoTrack]);

  // ── Camera watchdog: every 12s re-attach if cam is on but video may be frozen ──
  useEffect(() => {
    const id = setInterval(() => {
      if (!camOn || !ownVideoElemRef.current) return;
      const room = lkRoomRef.current;
      if (!room) return;
      const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      if (pub?.track) {
        try { pub.track.detach(ownVideoElemRef.current); } catch {}
        try { pub.track.attach(ownVideoElemRef.current); } catch {}
      }
    }, 12000);
    return () => clearInterval(id);
  }, [camOn]);

  // Q under jakt (q/huntPause.ts): transkriptionen pausas medan Q lyssnar eller pratar, och LiveKit-mikrofonen
  // är av medan Q lyssnar. Funktionerna läses via ref, så att pausen överlever omritningar.
  const huntAudioRef = useRef({ stopSpeech, startSpeech, userName });
  huntAudioRef.current = { stopSpeech, startSpeech, userName };
  const onQVoiceActivity = useMemo(() => createQHuntPause({
    transcribing: () => transcribingRef.current,
    stopTranscription: () => { transcribingRef.current = false; huntAudioRef.current.stopSpeech(); },
    startTranscription: () => { transcribingRef.current = true; huntAudioRef.current.startSpeech(huntAudioRef.current.userName || 'Jägare'); },
    micOn: () => micOnRef.current,
    setMic: on => { void lkRoomRef.current?.localParticipant.setMicrophoneEnabled(on).catch(() => {}); },
    huntActive: () => !!huntSessionRef.current,
  }), []);

  // ── Stop speech when hunt ends (transcript is preserved for App.tsx to save) ──
  useEffect(() => {
    if (!huntSession) {
      transcribingRef.current = false;
      stopSpeech();
      // I2: en pågående inspelning till jaktchatten kastas.
      if (voiceRecRef.current) { voiceRecRef.current.rec.cancel(); voiceRecRef.current = null; setVoiceRec(null); }
      setShowVideoRec(false);
      stopAudioMeter();
      // Do NOT clear transcript here — App.tsx saves it via onEndHunt callback first.
      // Transcript is cleared when a new hunt starts (see below).
      setLiveText('');
    }
  }, [huntSession]); // eslint-disable-line

  // Clear transcript and start transcription when a new hunt starts
  useEffect(() => {
    if (huntSession) {
      setTranscript([]);
      transcriptRef.current = [];
      setShowHuntChat(false);
      setRemoteLiveLines({});
      setLiveText('');
      setHuntChatRows([]);
      assistantInsightIdsRef.current.clear();
      setHuntChatDraft('');
      radioForRef.current = null;
      setRadioFor(null);
      setLkCreds(null);
      // Start transcription immediately — always listening regardless of mic state
      transcribingRef.current = true;
      startSpeech(userName || 'Jägare');
      // Track local start time and persist for rejoin
      huntLocalStartRef.current = Date.now();
      try {
        safeLocalStorageSet(`qvenari_recent_hunt_${mark.id}`, JSON.stringify({
          meetingId: huntSession.meetingId,
          title: huntSession.title,
          startedAt: new Date().toISOString(),
        }));
      } catch (_) {}
    }
  }, [huntSession?.livekitToken]); // eslint-disable-line

  useEffect(() => {
    if (!huntSession) return;
    const t = window.setTimeout(() => {
      // Read chat rows from ref to avoid creating a state dependency (would cause loop)
      const chatAsTranscript = huntChatRowsRef.current
        .filter((r): r is Extract<HuntChatRow, {kind:'user'}> => r.kind === 'user' && !!r.text)
        .map(r => ({ speaker: r.senderName, text: r.text!, ts: r.ts }));
      const combined = [...transcriptRef.current, ...chatAsTranscript];
      const insights = extractAssistantInsights(combined);
      const newInsights: HuntChatRow[] = [];
      for (const ins of insights) {
        if (assistantInsightIdsRef.current.has(ins.id)) continue;
        assistantInsightIdsRef.current.add(ins.id);
        // QV1: fällt vilt med underkategorier (älg, hjort, rådjur, vildsvin …) får knappar för typen; hördes typen ("dovkalv") bara den.
        const familySubcats = ins.isKill && ins.family ? SUBCAT_MAP[ins.family] : undefined;
        const subcats = familySubcats?.length && ins.gameType
          ? matchingSubcats({ family: ins.family!, label: ins.gameType, subcat: ins.subcat, kon: ins.kon, alder: ins.alder }, familySubcats).map(sc => `${ins.gameType} — ${sc}`)
          : undefined;
        newInsights.push({ kind: 'assistant', id: ins.id, text: ins.text, inferredFrom: ins.inferredFrom, ts: Date.now(), gameType: ins.gameType, isKill: ins.isKill, lat: ins.lat, lng: ins.lng, subcats, kon: ins.kon, alder: ins.alder, wounded: ins.outcome === 'skadskjutet' });
      }
      if (newInsights.length > 0) {
        setHuntChatRows(prev => [...prev, ...newInsights]);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [transcript, userChatCount, huntSession?.meetingId]); // eslint-disable-line

  useEffect(() => {
    if (!showHuntChat || !huntChatScrollRef.current) return;
    const el = huntChatScrollRef.current;
    el.scrollTop = el.scrollHeight;
  }, [huntChatRows, showHuntChat]);

  // Auto-enter fullscreen when a hunt session starts
  useEffect(() => {
    if (huntSession) {
      setFullscreen(true);
    }
  }, [huntSession?.meetingId]); // eslint-disable-line


  // Navigation line
  const navLineRef = useRef<any>(null);
  const navLabelRef = useRef<any>(null);
  const [navigatingTo, setNavigatingTo] = useState<MarkPin | null>(null);

  // Compass & location tracking
  const [compassActive, setCompassActive] = useState(false);
  const [userPosition, setUserPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [heading, setHeading] = useState<number | null>(null);
  const [gpsStatus, setGpsStatus] = useState<'pending' | 'active' | 'denied' | 'unavailable'>('pending');
  const [mapReady, setMapReady] = useState(false);
  const userMarkerRef = useRef<any>(null);
  const userAccuracyRef = useRef<any>(null);
  const distanceRingsRef = useRef<any[]>([]);
  const ringLabelsRef = useRef<any[]>([]);
  const geoWatchRef = useRef<number | null>(null);
  const headingRef = useRef<number | null>(null);
  const compassActiveRef = useRef(false);
  /** UX14: i kompassläget pausas följningen när man drar kartan; Min position eller en stund utan dragning återupptar den. */
  const [followPaused, setFollowPaused] = useState(false);
  const followPausedRef = useRef(false);
  const followResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseFollow = useCallback((phase: 'start' | 'end') => {
    if (!compassActiveRef.current) return;
    if (followResumeTimerRef.current) { clearTimeout(followResumeTimerRef.current); followResumeTimerRef.current = null; }
    followPausedRef.current = true;
    setFollowPaused(true);
    if (phase === 'end') followResumeTimerRef.current = setTimeout(() => resumeFollowRef.current(), FOLLOW_RESUME_MS);
  }, []);
  const resumeFollowRef = useRef(() => {});
  resumeFollowRef.current = () => {
    if (followResumeTimerRef.current) { clearTimeout(followResumeTimerRef.current); followResumeTimerRef.current = null; }
    followPausedRef.current = false;
    setFollowPaused(false);
    const p = userPositionRef.current;
    if (p && compassActiveRef.current) mapRef.current?.panTo([p.lat, p.lng], { animate: true });
  };
  useEffect(() => () => { if (followResumeTimerRef.current) clearTimeout(followResumeTimerRef.current); }, []);
  /** Tar bort kompassens deviceorientation-lyssnare (annars blir det en ny vid varje påslagning). */
  const stopOrientationRef = useRef<(() => void) | null>(null);
  const showDistanceRingsRef = useRef(showDistanceRings);
  const userPositionRef = useRef<{ lat: number; lng: number } | null>(null);
  const updateDistanceRingsRef = useRef<((pos: { lat: number; lng: number }, map: any) => void) | null>(null);
  const vittringLayerRef = useRef<any>(null);

  // Jägarens position avrundad (~100 m) så att skjutmenyn inte hämtas om vid varje GPS-punkt.
  const skjutmenyLat = userPosition ? Math.round(userPosition.lat * 1000) / 1000 : null;
  const skjutmenyLng = userPosition ? Math.round(userPosition.lng * 1000) / 1000 : null;
  useEffect(() => {
    if (!showSkjutmenyModal || !huntSession) return;
    let cancelled = false;
    if (!skjutmeny) setSkjutmenyLoading(true);
    api.markSkjutmeny(mark.id, { lat: skjutmenyLat, lng: skjutmenyLng })
      .then(m => { if (!cancelled) { setSkjutmeny(m); setSkjutmenyError(null); } })
      .catch(e => { if (!cancelled) setSkjutmenyError(e instanceof Error ? e.message : 'Skjutmenyn kunde inte hämtas'); })
      .finally(() => { if (!cancelled) setSkjutmenyLoading(false); });
    return () => { cancelled = true; };
  }, [showSkjutmenyModal, huntSession, mark.id, skjutmenyLat, skjutmenyLng, skjutmenyTick]); // eslint-disable-line

  // Tid på dygnet ändras: hämta om varje minut medan skjutmenyn är öppen.
  useEffect(() => {
    if (!showSkjutmenyModal) return;
    const id = setInterval(() => setSkjutmenyTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, [showSkjutmenyModal]);


  // ── Garmin dog positions ──────────────────────────────────────
  const [dogPositions, setDogPositions] = useState<any[]>([]);
  const dogMarkersRef = useRef<any[]>([]);
  const dogPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── PAJ GPS-hundar (H1): servern hämtar från PAJ och cachar, kartan läser vårt API ──
  const [pajDogs, setPajDogs] = useState<PajDogPosition[]>([]);
  const [pajTracks, setPajTracks] = useState<PajDogTrack[]>([]);
  const [pajNow, setPajNow] = useState(() => Date.now());
  useEffect(() => {
    if (!token || !mark?.id) { setPajDogs([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiFetch(`/api/integrations/${mark.id}/paj/positions`, { headers: bearerHeaders(token) });
        if (r.ok && !cancelled) setPajDogs(await r.json());
      } catch { /* äldre API utan PAJ, eller tillfälligt nätfel */ }
      if (!cancelled) setPajNow(Date.now());
    };
    void load();
    const tid = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(tid); };
  }, [mark?.id, token]);

  // Distance ring helper: pick nice round distances based on zoom
  const getDistanceRings = useCallback((zoom: number): number[] => getDistanceRingsFor(zoom), []);

  const haversine = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
    const R = 6371000;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const sinA = Math.sin(dLat / 2);
    const sinB = Math.sin(dLng / 2);
    const c = sinA * sinA + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * sinB * sinB;
    return R * 2 * Math.atan2(Math.sqrt(c), Math.sqrt(1 - c));
  };

  const clearNavLine = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (navLineRef.current) { map.removeLayer(navLineRef.current); navLineRef.current = null; }
    if (navLabelRef.current) { map.removeLayer(navLabelRef.current); navLabelRef.current = null; }
    setNavigatingTo(null);
  }, []);

  const drawNavLine = useCallback((from: { lat: number; lng: number }, to: { lat: number; lng: number }, pin: MarkPin) => {
    const map = mapRef.current;
    if (!map) return;

    // Remove old nav line
    if (navLineRef.current) map.removeLayer(navLineRef.current);
    if (navLabelRef.current) map.removeLayer(navLabelRef.current);

    // Dashed line
    const line = L.polyline([[from.lat, from.lng], [to.lat, to.lng]], {
      color: pinConfig(pin.type).color,
      weight: 3,
      dashArray: '10, 8',
      opacity: 0.9,
      interactive: false,
    });
    line.addTo(map);
    navLineRef.current = line;

    // Distance label at midpoint
    const midLat = (from.lat + to.lat) / 2;
    const midLng = (from.lng + to.lng) / 2;
    const dist = haversine(from, to);
    const label = L.marker([midLat, midLng], {
      icon: L.divIcon({
        html: `<div style="background:${T.surface};color:${pinConfig(pin.type).color};font-size:12px;font-weight:700;padding:3px 10px;border-radius:3px;white-space:nowrap;border:1px solid ${pinConfig(pin.type).color};box-shadow:0 2px 8px rgba(0,0,0,0.35)">${formatDistance(dist)}</div>`,
        className: '',
        iconAnchor: [30, 12],
      }),
      interactive: false,
    });
    label.addTo(map);
    navLabelRef.current = label;

    // Fit both points in view
    map.fitBounds([[from.lat, from.lng], [to.lat, to.lng]], { padding: [60, 60], maxZoom: 16 });
  }, [T.surface]);

  // Update distance rings on map
  const updateDistanceRings = useCallback((pos: { lat: number; lng: number }, map: any) => {
    // Remove old rings and labels
    distanceRingsRef.current.forEach(c => map.removeLayer(c));
    ringLabelsRef.current.forEach(l => map.removeLayer(l));
    distanceRingsRef.current = [];
    ringLabelsRef.current = [];

    if (!showDistanceRings) return;

    const zoom = map.getZoom();
    const distances = getDistanceRings(zoom);

    distances.forEach((dist, i) => {
      const circle = L.circle([pos.lat, pos.lng], {
        radius: dist,
        color: '#EE8F09',
        weight: 2,
        fillColor: 'transparent',
        fillOpacity: 0,
        opacity: ringOpacity(i),
        dashArray: '16,12',
        interactive: false,
      });
      circle.addTo(map);
      distanceRingsRef.current.push(circle);

      // Label at top of circle (north point)
      const labelLatLng = L.latLng(...ringLabelPoint(pos, dist));

      // Motverka kartcontainerns rotate(-heading) så texten förblir läsbar rätt mot telefonen i kompassläge
      const labelRot =
        compassActiveRef.current && headingRef.current !== null
          ? `transform:rotate(${headingRef.current}deg);transform-origin:center center;`
          : '';

      const label = L.marker(labelLatLng, {
        icon: L.divIcon({
          html: `<div data-ring-label="1" style="${labelRot}color:#D97E00;font-size:12px;font-weight:800;white-space:nowrap;text-align:center;text-shadow:0 0 2px #fff,0 0 2px #fff,0 0 3px #fff,0 0 4px rgba(255,255,255,0.9)">${formatDistance(dist)}</div>`,
          className: '',
          iconSize: [64, 16],
          iconAnchor: [32, 8], // centrerad på ringen
        }),
        interactive: false,
      });
      label.addTo(map);
      ringLabelsRef.current.push(label);
    });
  }, [getDistanceRings, showDistanceRings]);

  // Update nav line when user position changes during navigation
  useEffect(() => {
    if (navigatingTo && userPosition) {
      drawNavLine(userPosition, navigatingTo, navigatingTo);
    }
  }, [userPosition, navigatingTo]);

  // Clean up distance rings
  const clearDistanceRings = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    distanceRingsRef.current.forEach(c => map.removeLayer(c));
    ringLabelsRef.current.forEach(l => map.removeLayer(l));
    distanceRingsRef.current = [];
    ringLabelsRef.current = [];
  }, []);

  useEffect(() => { showDistanceRingsRef.current = showDistanceRings; }, [showDistanceRings]);

  useEffect(() => {
    if (!showDistanceRings) {
      clearDistanceRings();
      return;
    }
    const map = mapRef.current;
    const pos = userPositionRef.current ?? userPosition;
    if (map && pos) {
      updateDistanceRings(pos, map);
    }
  }, [showDistanceRings, userPosition, clearDistanceRings, updateDistanceRings]);

  // Orange GPS-spår: vid påslag laddas upp till 3 h från localStorage; nya positioner läggs till löpande (GPS sparas alltid i watchPosition).
  useEffect(() => {
    if (!showUserTrail) {
      const map = mapRef.current;
      if (map && userTrailPolylineRef.current) {
        try { map.removeLayer(userTrailPolylineRef.current); } catch { /* noop */ }
        userTrailPolylineRef.current = null;
      }
      trailCoordsRef.current = [];
      lastTrailAppendRef.current = null;
      return;
    }
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const now = Date.now();
    const stored = readGpsTrackPoints().filter(p => now - p.t <= Math.min(GPS_TRACK_RETENTION_MS, trailPrefs.mine.minutes * 60_000));
    const coords: [number, number][] = stored.map(p => [p.lat, p.lng]);
    const pos = userPositionRef.current;
    if (pos) {
      const lastC = coords.length
        ? { lat: coords[coords.length - 1][0], lng: coords[coords.length - 1][1] }
        : null;
      if (!lastC || haversineMetersFlat(lastC, pos) >= 10) {
        coords.push([pos.lat, pos.lng]);
      }
      lastTrailAppendRef.current = pos;
    } else {
      lastTrailAppendRef.current = coords.length
        ? { lat: coords[coords.length - 1][0], lng: coords[coords.length - 1][1] }
        : null;
    }
    trailCoordsRef.current = coords;
    if (coords.length === 0) return;
    if (userTrailPolylineRef.current) {
      userTrailPolylineRef.current.setLatLngs(coords);
      if (!map.hasLayer(userTrailPolylineRef.current)) userTrailPolylineRef.current.addTo(map);
    } else {
      userTrailPolylineRef.current = L.polyline(coords, { ...USER_TRAIL_STYLE, color: trailPrefs.mine.color }).addTo(map);
    }
    userTrailPolylineRef.current.setStyle({ color: trailPrefs.mine.color });
  }, [showUserTrail, mapReady, trailVersion, trailPrefs.mine.minutes, trailPrefs.mine.color]);

  useEffect(() => {
    if (!showUserTrail || !userPosition) return;
    const map = mapRef.current;
    if (!map) return;
    const prev = lastTrailAppendRef.current;
    if (prev && haversineMetersFlat(prev, userPosition) < 12) return;
    lastTrailAppendRef.current = { lat: userPosition.lat, lng: userPosition.lng };
    trailCoordsRef.current.push([userPosition.lat, userPosition.lng]);
    if (trailCoordsRef.current.length > 3500) {
      trailCoordsRef.current.splice(0, trailCoordsRef.current.length - 3500);
    }
    if (userTrailPolylineRef.current) {
      userTrailPolylineRef.current.setLatLngs(trailCoordsRef.current);
    } else {
      userTrailPolylineRef.current = L.polyline(trailCoordsRef.current, { ...USER_TRAIL_STYLE, color: trailPrefs.mine.color }).addTo(map);
    }
  }, [userPosition, showUserTrail]);

  // Toggle compass mode
  const toggleCompass = useCallback(() => {
    if (compassActive) {
      // Deactivate compass mode — restore map div to original layout
      const map = mapRef.current;
      if (map) {
        if (userMarkerRef.current) { map.removeLayer(userMarkerRef.current); userMarkerRef.current = null; }
        if (userAccuracyRef.current) { map.removeLayer(userAccuracyRef.current); userAccuracyRef.current = null; }
        clearDistanceRings();
        if (mapDivRef.current) {
          const d = mapDivRef.current;
          d.style.transform = '';
          d.style.removeProperty(UPRIGHT_VAR);
          d.style.position = '';
          d.style.width = '';
          d.style.height = '';
          d.style.top = '';
          d.style.left = '';
          d.style.transition = '';
        }
        setTimeout(() => map.invalidateSize(), 50);
        if (showDistanceRings && userPosition) {
          updateDistanceRings(userPosition, map);
        }
      }
      compassActiveRef.current = false;
      setCompassActive(false);
      setHeading(null);
      headingRef.current = null;
      stopOrientationRef.current?.();
      stopOrientationRef.current = null;
      return;
    }

    // Resize map div to viewport diagonal (square) so corners are always covered when rotating
    if (mapDivRef.current) {
      const parent = mapDivRef.current.parentElement;
      if (parent) {
        const W = parent.clientWidth;
        const H = parent.clientHeight;
        const diagonal = Math.ceil(Math.sqrt(W * W + H * H)) + 20;
        const d = mapDivRef.current;
        d.style.position = 'absolute';
        d.style.width = diagonal + 'px';
        d.style.height = diagonal + 'px';
        d.style.top = Math.floor((H - diagonal) / 2) + 'px';
        d.style.left = Math.floor((W - diagonal) / 2) + 'px';
        d.style.transformOrigin = 'center center';
        setTimeout(() => mapRef.current?.invalidateSize?.(), 50);
      }
    }

    // Activate — set ref immediately so GPS callback sees it right away
    compassActiveRef.current = true;
    setCompassActive(true);

    // Request device orientation permission (iOS 13+)
    const startOrientation = () => {
      const handler = (e: any) => {
        let h: number | null = null;
        if (e.webkitCompassHeading !== undefined) {
          h = e.webkitCompassHeading; // iOS
        } else if (e.alpha !== null) {
          h = (360 - e.alpha) % 360; // Android
        }
        if (h !== null) {
          headingRef.current = h;
          setHeading(h);
        }
      };
      stopOrientationRef.current?.();
      window.addEventListener('deviceorientation', handler, true);
      stopOrientationRef.current = () => window.removeEventListener('deviceorientation', handler, true);
    };

    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      (DeviceOrientationEvent as any).requestPermission().then((perm: string) => {
        if (perm === 'granted') startOrientation();
      }).catch(() => {});
    } else {
      startOrientation();
    }

    // Center map on user position when compass activates (no zoom change)
    if (userPosition) {
      const map = mapRef.current;
      if (map) {
        map.panTo([userPosition.lat, userPosition.lng], { animate: false });
        if (showDistanceRings) {
          updateDistanceRings(userPosition, map);
        }
      }
    }
  }, [compassActive, clearDistanceRings, updateDistanceRings, userPosition, showDistanceRings]);

  // Keep refs in sync so GPS callback can read latest values without restarting the watch
  useEffect(() => { compassActiveRef.current = compassActive; }, [compassActive]);
  useEffect(() => { userPositionRef.current = userPosition; }, [userPosition]);
  useEffect(() => { updateDistanceRingsRef.current = updateDistanceRings; }, [updateDistanceRings]);
  useEffect(() => () => stopOrientationRef.current?.(), []);

  // Auto-center map on user position when compass mode is active
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (compassActive) {
      // UX14: kartan går att dra även i kompassläget – då pausas följningen (Min position eller en stund återupptar).
      map.dragging.enable();
      const onDragStart = () => pauseFollow('start');
      const onDragEnd = () => pauseFollow('end');
      map.on('dragstart', onDragStart);
      map.on('dragend', onDragEnd);
      // KB2: nyp och hjul zoomar runt mitten (min position), så att kartan inte hoppar tillbaka.
      map.options.touchZoom = 'center';
      map.options.scrollWheelZoom = 'center';
      // Center immediately when compass activates
      const pos = userPositionRef.current;
      if (pos) {
        map.panTo([pos.lat, pos.lng], { animate: false });
      }
      // Keep centering every second — panTo is pålitligare än setView
      const intervalId = setInterval(() => {
        const p = userPositionRef.current;
        const m = mapRef.current;
        if (!p || !m || followPausedRef.current) return;
        m.panTo([p.lat, p.lng], { animate: false });
      }, 1000);
      return () => {
        clearInterval(intervalId);
        map.off('dragstart', onDragStart);
        map.off('dragend', onDragEnd);
        resumeFollowRef.current();
        mapRef.current?.dragging.enable();
        if (mapRef.current) { mapRef.current.options.touchZoom = true; mapRef.current.options.scrollWheelZoom = true; }
      };
    } else {
      map.dragging.enable();
    }
  }, [compassActive]); // Only re-runs when compass is toggled

  // Rotate map container when heading changes in compass mode
  useEffect(() => {
    if (!compassActive || heading === null) return;
    const mapDiv = mapDivRef.current;
    if (!mapDiv) return;
    // Rotate map — mapDiv already resized to viewport diagonal on activation, so corners always covered
    mapDiv.style.transform = `rotate(${-heading}deg)`;
    mapDiv.style.transformOrigin = 'center center';
    mapDiv.style.transition = COMPASS_TRANSITION;
    // QV2: nålarna vrids tillbaka kring sin spets (createPinIcon), så att de står upprätt på sin plats.
    mapDiv.style.setProperty(UPRIGHT_VAR, `${heading}deg`);
  }, [heading, compassActive]);

  // Uppdatera ring-etiketternas rotation när kompassrubrik ändras (kartcontainern roterar redan med -heading)
  useEffect(() => {
    if (!compassActive || !showDistanceRings) return;
    ringLabelsRef.current.forEach((marker: any) => {
      const wrap = marker.getElement?.();
      if (!wrap) return;
      const inner = wrap.querySelector('[data-ring-label]') as HTMLElement | null;
      if (!inner) return;
      if (heading !== null) {
        inner.style.transform = `rotate(${heading}deg)`;
        inner.style.transformOrigin = 'center center';
      } else {
        inner.style.transform = '';
        inner.style.transformOrigin = '';
      }
    });
  }, [heading, compassActive, showDistanceRings]);

  // Update distance rings when zoom changes (vanligt läge och kompass)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !showDistanceRings) return;
    const onZoom = () => {
      if (userPosition) updateDistanceRings(userPosition, map);
    };
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, [userPosition, updateDistanceRings, showDistanceRings]);

  // User position marker (blue pulsing dot) — shown whenever GPS has a fix
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !userPosition) return;
    // Remove marker if no position
    if (!userPosition && userMarkerRef.current) {
      map.removeLayer(userMarkerRef.current);
      userMarkerRef.current = null;
      return;
    }

    if (!userMarkerRef.current) {
      const pulseHtml = `
        <div style="position:relative;width:20px;height:20px">
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(0,122,255,0.25);animation:compassPulse 2s infinite"></div>
          <div style="position:absolute;inset:4px;border-radius:50%;background:#007AFF;border:2.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>
          ${headingRef.current !== null ? `<div style="position:absolute;top:-8px;left:50%;transform:translateX(-50%) rotate(0deg);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:8px solid #007AFF"></div>` : ''}
        </div>
      `;
      userMarkerRef.current = L.marker([userPosition.lat, userPosition.lng], {
        icon: L.divIcon({
          html: pulseHtml,
          className: '',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
        zIndexOffset: 1000,
        interactive: false,
      });
      userMarkerRef.current.addTo(map);

      // Add CSS animation if not already present
      if (!document.getElementById('compass-pulse-css')) {
        const style = document.createElement('style');
        style.id = 'compass-pulse-css';
        style.textContent = `@keyframes compassPulse { 0% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(2); opacity: 0; } 100% { transform: scale(1); opacity: 0; } }`;
        document.head.appendChild(style);
      }
    } else {
      userMarkerRef.current.setLatLng([userPosition.lat, userPosition.lng]);
    }
  }, [userPosition]);

  // MJ2: GPS-bevakningen startas en gång och läser marken härifrån.
  const markGeoRef = useRef(mark);
  markGeoRef.current = mark;

  const mapPositions = useMemo(() => {
    const out = { ...remotePositions };
    for (const w of watchPositions) {
      out[`watch:${w.userEmail}`] = { lat: w.lat, lng: w.lng, name: w.name, avatarUrl: w.avatarUrl ?? undefined, ts: w.updatedAt };
    }
    return out;
  }, [remotePositions, watchPositions]);

  // Vänstermenyn: en sektion utfälld åt gången (sparas), och vilken spårtyp/hund som är utfälld.
  const [menuSection, setMenuSection] = useState<string | null>(() => {
    try { return localStorage.getItem('qvenari_menu_section'); } catch { return null; }
  });
  const toggleMenuSection = (id: string) => setMenuSection(cur => {
    const next = cur === id ? null : id;
    try { if (next) localStorage.setItem('qvenari_menu_section', next); else localStorage.removeItem('qvenari_menu_section'); } catch { /* ignore */ }
    return next;
  });
  const [trailOpen, setTrailOpen] = useState<TrailKind | null>(null);
  const [trailMore, setTrailMore] = useState<TrailKind | null>(null);
  const [dogOpen, setDogOpen] = useState<string | null>(null);
  const [myDogWatches, setMyDogWatches] = useState<DogWatch[]>([]);
  useEffect(() => {
    if (!showMapSettings || !token) return;
    api.myDogWatches().then(setMyDogWatches).catch(() => { /* äldre API */ });
  }, [showMapSettings, token]);
  const changeDogWatch = (dogId: string, change: { powerMode?: 'hunt' | 'saver'; mic?: boolean }) => {
    setMyDogWatches(prev => prev.map(d => (d.dogId === dogId ? { ...d, ...change } : d)));
    setWatchDogs(prev => prev.map(d => (d.dogId === dogId ? { ...d, ...change } : d)));
    api.setDogWatchSettings(dogId, change).catch(e => alert((e as Error).message || 'Kunde inte spara'));
  };
  // PAJ-spår: bara när hundspår är påslaget och det finns PAJ-hundar. Servern cachar spåret 2 min per enhet.
  const hasPajDogs = pajDogs.length > 0;
  useEffect(() => {
    const minutes = trailPrefs.dogs.minutes;
    if (!token || !mark?.id || !minutes || !hasPajDogs) { setPajTracks([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiFetch(`/api/integrations/${mark.id}/paj/tracks?minutes=${minutes}`, { headers: bearerHeaders(token) });
        if (r.ok && !cancelled) setPajTracks(await r.json());
      } catch { /* tillfälligt nätfel */ }
    };
    void load();
    const tid = setInterval(load, 120_000);
    return () => { cancelled = true; clearInterval(tid); };
  }, [mark?.id, token, trailPrefs.dogs.minutes, hasPajDogs]);

  /** Hundarna i menyn: dina hundar (klockans inställningar) med livedata från jakten, plus Garmin- och PAJ-hundar. */
  const menuDogs = useMemo(() => {
    const now = Date.now();
    const out: { key: string; name: string; device: string; battery: number | null; updatedAt: number | null; barking: boolean; watch: DogWatch | null }[] = [];
    for (const w of myDogWatches) {
      const live = watchDogs.find(d => d.dogId === w.dogId);
      out.push({
        key: `watch:${w.dogId}`, name: w.name, device: 'Apple Watch', watch: w,
        battery: live?.battery ?? null, updatedAt: live?.updatedAt ?? null,
        barking: !!live?.barkingAt && now - live.barkingAt < 15000,
      });
    }
    for (const g of dogPositions as any[]) {
      const name = g.dog_name || g.configured_name || g.device_name || g.imei;
      out.push({
        key: `garmin:${g.imei}`, name: String(name), device: g.device_name || 'Garmin', watch: null,
        battery: g.battery_percent != null ? Number(g.battery_percent) / 100 : null,
        updatedAt: g.recorded_at ? new Date(g.recorded_at).getTime() : null, barking: false,
      });
    }
    for (const p of pajDogs) {
      // Samma hund i profilen (klockrad utan livedata): visa PAJ-datan på den raden i stället för en rad till.
      const same = p.dogId ? out.find(o => o.watch?.dogId === p.dogId && o.updatedAt == null) : undefined;
      if (same) {
        Object.assign(same, {
          device: `PAJ GPS${p.deviceName ? ` · ${p.deviceName}` : ''}`,
          battery: p.batteryPercent != null ? p.batteryPercent / 100 : null,
          updatedAt: Date.parse(p.recordedAt),
        });
        continue;
      }
      out.push({
        key: `paj:${p.key}`, name: p.name, device: `PAJ GPS${p.deviceName && p.deviceName !== p.name ? ` · ${p.deviceName}` : ''}`, watch: null,
        battery: p.batteryPercent != null ? p.batteryPercent / 100 : null,
        updatedAt: Date.parse(p.recordedAt), barking: false,
      });
    }
    return out;
  }, [myDogWatches, watchDogs, dogPositions, pajDogs]);

  // Jägarnas och hundarnas spår (Spår i menyn): hämtas från servern var 30:e sekund under jakten.
  const [huntTracks, setHuntTracks] = useState<HuntTrack[]>([]);
  const otherTrailMinutes = Math.max(trailPrefs.hunters.minutes, trailPrefs.dogs.minutes);
  useEffect(() => {
    const meetingId = huntSession?.meetingId;
    if (!meetingId || !otherTrailMinutes) { setHuntTracks([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const tracks = await api.huntTracks(meetingId, Date.now() - otherTrailMinutes * 60_000);
        if (!cancelled) setHuntTracks(tracks);
      } catch { /* äldre API utan spår, eller tillfälligt nätfel */ }
    };
    void load();
    const tid = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(tid); };
  }, [huntSession?.meetingId, otherTrailMinutes]);

  const huntTrackLayerRef = useRef<any>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (huntTrackLayerRef.current) { map.removeLayer(huntTrackLayerRef.current); huntTrackLayerRef.current = null; }
    if (!huntTracks.length) return;
    const now = Date.now();
    const layers: any[] = [];
    for (const t of huntTracks) {
      if (t.kind === 'hunter' && t.isMe) continue; // eget spår ritas lokalt (Mitt spår)
      const pref = t.kind === 'dog' ? trailPrefs.dogs : trailPrefs.hunters;
      if (!pref.minutes) continue;
      const pts = t.points.filter(p => now - p[2] <= pref.minutes * 60_000).map(p => [p[0], p[1]] as [number, number]);
      if (pts.length < 2) continue;
      layers.push(L.polyline(pts, { color: pref.color, weight: t.kind === 'dog' ? 4 : 3.5, opacity: 0.85, lineCap: 'round', lineJoin: 'round', interactive: false }));
    }
    if (!layers.length) return;
    huntTrackLayerRef.current = L.layerGroup(layers).addTo(map);
  }, [huntTracks, trailPrefs.hunters.minutes, trailPrefs.hunters.color, trailPrefs.dogs.minutes, trailPrefs.dogs.color]);

  const pajTrackLayerRef = useRef<any>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pajTrackLayerRef.current) { map.removeLayer(pajTrackLayerRef.current); pajTrackLayerRef.current = null; }
    const layers = pajTracks
      .filter(t => t.points.length > 1)
      .map(t => L.polyline(t.points.map(p => [p[0], p[1]] as [number, number]), { color: trailPrefs.dogs.color, weight: 4, opacity: 0.85, lineCap: 'round', lineJoin: 'round', interactive: false }));
    if (layers.length) pajTrackLayerRef.current = L.layerGroup(layers).addTo(map);
  }, [pajTracks, trailPrefs.dogs.color]);

  // 3D-terräng: fristående MapLibre-vy som visas ovanpå Leaflet-kartan i helskärm.
  const [show3D, setShow3D] = useState(() => safeLocalStorageGet('qvenari_karta_3d') === '1');
  // QV2: tappar telefonen 3D-kartans WebGL (grafikminnet tar slut, t.ex. med video i jakten) ritas kartan inte
  // längre, men dess nålar ligger kvar ovanpå den platta kartan på fel ställen. Då visas den platta kartan i stället
  // tills 3D slås på igen.
  const [lost3d, setLost3d] = useState(false);
  const onLost3d = useCallback(() => setLost3d(true), []);
  /** 3D-kartan när den finns – lutningsreglaget ligger i vänstermenyn. */
  const [map3d, setMap3d] = useState<maplibregl.Map | null>(null);
  useEffect(() => { safeLocalStorageSet('qvenari_karta_3d', show3D ? '1' : '0'); }, [show3D]);
  // Varje del har egen memo så att 3D-vyn inte bygger om nålar och gränser vid varje GPS-uppdatering.
  // Senast kända gränser: marken hämtas om utan coordinatesJson strax efter öppning, och 2D
  // behåller då det som redan ritats – 3D ska visa samma sak.
  const lastCoordinatesJsonRef = useRef(mark.coordinatesJson);
  if (mark.coordinatesJson) lastCoordinatesJsonRef.current = mark.coordinatesJson;
  const coordinatesJson3d = lastCoordinatesJsonRef.current;
  const areas3d = useMemo<unknown>(() => {
    if (!show3D || !coordinatesJson3d) return null;
    try {
      return typeof coordinatesJson3d === 'string' ? JSON.parse(coordinatesJson3d) : coordinatesJson3d;
    } catch { return null; }
  }, [show3D, coordinatesJson3d]);
  const tracks3d = useMemo<Karta3DProps['tracks']>(() => {
    if (!show3D) return [];
    const now = Date.now();
    const tracks: Karta3DProps['tracks'] = [];
    for (const t of huntTracks) {
      if (t.kind === 'hunter' && t.isMe) continue;
      const pref = t.kind === 'dog' ? trailPrefs.dogs : trailPrefs.hunters;
      if (!pref.minutes) continue;
      const pts = t.points.filter(pt => now - pt[2] <= pref.minutes * 60_000).map(pt => [pt[0], pt[1]] as [number, number]);
      tracks.push({ id: `${t.kind}:${tracks.length}`, color: pref.color, width: t.kind === 'dog' ? 4 : 3.5, pts });
    }
    for (const t of pajTracks) {
      if (t.points.length > 1) tracks.push({ id: `paj:${t.key}`, color: trailPrefs.dogs.color, width: 4, pts: t.points.map(p => [p[0], p[1]] as [number, number]) });
    }
    if (showUserTrail && trailCoordsRef.current.length > 1) {
      tracks.push({ id: 'me', color: trailPrefs.mine.color, width: 4, pts: [...trailCoordsRef.current] });
    }
    return tracks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show3D, huntTracks, pajTracks, trailPrefs, showUserTrail, trailVersion]);
  // Samma nålar som 2D (samma av/på-inställningar), plus åtelkamerorna.
  const pins3d = useMemo<Karta3DProps['pins']>(() => {
    if (!show3D) return [];
    const out: Karta3DProps['pins'] = pins
      .filter(p => (p.type === 'pass' ? showPassPins : p.type === 'observation' ? showObservationPins : p.type === 'skjutet_vilt' ? showSkjutetViltPins : true))
      .map(p => ({ id: p.id, lat: p.lat, lng: p.lng, type: p.type as PinType, name: p.name, showLabel: p.type === 'pass' ? showPassPinLabels : true }));
    cameras.forEach(c => {
      if (c.lat != null && c.lng != null) out.push({ id: `cam:${c.id}`, lat: c.lat, lng: c.lng, type: 'kamera', name: c.name, showLabel: true });
    });
    return out;
  }, [show3D, pins, cameras, showPassPins, showPassPinLabels, showObservationPins, showSkjutetViltPins]);
  const participants3d = useMemo<Karta3DProps['participants']>(
    () => (show3D ? Object.entries(mapPositions).map(([id, p]) => ({ id, lat: p.lat, lng: p.lng, name: p.name, avatarUrl: p.avatarUrl })) : []),
    [show3D, mapPositions],
  );
  const dogs3d = useMemo<Karta3DProps['dogs']>(
    () => (show3D ? [
      ...watchDogs.map(d => ({ id: d.dogId, lat: d.lat, lng: d.lng, name: d.dogName })),
      // PAJ-hundar: samma färger som i 2D (orange färsk, grå gammal/tyst).
      ...pajDogs.map(p => ({ id: `paj:${p.key}`, lat: p.lat, lng: p.lng, name: p.name, color: PAJ_DOG_COLORS[pajFreshness(p, pajNow)] })),
    ] : []),
    [show3D, watchDogs, pajDogs, pajNow],
  );
  // Vittringen utgår från samma punkt som i 2D: min position, annars markens mitt, annars kartans mitt.
  const vittring3d = useMemo<Karta3DProps['vittring']>(() => {
    if (!show3D || !showVittring || vittringWindDir === null) return null;
    const c = mapRef.current?.getCenter?.();
    const origin = userPosition
      ?? (mark.mapLat != null && mark.mapLng != null ? { lat: mark.mapLat, lng: mark.mapLng } : null)
      ?? (c ? { lat: c.lat, lng: c.lng } : null);
    return origin ? { origin, windDir: vittringWindDir, windSpd: vittringWindSpd } : null;
  }, [show3D, showVittring, vittringWindDir, vittringWindSpd, userPosition, mark.mapLat, mark.mapLng]);
  const getHeading3d = useCallback(() => headingRef.current, []);
  const karta3dProps = useMemo<Karta3DProps | null>(() => {
    if (!show3D || lost3d) return null;
    return {
      center: mark.mapLat && mark.mapLng ? [mark.mapLat, mark.mapLng] : userPosition ? [userPosition.lat, userPosition.lng] : [59.322, 16.681],
      // 3D startar där den platta kartan står när vyn öppnas, inte på markens mittpunkt.
      startView: () => {
        const leaflet = mapRef.current;
        if (!leaflet) return null;
        const c = leaflet.getCenter();
        return { center: [c.lat, c.lng], zoom: leaflet.getZoom(), bearing: compassActiveRef.current && headingRef.current != null ? headingRef.current : 0 };
      },
      baseMap: mapType,
      areas: areas3d,
      me: userPosition,
      showRings: showDistanceRings,
      vittring: vittring3d,
      fastighetsgranser: showFastighetsgranser,
      compassActive,
      getHeading: getHeading3d,
      participants: participants3d,
      onParticipantClick: setStatusFor, // KA1
      dogs: dogs3d,
      tracks: tracks3d,
      pins: pins3d,
      onMap: setMap3d,
      followPaused,
      onUserPan: pauseFollow,
      onContextLost: onLost3d,
    };
  }, [show3D, lost3d, onLost3d, mark.mapLat, mark.mapLng, userPosition, mapType, areas3d, showDistanceRings, vittring3d, showFastighetsgranser, compassActive, getHeading3d, participants3d, dogs3d, tracks3d, pins3d, followPaused, pauseFollow]);

  // UX14: hårkors och avstånd medan man drar kartan, inbäddad och i helskärm (inte i kompassläget, som följer positionen).
  // Utan position just nu: senast kända position, annars markens mitt (det står då vid hårkorset).
  const centerDist = useCenterDistance({
    enabled: true, // UX14: även i kompassläget (följningen pausas medan man drar)
    me: userPosition,
    fallback: () => {
      const last = readLastKnownPosition();
      if (last) return { pos: last, origin: 'last' as const };
      return mark.mapLat != null && mark.mapLng != null ? { pos: { lat: mark.mapLat, lng: mark.mapLng }, origin: 'mark' as const } : null;
    },
    leaflet: mapReady ? mapRef.current : null,
    map3d: show3D && fullscreen ? map3d : null,
  });

  // UX15: Min position (cirkel med punkt), en egen knapp bredvid kompassen: centrerar kartan på mig (i kompassläget
  // följer kartan redan positionen). Utan position frågar den efter en.
  const [locating, setLocating] = useState(false);
  const locateMe = () => {
    if (followPausedRef.current) resumeFollowRef.current();
    const pos = userPositionRef.current ?? userPosition;
    if (!pos) {
      // Ingen position än (GPS av, nekad eller långsam): fråga efter en nu och centrera när den kommer.
      if (!navigator.geolocation) { alert('Den här enheten kan inte ge din position.'); return; }
      setLocating(true);
      navigator.geolocation.getCurrentPosition((p) => {
        setLocating(false);
        const np = { lat: p.coords.latitude, lng: p.coords.longitude };
        userPositionRef.current = np;
        setUserPosition(np);
        setGpsStatus('active');
        persistLastKnownPosition(np.lat, np.lng, Date.now());
        const m3 = show3D && fullscreen ? map3d : null;
        if (m3) m3.easeTo({ center: [np.lng, np.lat], duration: 400 });
        else mapRef.current?.setView([np.lat, np.lng], Math.max(mapRef.current.getZoom(), 15), { animate: true });
      }, (err) => {
        setLocating(false);
        if (err.code === 1) { setGpsStatus('denied'); alert('Qvenari får inte använda din position. Slå på platsåtkomst för Qvenari i telefonens eller webbläsarens inställningar.'); }
        else alert('Kunde inte hitta din position just nu. Försök igen utomhus eller om en stund.');
      }, { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 });
      return;
    }
    const m3 = show3D && fullscreen ? map3d : null;
    const leaflet = mapRef.current;
    if (m3) m3.easeTo({ center: [pos.lng, pos.lat], duration: 400 });
    else leaflet?.panTo([pos.lat, pos.lng], { animate: true });
  };

  // Hundar med klocka i hundläge: samma 🐕-markör som Garmin-hundarna, röd pulserande ring vid skall.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const existing = watchDogMarkersRef.current;
    const ids = new Set(watchDogs.map(d => d.dogId));
    Object.keys(existing).forEach(id => {
      if (!ids.has(id)) { map.removeLayer(existing[id]); delete existing[id]; }
    });
    const now = Date.now();
    watchDogs.forEach(d => {
      const barking = d.barkingAt != null && now - d.barkingAt < 15000;
      const html = `<div style="position:relative;width:34px;height:34px">
        <div style="width:34px;height:34px;border-radius:50%;background:#EE8F09;border:3px solid ${barking ? '#ff3b30' : '#fff'};
          display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.5);font-size:17px;line-height:1;
          ${barking ? 'animation:pulse 0.8s infinite;' : ''}">🐕</div>
        ${barking ? '<div style="position:absolute;top:-6px;right:-8px;background:#ff3b30;color:#fff;border-radius:9px;padding:1px 4px;font-size:10px;font-weight:800">🔊</div>' : ''}
        <div style="position:absolute;top:36px;left:50%;transform:translateX(-50%);white-space:nowrap;background:rgba(0,0,0,0.6);color:#fff;font:700 11px system-ui;padding:1px 5px;border-radius:6px">${d.dogName.replace(/</g, '&lt;')}</div>
      </div>`;
      const icon = L.divIcon({ html, className: '', iconSize: [34, 34], iconAnchor: [17, 17] });
      const batt = d.battery != null ? ` · Batteri ${Math.round(d.battery * 100)}%` : '';
      const popup = dogWatchPopup(d, barking, batt, change => {
        // Visa ändringen direkt; klockan får den vid nästa rapport (inom 30 s).
        setWatchDogs(prev => prev.map(x => x.dogId === d.dogId ? { ...x, ...change } : x));
        api.setDogWatchSettings(d.dogId, change).catch(e => alert((e as Error).message || 'Kunde inte spara'));
      });
      if (existing[d.dogId]) {
        existing[d.dogId].setLatLng([d.lat, d.lng]).setIcon(icon).setPopupContent(popup);
      } else {
        existing[d.dogId] = L.marker([d.lat, d.lng], { icon, zIndexOffset: 950 }).bindPopup(popup).addTo(map);
      }
    });
  }, [watchDogs]);

  // Render remote participant position markers on the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const existing = remotePositionMarkersRef.current;
    // Remove stale markers
    const activeIds = new Set(Object.keys(mapPositions));
    Object.keys(existing).forEach(id => {
      if (!activeIds.has(id)) {
        map.removeLayer(existing[id]);
        delete existing[id];
      }
    });
    // Add/update markers
    Object.entries(mapPositions).forEach(([id, p]) => {
      const initials = p.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
      const avatarHtml = p.avatarUrl
        ? `<img src="${p.avatarUrl}" style="width:22px;height:22px;border-radius:50%;object-fit:cover;border:2px solid #EE8F09;display:block"/>`
        : `<div style="width:22px;height:22px;border-radius:50%;background:#EE8F09;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:10px;color:#1a2d18;font-family:system-ui,sans-serif">${initials}</div>`;
      const html = avatarHtml;
      if (existing[id]) {
        existing[id].setLatLng([p.lat, p.lng]);
        existing[id].setIcon(L.divIcon({ html, className: '', iconSize: [22, 22], iconAnchor: [11, 11] }));
      } else {
        existing[id] = L.marker([p.lat, p.lng], {
          icon: L.divIcon({ html, className: '', iconSize: [22, 22], iconAnchor: [11, 11] }),
          zIndexOffset: 900,
        }).on('click', () => setStatusFor(id)).addTo(map); // KA1
      }
    });
  }, [mapPositions]);

  // Auto-start GPS when map is ready — runs ONCE, never restarts due to state changes
  // Uses refs to read compass/ring state inside the callback without restarting the watch
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    if (!navigator.geolocation) {
      setGpsStatus('unavailable');
      return;
    }

    // Clear any stale watch before starting fresh
    if (geoWatchRef.current !== null) {
      navigator.geolocation.clearWatch(geoWatchRef.current);
      geoWatchRef.current = null;
    }

    let firstFix = true;
    geoWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const newPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        // Update ref immediately (don't wait for useEffect sync)
        userPositionRef.current = newPos;
        setUserPosition(newPos);
        setGpsStatus('active');
        const nowMs = Date.now();
        persistGpsTrackSample(newPos.lat, newPos.lng, nowMs);
        persistLastKnownPosition(newPos.lat, newPos.lng, nowMs);

        const currentMap = mapRef.current;
        if (!currentMap) return;

        if (firstFix) {
          firstFix = false;
          // MJ2: stanna på marken om jag är långt därifrån (t.ex. hemma).
          if (followFirstFix(markGeoRef.current, newPos)) {
            currentMap.panTo([newPos.lat, newPos.lng], { animate: false });
            currentMap.setZoom(15);
          }
        }

        if (compassActiveRef.current && !followPausedRef.current) {
          currentMap.panTo([newPos.lat, newPos.lng], { animate: false });
        }
        if (updateDistanceRingsRef.current && showDistanceRingsRef.current) {
          updateDistanceRingsRef.current(newPos, currentMap);
        }
      },
      (err) => {
        console.warn('Geolocation error:', err.code, err.message);
        if (err.code === 1) setGpsStatus('denied');
        else setGpsStatus('unavailable');
      },
      { enableHighAccuracy: true, maximumAge: 4000, timeout: 25000 }
    );

    return () => {
      if (geoWatchRef.current !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.current);
        geoWatchRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]); // Only restart GPS if the map itself remounts

  // Eget spår med skärmlåset på: den inbyggda spåraren (iOS och Android) sparar positioner medan webbvyn
  // sover, och under jakten skickar den själv spåret och positionen till servern (BackgroundTrackPlugin).
  const trackInBackground = showUserTrail || !!huntSession;
  const huntMeetingId = huntSession?.meetingId ?? null;
  const backgroundUpload = useCallback(
    () => (huntMeetingId ? huntTrackUpload(huntMeetingId, userAvatarUrl || undefined) : null),
    [huntMeetingId, userAvatarUrl],
  );
  const backgroundUploadRef = useRef(backgroundUpload);
  backgroundUploadRef.current = backgroundUpload;
  useEffect(() => {
    if (trackInBackground) startBackgroundTrack(backgroundUpload());
  }, [trackInBackground, backgroundUpload]);
  useEffect(() => {
    if (!trackInBackground) return;
    return () => stopBackgroundTrack();
  }, [trackInBackground]);
  // Appen fram igen: sträckan från fickan in i spåret, och färsk inloggning till pluginen så att
  // den kan skicka det som inte hann iväg.
  const trackInBackgroundRef = useRef(trackInBackground);
  trackInBackgroundRef.current = trackInBackground;
  const syncBackgroundTrack = useCallback(() => {
    if (trackInBackgroundRef.current) startBackgroundTrack(backgroundUploadRef.current());
    void drainBackgroundTrack().then(points => {
      if (!points.length) return;
      // Webbvyns GPS hinner ofta spara en färsk punkt först — sortera in efter tid.
      writeGpsTrackPoints(mergeGpsTrackPoints(readGpsTrackPoints(), points, Date.now()));
      setTrailVersion(v => v + 1);
    });
  }, []);
  useEffect(() => onBackgroundTrackResume(syncBackgroundTrack), [syncBackgroundTrack]);

  // När användaren återgår till fliken: hämta färsk position (underlättar när webben legat i bakgrunden)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') syncBackgroundTrack();
      if (document.visibilityState !== 'visible' || !mapReady || !navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const newPos = { lat, lng };
          userPositionRef.current = newPos;
          setUserPosition(newPos);
          setGpsStatus('active');
          const nowMs = Date.now();
          persistGpsTrackSample(lat, lng, nowMs);
          persistLastKnownPosition(lat, lng, nowMs);
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
      );
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [mapReady, syncBackgroundTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (geoWatchRef.current !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.current);
      }
    };
  }, []);

  // Broadcast own position to other hunt participants every 10 s when in active hunt
  useEffect(() => {
    if (!huntSession) return;
    const broadcast = () => {
      const room = lkRoomRef.current;
      const pos = userPositionRef.current;
      if (pos && huntServerReadyRef.current) {
        api.reportHuntPosition(huntSession.meetingId, { lat: pos.lat, lng: pos.lng, avatarUrl: userAvatarUrl || undefined }).catch(() => {});
      }
      if (!room || room.state !== 'connected' || !pos) return;
      const payload = {
        type: HUNT_POSITION_V1,
        lat: pos.lat,
        lng: pos.lng,
        name: userName || room.localParticipant.name || 'Jägare',
        avatarUrl: userAvatarUrl,
        senderIdentity: room.localParticipant.identity,
        ts: Date.now(),
      };
      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(payload)),
        { reliable: false },
      ).catch(() => {});
    };
    broadcast();
    const tid = setInterval(broadcast, 10000);
    return () => clearInterval(tid);
  }, [huntSession, userName, userAvatarUrl]);

  // Apple Watch: klockans Tysta-knapp styr radion här, och klockan får veta radions läge.
  const watchToggleRef = useRef<{ mic: () => void; speaker: () => void }>({ mic: () => {}, speaker: () => {} });
  watchToggleRef.current = { mic: () => { void toggleMic(); }, speaker: toggleSpeaker };
  useEffect(() => {
    if (!huntSession) return;
    return onWatchCommand(cmd => {
      if (cmd === 'toggleSpeaker') watchToggleRef.current.speaker();
      else if (cmd === 'toggleMic') watchToggleRef.current.mic();
    });
  }, [huntSession]);
  useEffect(() => {
    setWatchHuntState(huntSession ? { meetingId: huntSession.meetingId, markId: mark.id, markName: mark.name, micOn, speakerOn, showTrail: showUserTrail } : null);
  }, [huntSession, mark.id, mark.name, micOn, speakerOn, showUserTrail]);
  useEffect(() => () => setWatchHuntState(null), []);
  // Dagens steg ur Hälsa på iPhone, var minut medan Jägarhälsa visas.
  const [steps, setSteps] = useState<number | null>(null);
  useEffect(() => {
    if (!showHunterHealth) { setSteps(null); return; }
    let cancelled = false;
    const load = () => { void stepsToday().then(n => { if (!cancelled) setSteps(n); }); };
    load();
    const tid = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(tid); };
  }, [showHunterHealth]);

  // Jakten som träningspass i Hälsa (hunterHealth.ts): start när jakten börjar, spara när den tar
  // slut — här när jakten byts eller stängs av servern medan kartan visas, och i knapparna Avsluta/
  // Lämna (kartan för inbjudna stängs med jakten). Servern som stänger jakten medan kartan inte visas
  // hanteras i App.tsx. Att kartan stängs medan jakten fortsätter avslutar inget.
  const huntWorkoutMeetingRef = useRef<string | null>(null);
  useEffect(() => {
    const meetingId = huntSession?.meetingId || null;
    const previous = huntWorkoutMeetingRef.current;
    huntWorkoutMeetingRef.current = meetingId;
    if (previous && previous !== meetingId) void finishHuntWorkout(previous);
    if (meetingId && meetingId !== previous) beginHuntWorkout(meetingId);
  }, [huntSession?.meetingId]);

  // Pulsen från klockans träningspass; döljs om klockan inte hört av sig på en halv minut.
  const [watchBpm, setWatchBpm] = useState<number | null>(null);
  useEffect(() => {
    if (!huntSession) { setWatchBpm(null); return; }
    let staleTimer: number | undefined;
    const meetingId = huntSession.meetingId;
    const off = onWatchHeartRate(bpm => {
      // Pulsen kommer bara från klockans sparade pass — då sparar klockan jakten i Hälsa.
      markWatchInHunt(meetingId);
      setWatchBpm(bpm);
      window.clearTimeout(staleTimer);
      staleTimer = window.setTimeout(() => setWatchBpm(null), 30000);
    });
    return () => { off(); window.clearTimeout(staleTimer); };
  }, [huntSession]);

  // KA1: min status (batteri, steg, puls, hastighet, höjd, högtalare) till de andra i jakten.
  useHuntStatusReporter({
    meetingId: huntSession?.meetingId ?? null,
    enabled: !!token && !!huntSession && !isGuestHunt(huntSession.meetingId),
    getIdentity: () => lkRoomRef.current?.localParticipant.identity ?? null,
    speakerOn, heartRate: watchBpm,
  });
  useEffect(() => { if (!huntSession) setStatusFor(null); }, [huntSession]);

  // Spegla jakten på servern: Apple Watch saknar LiveKit och läser/skriver positioner och chatt där.
  useEffect(() => {
    const meetingId = huntSession?.meetingId;
    if (!meetingId || (!token && !isGuestHunt(meetingId))) return; // GU1: gästen har ingen inloggning men gästtoken
    let cancelled = false;
    let chatAfter: number | undefined;
    huntServerReadyRef.current = false;
    const addIncoming = (msgs: HuntChatMessage[], announce: boolean) => {
      if (!msgs.length) return;
      setHuntChatRows(prev => {
        const fresh = msgs.filter(m => !prev.some(r => r.kind === 'user' && r.id === m.clientId));
        if (!fresh.length) return prev;
        return [...prev, ...fresh.map(m => ({
          kind: 'user' as const, id: m.clientId, senderName: m.senderName, text: m.text ?? undefined,
          imageUrl: m.imageUrl ?? undefined, ts: m.createdAt, lat: m.lat ?? undefined, lng: m.lng ?? undefined,
          audioUrl: m.audioUrl ?? undefined, videoUrl: m.videoUrl ?? undefined, durationMs: m.durationMs ?? undefined,
        }))].sort((a, b) => a.ts - b.ts);
      });
      if (!announce) return;
      setUserChatCount(c => c + msgs.length);
      for (const m of msgs) {
        if (m.text && chatTtsEnabledRef.current && window.speechSynthesis) {
          window.speechSynthesis.speak(new SpeechSynthesisUtterance(`${m.senderName} — ${m.text}`));
        }
      }
    };
    const pollPositions = async () => {
      if (!huntServerReadyRef.current) return;
      try {
        const rows = await api.huntPositions(meetingId);
        if (cancelled) return;
        // Servern ger en rad per användare: telefonens om den är ansluten, annars klockans.
        // Telefonerna syns redan via LiveKit — rita bara de som bara har klockan med.
        const inApp = new Set(rows.filter(r => r.source === 'app').map(r => r.userEmail));
        if (rows.some(r => r.isMe && r.source === 'watch')) { setMyWatchInHunt(true); markWatchInHunt(meetingId); }
        // MT1: utan radio (LiveKit) ritas också telefonernas positioner härifrån.
        setWatchPositions(rows.filter(r => !r.isMe && (r.source === 'watch' ? !inApp.has(r.userEmail) : !lkConnectedRef.current)));
      } catch { /* behåll senaste */ }
    };
    const pollChat = async () => {
      if (!huntServerReadyRef.current) return;
      try {
        const initial = chatAfter === undefined;
        const all = await api.huntChatMessages(meetingId, chatAfter);
        if (cancelled) return;
        chatAfter = all.length ? all[all.length - 1].createdAt : (chatAfter ?? 0);
        const msgs = all.filter(m => !m.isMe);
        // Första hämtningen: historik för den som anslöt sent. Därefter bara klockans meddelanden,
        // telefonernas kommer redan via LiveKit.
        addIncoming(initial || !lkConnectedRef.current ? msgs : msgs.filter(m => m.source === 'watch'), !initial); // MT1: utan radio kommer allt härifrån
      } catch { /* försök igen vid nästa intervall */ }
    };
    api.joinMarkHunt(mark.id, meetingId, false)
      .then(() => { if (cancelled) return; huntServerReadyRef.current = true; void pollPositions(); void pollChat(); })
      .catch(e => console.warn('[QVENARI-MEET] jakten kunde inte kopplas på servern', e));
    const posTid = setInterval(pollPositions, 10000);
    // Hundarna oftare, så att skall syns nästan direkt.
    const pollDogs = async () => {
      if (!huntServerReadyRef.current) return;
      try {
        const dogs = await api.huntDogPositions(meetingId);
        if (!cancelled) setWatchDogs(dogs);
      } catch { /* äldre API utan hundpositioner */ }
    };
    const dogTid = setInterval(pollDogs, 5000);
    const chatTid = setInterval(pollChat, 4000);
    return () => {
      // Telefonen lämnar: dess position tas bort så att klockans (om den är med) tar över direkt.
      if (huntServerReadyRef.current) api.leaveMarkHunt(meetingId).catch(() => {});
      cancelled = true;
      huntServerReadyRef.current = false;
      clearInterval(posTid);
      clearInterval(chatTid);
      setWatchPositions([]);
      setMyWatchInHunt(false);
      clearInterval(dogTid);
      setWatchDogs([]);
    };
  }, [huntSession?.meetingId, mark.id, token]);

  // Allow clicking map to set position manually (fallback for no GPS)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (gpsStatus === 'active') return; // GPS works, no need for fallback
    const handler = (e: any) => {
      if (placingPinType) return;
      if (!userPosition) {
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        const t = Date.now();
        userPositionRef.current = { lat, lng };
        setUserPosition({ lat, lng });
        persistGpsTrackSample(lat, lng, t);
        persistLastKnownPosition(lat, lng, t);
        if (showDistanceRings) updateDistanceRings({ lat, lng }, map);
      }
    };
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [gpsStatus, userPosition, placingPinType, updateDistanceRings, showDistanceRings]);

  const API_URL = (import.meta as any).env?.VITE_API_URL || '';

  async function gqlFetch(query: string, variables?: any) {
    const res = await fetch(resolveUrl('/graphql'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': 'dev-secret',
        ...bearerHeaders(token),
        ...guestTokenHeader(),
      },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0].message);
    return json.data;
  }

  const loadPins = useCallback(async () => {
    try {
      const data = await gqlFetch(`query($markId: ID!) { markPins(markId: $markId) { id markId type lat lng name description imageUrl createdAt } }`, { markId: mark.id });
      setPins((data.markPins || []).map((p: MarkPin) => ({ ...p, type: toPinType(p.type) })));
    } catch (e) {
      console.error('Failed to load pins', e);
    } finally {
      setLoading(false);
    }
  }, [mark.id, token]);

  useEffect(() => {
    loadPins();
  }, [loadPins]);

  // ── Garmin dog positions: poll every 30s ──────────────────────
  const loadDogPositions = useCallback(async () => {
    if (!token || !mark?.id) return;
    try {
      const apiBase = (import.meta as any).env?.VITE_API_URL || '';
      const r = await fetch(`${apiBase}/api/integrations/${mark.id}/garmin/positions`, {
        headers: { ...bearerHeaders(token), 'X-Api-Key': 'dev-secret' },
      });
      if (r.ok) setDogPositions(await r.json());
    } catch {}
  }, [mark?.id, token]);

  useEffect(() => {
    loadDogPositions();
    dogPollRef.current = setInterval(loadDogPositions, 30_000);
    return () => { if (dogPollRef.current) clearInterval(dogPollRef.current); };
  }, [loadDogPositions]);

  // ── Render Garmin dog markers on map ─────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Remove old markers
    dogMarkersRef.current.forEach(m => map.removeLayer(m));
    dogMarkersRef.current = [];
    if (!dogPositions.length) return;

    dogPositions.forEach((pos: any) => {
      const lat = Number(pos.latitude);
      const lng = Number(pos.longitude);
      if (!lat || !lng) return;

      // Custom dog paw icon (orange circle)
      const dogIcon = L.divIcon({
        html: `<div style="
          width:34px;height:34px;border-radius:50%;
          background:#EE8F09;border:3px solid #fff;
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 2px 8px rgba(0,0,0,0.5);
          font-size:17px;line-height:1;
        ">🐕</div>`,
        className: '',
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });

      const dogName = pos.dog_name || pos.device_name || pos.configured_name || pos.imei;
      const battStr = pos.battery_percent != null ? ` · Batteri ${Math.round(pos.battery_percent)}%` : '';
      const timeStr = pos.recorded_at ? new Date(pos.recorded_at).toLocaleTimeString('sv-SE') : '';
      const speedStr = pos.speed != null ? ` · ${Math.round(pos.speed)} km/h` : '';

      const marker = L.marker([lat, lng], { icon: dogIcon })
        .bindPopup(`
          <div style="font-family:sans-serif;min-width:160px">
            <div style="font-weight:700;font-size:14px;margin-bottom:4px">${dogName}</div>
            <div style="font-size:12px;color:#555">GPS: ${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
            <div style="font-size:12px;color:#555">${timeStr}${speedStr}${battStr}</div>
            <div style="font-size:11px;color:#888;margin-top:2px">IMEI: ${pos.imei}</div>
          </div>
        `, { maxWidth: 240 })
        .addTo(map);

      // Label below marker
      const label = L.marker([lat, lng], {
        icon: L.divIcon({
          html: `<div style="
            background:rgba(238,143,9,0.9);color:#000;
            font-size:10px;font-weight:700;
            padding:2px 6px;border-radius:3px;
            white-space:nowrap;margin-top:2px;
          ">${dogName}</div>`,
          className: '',
          iconAnchor: [-2, -20],
        }),
        interactive: false,
        zIndexOffset: -100,
      }).addTo(map);

      dogMarkersRef.current.push(marker, label);
    });
  }, [dogPositions]);

  // ── PAJ-hundar på kartan: orange som övriga hundar, grå när positionen är gammal ──
  const pajMarkersRef = useRef<Record<string, any>>({});
  // Markörerna grånar även mellan hämtningarna.
  useEffect(() => {
    if (!pajDogs.length) return;
    const tid = setInterval(() => setPajNow(Date.now()), 30_000);
    return () => clearInterval(tid);
  }, [pajDogs.length]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const existing = pajMarkersRef.current;
    const keys = new Set(pajDogs.map(p => p.key));
    Object.keys(existing).forEach(k => { if (!keys.has(k)) { map.removeLayer(existing[k]); delete existing[k]; } });
    pajDogs.forEach(p => {
      const icon = L.divIcon({ html: pajDogMarkerHtml(p, pajNow), className: '', iconSize: [34, 34], iconAnchor: [17, 17] });
      const popup = pajDogPopupHtml(p, pajTracks.find(t => t.key === p.key), pajNow);
      const live = pajFreshness(p, pajNow) === 'live';
      if (existing[p.key]) {
        existing[p.key].setLatLng([p.lat, p.lng]).setIcon(icon).setPopupContent(popup).setZIndexOffset(live ? 940 : 900);
      } else {
        existing[p.key] = L.marker([p.lat, p.lng], { icon, zIndexOffset: live ? 940 : 900 }).bindPopup(popup, { maxWidth: 260 }).addTo(map);
      }
    });
  }, [pajDogs, pajTracks, pajNow]);
  useEffect(() => () => {
    const map = mapRef.current;
    Object.values(pajMarkersRef.current).forEach(m => map?.removeLayer(m));
    pajMarkersRef.current = {};
  }, []);

  // ── Abeeway tracker positions: poll every 20s ─────────────────
  const [abewayPositions, setAbewayPositions] = useState<any[]>([]);
  const abewayMarkersRef = useRef<any[]>([]);
  const abewayPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadAbewayPositions = useCallback(async () => {
    if (!token || !mark?.id) return;
    try {
      const apiBase = (import.meta as any).env?.VITE_API_URL || '';
      const r = await fetch(`${apiBase}/api/integrations/${mark.id}/abeeway/positions`, {
        headers: { ...bearerHeaders(token), 'X-Api-Key': 'dev-secret' },
      });
      if (r.ok) setAbewayPositions(await r.json());
    } catch {}
  }, [mark?.id, token]);

  useEffect(() => {
    loadAbewayPositions();
    abewayPollRef.current = setInterval(loadAbewayPositions, 20_000);
    return () => { if (abewayPollRef.current) clearInterval(abewayPollRef.current); };
  }, [loadAbewayPositions]);

  // ── Render Abeeway markers on map ────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    abewayMarkersRef.current.forEach(m => map.removeLayer(m));
    abewayMarkersRef.current = [];
    if (!abewayPositions.length) return;

    abewayPositions.forEach((pos: any) => {
      const lat = Number(pos.latitude);
      const lng = Number(pos.longitude);
      if (!lat || !lng) return;

      const isSmartBadge = pos.device_type === 'smart_badge';
      const isSOS = pos.sos === 1 || pos.sos === true;
      const isManDown = pos.man_down === 1 || pos.man_down === true;
      const hasAlert = isSOS || isManDown;

      // Smart Badge = red/orange person icon, Micro Tracker = blue LoRaWAN icon
      const iconHtml = isSmartBadge
        ? `<div style="
            width:36px;height:36px;border-radius:50%;
            background:${hasAlert ? 'linear-gradient(135deg,#c0392b,#e74c3c)' : 'linear-gradient(135deg,#e67e22,#EE8F09)'};
            border:3px solid ${hasAlert ? '#ff8080' : '#fff'};
            display:flex;align-items:center;justify-content:center;
            box-shadow:0 2px 8px rgba(0,0,0,0.6);
            font-size:17px;line-height:1;
            ${hasAlert ? 'animation:sos-pulse 1s infinite;' : ''}
          ">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white">
              <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z"/>
            </svg>
            ${hasAlert ? '<div style="position:absolute;top:-4px;right:-4px;width:12px;height:12px;background:#ff0000;border-radius:50%;border:2px solid #fff;"></div>' : ''}
          </div>`
        : `<div style="
            width:34px;height:34px;border-radius:50%;
            background:linear-gradient(135deg,#1a6ba0,#00c4ff);
            border:3px solid #fff;
            display:flex;align-items:center;justify-content:center;
            box-shadow:0 2px 8px rgba(0,0,0,0.5);
            font-size:17px;line-height:1;
          ">📡</div>`;

      const iconEl = L.divIcon({
        html: iconHtml,
        className: '',
        iconSize: isSmartBadge ? [36, 36] : [34, 34],
        iconAnchor: isSmartBadge ? [18, 18] : [17, 17],
      });

      const devName = pos.device_name || pos.dev_eui;
      const battStr = pos.battery != null ? ` · Batteri ${pos.battery}%` : '';
      const accStr = pos.accuracy != null ? ` · ±${Math.round(pos.accuracy)}m` : '';
      const timeStr = pos.recorded_at ? new Date(pos.recorded_at).toLocaleTimeString('sv-SE') : '';
      const tempStr = pos.temperature != null ? ` · ${pos.temperature}°C` : '';
      const alertBanner = isSOS
        ? `<div style="background:#c0392b;color:#fff;font-weight:700;font-size:13px;padding:6px 10px;border-radius:4px;margin-bottom:6px;text-align:center;">🆘 SOS-LARM AKTIVT</div>`
        : isManDown
        ? `<div style="background:#e67e22;color:#fff;font-weight:700;font-size:13px;padding:6px 10px;border-radius:4px;margin-bottom:6px;text-align:center;">⚠️ MAN-DOWN LARM</div>`
        : '';
      const deviceLabel = isSmartBadge ? 'Smart Badge (Person)' : 'Micro Tracker (Hund/Utrustning)';

      const marker = L.marker([lat, lng], { icon: iconEl, zIndexOffset: hasAlert ? 1000 : 0 })
        .bindPopup(`
          <div style="font-family:sans-serif;min-width:170px">
            ${alertBanner}
            <div style="font-weight:700;font-size:14px;margin-bottom:2px">${devName}</div>
            <div style="font-size:11px;color:${isSmartBadge ? '#e67e22' : '#1a6ba0'};font-weight:600;margin-bottom:4px">${deviceLabel}</div>
            <div style="font-size:12px;color:#555">GPS: ${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
            <div style="font-size:12px;color:#555">${timeStr}${accStr}${battStr}${tempStr}</div>
            <div style="font-size:11px;color:#888;margin-top:2px">DevEUI: ${pos.dev_eui}</div>
          </div>
        `, { maxWidth: 250 })
        .addTo(map);

      const labelBg = isSmartBadge
        ? (hasAlert ? 'rgba(192,57,43,0.95)' : 'rgba(238,143,9,0.95)')
        : 'rgba(0,196,255,0.9)';
      const labelColor = isSmartBadge ? '#fff' : '#000';

      const labelMark = L.marker([lat, lng], {
        icon: L.divIcon({
          html: `<div style="
            background:${labelBg};color:${labelColor};
            font-size:10px;font-weight:700;
            padding:2px 6px;border-radius:3px;
            white-space:nowrap;margin-top:2px;
          ">${hasAlert ? (isSOS ? 'SOS ' : 'MAN-DOWN ') : ''}${devName}</div>`,
          className: '',
          iconAnchor: [-2, -20],
        }),
        interactive: false,
        zIndexOffset: -100,
      }).addTo(map);

      abewayMarkersRef.current.push(marker, labelMark);
    });
  }, [abewayPositions]);

  const mapDraggedRef = useRef(false);
  // Init map
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;

    const center: [number, number] = mark.mapLat && mark.mapLng
      ? [mark.mapLat, mark.mapLng]
      : [59.322, 16.681];

    const map = L.map(mapDivRef.current, { zoomControl: false }).setView(center, mark.mapLat ? 14 : 12);
    mapRef.current = map;
    setMapReady(true);
    // UX14: en dragning i den inbäddade kartan (för att mäta avstånd) ska inte öppna helskärmen – klicket efter den ignoreras.
    map.on('dragstart', () => { mapDraggedRef.current = true; });
    map.on('dragend', () => { setTimeout(() => { mapDraggedRef.current = false; }, 80); });

    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    });
    osmLayer.addTo(map);
    tileLayerRef.current = osmLayer;

    // Drawn layers
    const drawnItems = new (L as any).FeatureGroup();
    map.addLayer(drawnItems);
    drawnLayersRef.current = drawnItems;

    // Pins layer
    const pinsLayer = new (L as any).FeatureGroup();
    map.addLayer(pinsLayer);
    pinsLayerRef.current = pinsLayer;

    // Åtelkameror (egna nålar med senaste bilden)
    const camerasLayer = new (L as any).FeatureGroup();
    map.addLayer(camerasLayer);
    camerasLayerRef.current = camerasLayer;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Load existing polygon from coordinatesJson
  // Markgränsen ritas som förut; såterna (S1, sub_area) ritas var för sig med namn, och de som är
  // bokade eller har jakt i dag blir röda så att jägare i olika såter ser varandras område.
  const fittedBoundsRef = useRef(false);
  useEffect(() => {
    if (!mapRef.current || !drawnLayersRef.current) return;
    if (!mark.coordinatesJson) return;
    try {
      drawnLayersRef.current.clearLayers();
      const geoJson = typeof mark.coordinatesJson === 'string' ? JSON.parse(mark.coordinatesJson) : mark.coordinatesJson;
      const layer = (L as any).geoJSON(geoJson, {
        filter: (feature: any) => feature?.properties?.type !== 'sub_area',
        style: () => ({ color: '#0064FF', weight: 3, opacity: 0.9, fillOpacity: 0 }),
      });
      layer.addTo(drawnLayersRef.current);
      const satar = parseSatar(geoJson);
      const busy = busyBySat(satar, satOcc);
      satar.forEach((sat, i) => {
        const b = busy.get(sat.id) ?? [];
        const color = b.length ? SAT_BUSY_COLOR : satColor(i);
        const sl = (L as any).geoJSON({ type: 'Feature', properties: {}, geometry: sat.geometry }, {
          style: { color, weight: b.length ? 3 : 2, opacity: 0.85, fillColor: color, fillOpacity: b.length ? 0.14 : 0, dashArray: b.length ? undefined : '6,4' },
          interactive: false,
        });
        sl.bindTooltip(escapePinLabelHtml(b.length ? `${sat.name} · ${occupancyLabel(b[0])}` : sat.name), { permanent: true, direction: 'center', opacity: 0.85 });
        layer.addLayer(sl);
      });
      if (!fittedBoundsRef.current && layer.getBounds && layer.getBounds().isValid()) {
        mapRef.current.fitBounds(layer.getBounds(), { padding: [30, 30] });
        fittedBoundsRef.current = true;
      }
    } catch (e) {
      console.error('Failed to parse coordinatesJson', e);
    }
  }, [mark.coordinatesJson, satOcc]);

  // S1: dagens beläggning per såt (hämtas om var femte minut)
  useEffect(() => {
    let cancelled = false;
    const load = () => api.markSatOccupancy(mark.id, stockholmToday()).then(o => { if (!cancelled) setSatOcc(o); }).catch(() => {});
    void load();
    const t = window.setInterval(load, 5 * 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [mark.id, token]);

  // Åtelkameror — bara markens medlemmar får dem (tom lista annars)
  useEffect(() => {
    let cancelled = false;
    api.markCameras(mark.id).then(c => { if (!cancelled) setCameras(c); }).catch(e => console.error('Failed to load cameras', e));
    return () => { cancelled = true; };
  }, [mark.id, token]);

  useEffect(() => {
    if (!mapRef.current || !camerasLayerRef.current) return;
    camerasLayerRef.current.clearLayers();
    cameras.forEach(cam => {
      if (cam.lat == null || cam.lng == null) return;
      const label = escapePinLabelHtml(cam.name);
      const inner = cam.lastImageUrl
        ? `<img src="${escapePinLabelHtml(mediaPublicUrl(cam.lastImageUrl))}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" />`
        : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:20px;background:${T.surface}">📷</div>`;
      const html = `<div style="position:relative;width:48px;height:48px">
        <div style="width:48px;height:48px;border-radius:8px;overflow:hidden;border:2px solid ${PIN_CONFIG.kamera.color};box-shadow:0 2px 8px rgba(0,0,0,0.45);background:#000">${inner}</div>
        ${cam.lastImageUrl ? `<div style="position:absolute;right:-6px;bottom:-6px;width:20px;height:20px;border-radius:50%;background:${PIN_CONFIG.kamera.color};display:flex;align-items:center;justify-content:center;font-size:11px;box-shadow:0 1px 4px rgba(0,0,0,0.4)">📷</div>` : ''}
        <div style="position:absolute;top:52px;left:50%;transform:translateX(-50%);white-space:nowrap;background:rgba(0,0,0,0.7);color:#fff;font-size:11px;font-weight:700;padding:1px 6px;border-radius:3px">${label}</div>
      </div>`;
      const marker = L.marker([cam.lat, cam.lng], {
        icon: L.divIcon({ html, className: '', iconSize: [48, 48], iconAnchor: [24, 24] }),
        draggable: isAdmin,
      } as any);
      let wasDragged = false;
      if (isAdmin) {
        marker.on('dragstart', () => { wasDragged = true; });
        marker.on('dragend', async () => {
          setTimeout(() => { wasDragged = false; }, 300);
          const { lat, lng } = (marker as any).getLatLng();
          try {
            const updated = await api.updateMarkCamera({ id: cam.id, lat, lng });
            setCameras(prev => prev.map(c => c.id === cam.id ? updated : c));
          } catch (e) { console.error('Failed to move camera', e); }
        });
      }
      marker.on('click', () => { if (!wasDragged) setSelectedCamera(cam); });
      marker.addTo(camerasLayerRef.current);
    });
  }, [cameras, isAdmin, T.surface]);

  // Render pins on map
  useEffect(() => {
    if (!mapRef.current || !pinsLayerRef.current) return;
    pinsLayerRef.current.clearLayers();
    const apiUrl = (import.meta as any).env?.VITE_API_URL || '';
    const visiblePins = pins.filter(pin => {
      if (pin.type === 'pass' && !showPassPins) return false;
      if (pin.type === 'observation' && !showObservationPins) return false;
      if (pin.type === 'skjutet_vilt' && !showSkjutetViltPins) return false;
      return true;
    });
    visiblePins.forEach(pin => {
      const pt = pin.type as PinType;
      const marker = L.marker([pin.lat, pin.lng], {
        icon: createPinIcon(pt, pin.name, pt === 'pass' ? { passShowLabel: showPassPinLabels } : undefined),
        draggable: isAdmin,
      } as any);

      let wasDragged = false;

      if (isAdmin) {
        marker.on('dragstart', () => { wasDragged = true; });
        marker.on('dragend', async () => {
          setTimeout(() => { wasDragged = false; }, 300);
          const latlng = (marker as any).getLatLng();
          try {
            await fetch(resolveUrl('/graphql'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Api-Key': 'dev-secret', ...bearerHeaders(token) },
              body: JSON.stringify({
                query: `mutation($input: UpdateMarkPinInput!) { updateMarkPin(input: $input) { id lat lng } }`,
                variables: { input: { id: pin.id, lat: latlng.lat, lng: latlng.lng } },
              }),
            });
            setPins(prev => prev.map(p => p.id === pin.id ? { ...p, lat: latlng.lat, lng: latlng.lng } : p));
          } catch (e) { console.error('Failed to move pin', e); }
        });
      }

      marker.on('click', () => {
        if (wasDragged) return;
        if (pin.type === 'skjutet_vilt') {
          // Load full details including events before opening panel
          gqlFetch(
            `query($pinId: ID!) { falltViltPublic(pinId: $pinId) { id markId type lat lng name viltslag kon alder jagarEmail falltStatus qregistryObjectId photoUrls falltViltEvents { id eventType authorEmail oldStatus newStatus commentText newLat newLng photoUrl createdAt } createdAt } }`,
            { pinId: pin.id }
          ).then((data: any) => {
            setSelectedViltPin(data.falltViltPublic || pin);
          }).catch(() => setSelectedViltPin(pin));
        } else {
          setSelectedPin(pin);
        }
      });

      marker.addTo(pinsLayerRef.current);
    });
  }, [pins, isAdmin, token, showPassPins, showPassPinLabels, showObservationPins, showSkjutetViltPins]);

  // Switch tile layer
  useEffect(() => {
    if (!mapRef.current || !tileLayerRef.current) return;
    mapRef.current.removeLayer(tileLayerRef.current);
    const url = mapType === 'satellite'
      ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      : mapType === 'terrain'
        ? 'https://tile.opentopomap.org/{z}/{x}/{y}.png'
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const attribution = mapType === 'satellite'
      ? '© Esri'
      : mapType === 'terrain'
        ? '© OpenStreetMap, © OpenTopoMap (CC-BY-SA), SRTM'
        : '© OpenStreetMap';
    const opts: L.TileLayerOptions = { attribution, maxZoom: 19 };
    if (mapType === 'terrain') opts.maxNativeZoom = 17;
    const newLayer = L.tileLayer(url, opts);
    newLayer.addTo(mapRef.current);
    tileLayerRef.current = newLayer;
  }, [mapType]);

  // Fastighetsgränser WMS layer (Lantmäteriet) — rosa streckade gränser + fastighetsnamn
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (showFastighetsgranser) {
      // Gränser-lager med rosa streckad stil
      if (!fastighetsgranserLayerRef.current) {
        const sldBody = FASTIGHET_GRANSER_SLD;

        fastighetsgranserLayerRef.current = (L as any).tileLayer.wms(
          FASTIGHET_WMS_URL,
          {
            layers: 'granser',
            format: 'image/png',
            transparent: true,
            attribution: '© Lantmäteriet',
            opacity: 0.9,
            maxZoom: 19,
            styles: '',
            sld_body: sldBody,
          }
        );
      }
      fastighetsgranserLayerRef.current.addTo(map);

      // Fastighetsnamn (text-lager)
      if (!fastighetsTextLayerRef.current) {
        fastighetsTextLayerRef.current = (L as any).tileLayer.wms(
          FASTIGHET_WMS_URL,
          {
            layers: 'text',
            format: 'image/png',
            transparent: true,
            attribution: '© Lantmäteriet',
            opacity: 0.9,
            maxZoom: 19,
          }
        );
      }
      fastighetsTextLayerRef.current.addTo(map);
    } else {
      if (fastighetsgranserLayerRef.current) {
        map.removeLayer(fastighetsgranserLayerRef.current);
      }
      if (fastighetsTextLayerRef.current) {
        map.removeLayer(fastighetsTextLayerRef.current);
      }
    }
  }, [showFastighetsgranser]);

  // ── Vittring (scent cone) ─────────────────────────────────────────────────

  // Var vädret hämtas: avrundat till ~1 km, så att GPS-uppdateringar var några sekunder inte
  // hämtar om vädret hela tiden (raden blinkade "…" vid varje ny position).
  const weatherSpot = useMemo(() => {
    const lat = userPosition?.lat ?? mark.mapLat ?? mapRef.current?.getCenter()?.lat ?? null;
    const lng = userPosition?.lng ?? mark.mapLng ?? mapRef.current?.getCenter()?.lng ?? null;
    if (lat == null || lng == null) return null;
    return `${lat.toFixed(2)},${lng.toFixed(2)}`;
  }, [userPosition, mark.mapLat, mark.mapLng, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps
  // Hämta om vädret och vinden en gång i kvarten även om man står still.
  const [weatherTick, setWeatherTick] = useState(0);
  useEffect(() => {
    if (!showWeather && !showVittring) return;
    const tid = setInterval(() => setWeatherTick(t => t + 1), 15 * 60 * 1000);
    return () => clearInterval(tid);
  }, [showWeather, showVittring]);

  // Vind för vittring (Open-Meteo `current` — hourly använde fel fältnamn → vind blev alltid null)
  useEffect(() => {
    if (!showVittring) {
      setVittringWindDir(null);
      setVittringWindSpd(null);
      return;
    }
    if (!weatherSpot) return;
    const [lat, lng] = weatherSpot.split(',');
    let cancelled = false;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&timezone=Europe%2FStockholm&current=wind_speed_10m,wind_direction_10m&wind_speed_unit=ms&forecast_days=1`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const c = data?.current;
        const dirRaw = c?.wind_direction_10m;
        const spdRaw = c?.wind_speed_10m;
        const dir = typeof dirRaw === 'number' && !Number.isNaN(dirRaw) ? dirRaw : 270;
        const spd = typeof spdRaw === 'number' && !Number.isNaN(spdRaw) ? spdRaw : 3;
        setVittringWindDir(dir);
        setVittringWindSpd(spd);
      })
      .catch(() => {
        // Behåll senaste vinden vid nätfel; bara utan tidigare värde används ett antaget.
        if (!cancelled) {
          setVittringWindDir(d => d ?? 270);
          setVittringWindSpd(v => v ?? 3);
        }
      });
    return () => { cancelled = true; };
  }, [showVittring, weatherSpot, weatherTick]);

  // Väder: kompakt rad bredvid hamburgaren (hämtas när showWeather)
  useEffect(() => {
    if (!showWeather) {
      setWeatherSnap(null);
      return;
    }
    if (!weatherSpot) return;
    const [lat, lng] = weatherSpot.split(',');
    let cancelled = false;
    setWeatherLoading(true);
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&timezone=Europe%2FStockholm&current=temperature_2m,weather_code,wind_speed_10m,wind_direction_10m&wind_speed_unit=ms&daily=sunrise,sunset&forecast_days=1`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const cur = data?.current;
        const sr = data?.daily?.sunrise?.[0];
        const ss = data?.daily?.sunset?.[0];
        if (!cur) return; // behåll senaste vädret
        setWeatherSnap({
          tempC: Number(cur.temperature_2m),
          code: Number(cur.weather_code ?? 0),
          windMs: Number(cur.wind_speed_10m ?? 0),
          windDeg: Number(cur.wind_direction_10m ?? 0),
          sunrise: formatSunTimeLocal(sr),
          sunset: formatSunTimeLocal(ss),
        });
      })
      .catch(() => { /* behåll senaste vädret vid nätfel */ })
      .finally(() => { if (!cancelled) setWeatherLoading(false); });
    return () => { cancelled = true; };
  }, [showWeather, weatherSpot, weatherTick]);

  // Vittring: mjuk sektor — flera lager med tydligare opacitet (tidigare ~0.05–0.14 var nästan osynlig)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (vittringLayerRef.current) {
      map.removeLayer(vittringLayerRef.current);
      vittringLayerRef.current = null;
    }
    if (!showVittring || vittringWindDir === null) return;

    const origin = userPosition
      ?? (mark.mapLat != null && mark.mapLng != null ? { lat: mark.mapLat, lng: mark.mapLng } : null)
      ?? { lat: map.getCenter().lat, lng: map.getCenter().lng };

    // Geometrin (12 tunna lager, rundad spets) delas med 3D-vyn: karta/mapGeometry.ts.
    const layers = vittringCone(origin, vittringWindDir, vittringWindSpd).map(({ pts, op }) =>
      L.polygon(pts, {
        stroke: false,
        fillColor: VITTRING_COLOR,
        fillOpacity: op,
        interactive: false,
        smoothFactor: 0,
      }),
    );

    const group = L.layerGroup(layers);
    group.addTo(map);
    vittringLayerRef.current = group;
  }, [showVittring, vittringWindDir, vittringWindSpd, userPosition, mark.mapLat, mark.mapLng, mapReady]);

  // Map click handler for placing pins
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const handler = (e: any) => {
      if (placingPinType) {
        if (placingPinType === 'skjutet_vilt') {
          setShowFalltViltModal({ lat: e.latlng.lat, lng: e.latlng.lng });
          setPlacingPinType(null);
        } else {
          setShowPinModal({ lat: e.latlng.lat, lng: e.latlng.lng, type: placingPinType });
        }
      }
    };
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [placingPinType]);

  // Polygon drawing
  const startDrawPolygon = () => {
    if (!mapRef.current || !drawnLayersRef.current) return;
    setDrawingPolygon(true);
    setPlacingPinType(null);
    const drawControl = new (L as any).Draw.Polygon(mapRef.current, {
      shapeOptions: { color: '#0064FF', weight: 3, fillOpacity: 0 },
    });
    drawControl.enable();
    mapRef.current.once('draw:created', (e: any) => {
      const layer = e.layer;
      // Såterna (sub_area) följer med när gränsen ritas om.
      let keep: any[] = [];
      try {
        const cur = lastCoordinatesJsonRef.current;
        const fc = typeof cur === 'string' ? JSON.parse(cur) : cur;
        keep = (fc?.features ?? []).filter((f: any) => f?.properties?.type === 'sub_area');
      } catch { /* */ }
      const newGeoJson = {
        type: 'FeatureCollection',
        features: [
          { type: 'Feature', properties: { type: 'huntarea_border', name: mark.name }, geometry: layer.toGeoJSON().geometry },
          ...keep,
        ]
      };
      setPendingPolygon(JSON.stringify(newGeoJson));
      drawnLayersRef.current.addLayer(layer);
      setDrawingPolygon(false);
    });
    mapRef.current.once('draw:drawstop', () => setDrawingPolygon(false));
  };

  const savePolygon = async () => {
    if (!pendingPolygon) return;
    setSaving(true);
    try {
      await onMarkUpdated({ coordinatesJson: pendingPolygon });
      setPendingPolygon(null);
    } finally {
      setSaving(false);
    }
  };

  const confirmPin = async () => {
    if (!showPinModal) return;
    setSaving(true);
    try {
      if (showPinModal.type === 'kamera') {
        const cam = await api.createMarkCamera({ markId: mark.id, name: newPinName.trim() || `Åtelkamera ${cameras.length + 1}`, lat: showPinModal.lat, lng: showPinModal.lng });
        setCameras(prev => [...prev, cam]);
        setShowPinModal(null);
        setNewPinName('');
        setPlacingPinType(null);
        setSelectedCamera(cam); // visa adressen direkt så den kan läggas in i kameran
        return;
      }
      const data = await gqlFetch(
        `mutation($input: CreateMarkPinInput!) { createMarkPin(input: $input) { id markId type lat lng name description createdAt } }`,
        { input: { markId: mark.id, type: showPinModal.type, lat: showPinModal.lat, lng: showPinModal.lng, name: newPinName || null } }
      );
      setPins(prev => [...prev, data.createMarkPin]);
      setShowPinModal(null);
      setNewPinName('');
    } finally {
      setSaving(false);
    }
  };

  const handleGpxImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const { waypoints, tracks } = parseGpx(text);

      // Build GeoJSON FeatureCollection from tracks
      const features: any[] = tracks.map(trk => ({
        type: 'Feature',
        properties: { type: trk.type, name: trk.name },
        geometry: {
          type: 'Polygon',
          coordinates: [[...trk.points.map(p => [p.lng, p.lat]), [trk.points[0].lng, trk.points[0].lat]]],
        },
      }));
      const coordinatesJson = features.length > 0 ? JSON.stringify({ type: 'FeatureCollection', features }) : undefined;

      // Center: use centroid of main boundary if available
      const mainTrack = tracks.find(t => t.type === 'huntarea_border');
      let centerLat: number | undefined, centerLng: number | undefined;
      if (mainTrack && mainTrack.points.length > 0) {
        centerLat = mainTrack.points.reduce((s, p) => s + p.lat, 0) / mainTrack.points.length;
        centerLng = mainTrack.points.reduce((s, p) => s + p.lng, 0) / mainTrack.points.length;
      }

      // Filter waypoints to main area (within bounding box of tracks)
      const allPts = tracks.flatMap(t => t.points);
      const minLat = Math.min(...allPts.map(p => p.lat)) - 0.1;
      const maxLat = Math.max(...allPts.map(p => p.lat)) + 0.1;
      const minLng = Math.min(...allPts.map(p => p.lng)) - 0.1;
      const maxLng = Math.max(...allPts.map(p => p.lng)) + 0.1;
      const filteredWpts = waypoints.filter(w =>
        allPts.length === 0 || (w.lat >= minLat && w.lat <= maxLat && w.lng >= minLng && w.lng <= maxLng)
      );

      const pinsInput = filteredWpts.map(w => ({
        markId: mark.id,
        type: wehuntTypeToPinType(w.type, w.name),
        lat: w.lat,
        lng: w.lng,
        name: w.name || null,
        description: w.desc || null,
      }));

      await gqlFetch(
        `mutation($markId: ID!, $pins: [CreateMarkPinInput!]!, $coordinatesJson: String) { importMarkData(markId: $markId, pins: $pins, coordinatesJson: $coordinatesJson) }`,
        { markId: mark.id, pins: pinsInput, coordinatesJson }
      );

      // Update mark coordinates (non-fatal — pins are already saved)
      if (coordinatesJson) {
        try {
          await onMarkUpdated({
            coordinatesJson,
            ...(centerLat !== undefined ? { mapLat: centerLat, mapLng: centerLng } : {})
          });
        } catch (updErr) {
          console.warn('Mark coordinate update failed (non-fatal):', updErr);
        }
      }

      await loadPins();
      alert(`Import klar! ${filteredWpts.length} pins och ${features.length} områden importerade.`);
    } catch (err) {
      console.error('GPX import failed', err);
      alert('Import misslyckades: ' + (err as any).message);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // Invalidate map size when fullscreen changes (WebKit kan lägga ut sidan sent — räkna om igen efter en stund)
  useEffect(() => {
    if (!mapRef.current) return;
    const timers = [50, 350].map(ms => setTimeout(() => mapRef.current?.invalidateSize?.(), ms));
    return () => timers.forEach(clearTimeout);
  }, [fullscreen]);

  // B8: sidan under helskärmen står still (i iPhone-appen kunde den skrolla bakom kartan, och efteråt
  // låg menyn/flikraden över kartan). Vid utgång (UX6): markens startsida om kartan syns där, annars samma
  // skrolläge som innan; täcker en fast meny ändå kartans överkant skrollas kartan fram.
  // Skrollläget innan helskärmen: läses av en lyssnare medan sidan syns, eftersom window.scrollY redan är
  // klämt när effekten körs. Öppnas kartan direkt i helskärm (appen) blir det 0, alltså markens överkant.
  const fullscreenRef = useRef(fullscreen);
  fullscreenRef.current = fullscreen;
  // Kartvyns höjd i sidan (utanför helskärm), för platshållaren medan helskärmen är öppen.
  const rootRef = useRef<HTMLDivElement>(null);
  const [inFlowHeight, setInFlowHeight] = useState(0);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (!fullscreenRef.current) setInFlowHeight(el.offsetHeight); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pageScrollYRef = useRef(fullscreen ? 0 : window.scrollY);
  useEffect(() => {
    const onScroll = () => { if (!fullscreenRef.current) pageScrollYRef.current = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!fullscreen) return;
    const savedY = pageScrollYRef.current;
    const restore = lockPageScroll(savedY);
    const div = mapDivRef.current;
    return () => {
      const stillHere = !!div?.isConnected; // annars stängdes fliken i helskärm — skrolla inte
      restore(false);
      if (stillHere && div) scrollToWhenLaidOut(scrollAfterFullscreen(savedY, div.getBoundingClientRect().top + window.scrollY, window.innerHeight));
      // Efter att skrolläget återställts (det tar några bildrutor medan sidan växer tillbaka).
      if (stillHere) setTimeout(() => { if (div?.isConnected) uncoverTop(div); }, 150);
    };
  }, [fullscreen]);

  const toggleCompassRef = useRef(toggleCompass); toggleCompassRef.current = toggleCompass;
  // UX6: Esc stänger helskärmen på datorn (som knappen), om inget annat i kartan redan tagit tangenten.
  useEffect(() => {
    if (!fullscreen || IS_CAP_NATIVE) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('input, textarea, select, [role="dialog"]') || t.isContentEditable)) return;
      setFullscreen(false); setShowMapSettings(false); if (compassActiveRef.current) toggleCompassRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  function broadcastHuntChatPayload(payload: Record<string, unknown>) {
    const r = lkRoomRef.current;
    if (!r || r.state !== 'connected') return;
    r.localParticipant.publishData(
      new TextEncoder().encode(JSON.stringify(payload)),
      { reliable: true },
    ).catch(() => {});
  }

  function sendHuntChatMessage() {
    const text = huntChatDraft.trim();
    if (!text || !huntSession) return;
    const chatId = `hc:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`;
    const ts = Date.now();
    const fromName = userName || 'Jägare';
    const pos = userPositionRef.current;
    setHuntChatRows(prev => [...prev, { kind: 'user', id: chatId, senderName: fromName, text, ts, lat: pos?.lat, lng: pos?.lng, mine: true }]);
    setUserChatCount(c => c + 1);
    setHuntChatDraft('');
    // Broadcast via LiveKit if connected (optional — kill detection runs regardless)
    const r = lkRoomRef.current;
    if (r && r.state === 'connected') {
      if (chatTtsEnabledRef.current && window.speechSynthesis) {
        window.speechSynthesis.speak(new SpeechSynthesisUtterance(`${fromName} — ${text}`));
      }
      broadcastHuntChatPayload({
        type: HUNT_CHAT_MSG_V1,
        chatId,
        fromName,
        text,
        ts,
        lat: pos?.lat,
        lng: pos?.lng,
        senderIdentity: r.localParticipant.identity,
      });
    }
    if (huntServerReadyRef.current) {
      api.sendHuntChatMessage(huntSession.meetingId, { clientId: chatId, text, lat: pos?.lat, lng: pos?.lng }).catch(() => {});
    }
    // Fällt vilt i chatten ("skjuten dovkalv", "sugga ligger") → fråga om typen, med det som skrevs förvalt (QV1).
    const game = parseHeardGame(text);
    const outcome = game ? heardOutcome(text) : null;
    const isKillMsg = !!game && (outcome === 'fallt' || outcome === 'skott' || (outcome == null && text.trim().length <= 40));
    if (game && isKillMsg) {
      const subcatLabels = SUBCAT_MAP[game.family];
      if (subcatLabels && subcatLabels.length > 0) {
        const fullSubcats = matchingSubcats(game, subcatLabels).map(s => `${game.label} — ${s}`);
        setHuntChatRows(prev => [...prev, {
          kind: 'assistant',
          id: `kq_${Date.now()}`,
          text: fullSubcats.length === 1 ? `Registrera ${fullSubcats[0].toLowerCase()} som fällt?` : `Vilken typ av ${heardGameText(game).toLowerCase()} sköts?`,
          inferredFrom: fromName,
          ts: ts + 10,
          gameType: game.label,
          isKill: true,
          subcats: fullSubcats,
          kon: game.kon,
          alder: game.alder,
          lat: pos?.lat,
          lng: pos?.lng,
        }]);
      }
      // Utan underkategorier: confirmAndSaveChatInsight med artens namn
    }
  }

  // ── I2: röst- och videomeddelanden ──
  // Kräver inloggning (POST /api/q/hunt/…/chat-media); gäster har bara text.
  const canSendHuntMedia = !!token && !!huntSession && !isGuestHunt(huntSession.meetingId);
  const walkieTitle = canSendHuntMedia ? 'Walkie-talkie: tryck för att prata i radion, håll in för röstmeddelande till chatten' : 'Walkie-talkie: prata i radion';

  /** WebKit ska ta mikrofonen: jaktens lyssning släpper den först (regel 1 i QVoicePlugin.swift). */
  async function pauseHuntListening(): Promise<boolean> {
    if (!transcribingRef.current) return false;
    transcribingRef.current = false;
    stopSpeech();
    await new Promise(r => setTimeout(r, 300));
    return true;
  }
  function resumeHuntListening(was: boolean) {
    if (was && huntSessionRef.current && !transcribingRef.current) {
      transcribingRef.current = true;
      startSpeech(userName || 'Jägare');
    }
  }

  /** hold: walkie-talkien hålls in och släpps för att skicka; annars Skicka/Avbryt i inspelningsraden. */
  async function startVoiceMessage(hold: boolean) {
    if (!canSendHuntMedia || voiceRecRef.current || voiceRecStartingRef.current) return;
    voiceRecStartingRef.current = { stop: null };
    // Med telefonens lyssning igång spelar samma ljudmotor in; annars WebKit (getUserMedia).
    let rec: VoiceRecording | null = nativeListenRef.current ? await startNativeVoiceRecording() : null;
    let resume = false;
    if (!rec) {
      resume = await pauseHuntListening();
      try {
        rec = await startWebVoiceRecording();
      } catch {
        voiceRecStartingRef.current = null;
        resumeHuntListening(resume);
        setMediaNotice('Mikrofonen kunde inte startas');
        return;
      }
    }
    const pendingStop = voiceRecStartingRef.current?.stop ?? null;
    voiceRecStartingRef.current = null;
    voiceRecRef.current = { rec, resume };
    setVoiceRec({ startedAt: Date.now(), hold });
    setVoiceRecNow(Date.now());
    if (pendingStop) void finishVoiceMessage(pendingStop === 'send');
  }

  async function finishVoiceMessage(send: boolean) {
    if (voiceRecStartingRef.current) { voiceRecStartingRef.current.stop = send ? 'send' : 'cancel'; return; }
    const cur = voiceRecRef.current;
    if (!cur) return;
    voiceRecRef.current = null;
    setVoiceRec(null);
    let recorded: Awaited<ReturnType<VoiceRecording['stop']>> = null;
    if (send) recorded = await cur.rec.stop();
    else cur.rec.cancel();
    resumeHuntListening(cur.resume);
    if (!recorded) return;
    if (recorded.durationMs < VOICE_MESSAGE_MIN_MS) { setMediaNotice('För kort – håll in och prata'); return; }
    void postHuntChatMedia('audio', recorded.blob, recorded.durationMs, recorded.text);
  }

  async function openVideoRecorder() {
    if (!canSendHuntMedia || showVideoRec) return;
    if (camOn || glassesOn) { setMediaNotice('Stäng kameran i radion först'); return; }
    videoRecResumeRef.current = await pauseHuntListening();
    setShowVideoRec(true);
  }
  function closeVideoRecorder() {
    setShowVideoRec(false);
    // Låt WebKit släppa kameran och mikrofonen innan telefonen börjar lyssna igen.
    const was = videoRecResumeRef.current;
    videoRecResumeRef.current = false;
    setTimeout(() => resumeHuntListening(was), 300);
  }

  /** Visas direkt i chatten (lokalt ljud/video), laddas upp och ersätts med serverns adress. */
  async function postHuntChatMedia(kind: 'audio' | 'video', blob: Blob, durationMs: number, text?: string) {
    if (!huntSession) return;
    const meetingId = huntSession.meetingId;
    const chatId = `hc:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`;
    const ts = Date.now();
    const fromName = userName || 'Jägare';
    const pos = userPositionRef.current;
    const localUrl = URL.createObjectURL(blob);
    setHuntChatRows(prev => [...prev, {
      kind: 'user', id: chatId, senderName: fromName, text: text || undefined, ts, mine: true, pending: true, durationMs,
      ...(kind === 'video' ? { videoUrl: localUrl } : { audioUrl: localUrl }),
    }]);
    setUserChatCount(c => c + 1);
    try {
      const msg = await sendHuntChatMedia(meetingId, { kind, clientId: chatId, blob, durationMs, text, lat: pos?.lat, lng: pos?.lng });
      setHuntChatRows(prev => prev.map(r => (r.kind === 'user' && r.id === chatId
        ? { ...r, pending: false, text: msg.text ?? undefined, audioUrl: msg.audioUrl ?? undefined, videoUrl: msg.videoUrl ?? undefined }
        : r)));
      URL.revokeObjectURL(localUrl);
      const room = lkRoomRef.current;
      if (room?.state === 'connected') {
        broadcastHuntChatPayload({
          type: HUNT_CHAT_MSG_V1, chatId, fromName, text: msg.text ?? undefined, ts, durationMs,
          audioUrl: msg.audioUrl ?? undefined, videoUrl: msg.videoUrl ?? undefined, senderIdentity: room.localParticipant.identity,
        });
      }
      setMediaNotice(kind === 'video' ? 'Videon är skickad och finns i markens album' : 'Röstmeddelandet är skickat till jaktchatten');
    } catch (e) {
      setHuntChatRows(prev => prev.filter(r => !(r.kind === 'user' && r.id === chatId)));
      URL.revokeObjectURL(localUrl);
      setMediaNotice(`${kind === 'video' ? 'Videon' : 'Röstmeddelandet'} kunde inte skickas${e instanceof Error && e.message ? `: ${e.message}` : ''}`);
    }
  }

  async function handleHuntChatImagePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !token || !huntSession) return;
    const r = lkRoomRef.current;
    if (!r || r.state !== 'connected') {
      alert('Anslut till mötet först.');
      return;
    }
    try {
      const up = await api.uploadFile(file);
      const chatId = `hc:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`;
      const ts = Date.now();
      const fromName = userName || 'Jägare';
      setHuntChatRows(prev => [...prev, { kind: 'user', id: chatId, senderName: fromName, imageUrl: up.url, ts, mine: true }]);
      broadcastHuntChatPayload({
        type: HUNT_CHAT_MSG_V1,
        chatId,
        fromName,
        imageUrl: up.url,
        ts,
        senderIdentity: r.localParticipant.identity,
      });
      if (huntServerReadyRef.current) {
        api.sendHuntChatMessage(huntSession.meetingId, { clientId: chatId, imageUrl: up.url }).catch(() => {});
      }
    } catch {
      alert('Bilduppladdning misslyckades.');
    }
  }

  async function confirmAndSaveChatInsight(row: Extract<HuntChatRow, { kind: 'assistant' }>) {
    if (row.savedToMark) return;
    setHuntChatSavingId(row.id);
    const lat = row.lat ?? userPositionRef.current?.lat ?? (mark.mapLat ?? undefined);
    const lng = row.lng ?? userPositionRef.current?.lng ?? (mark.mapLng ?? undefined);
    if (lat == null || lng == null) {
      alert('Ingen position tillgänglig — kan inte spara utan koordinater.');
      setHuntChatSavingId(null);
      return;
    }
    const pinName = row.gameType ?? (row.isKill ? 'Skjutet vilt' : 'Observation');
    try {
      if (row.isKill) {
        // Skapat fallt vilt-pin (med status-logg) + viltregistrering automatiskt
        const pin = await api.createFalltViltPin({ markId: mark.id, lat, lng, viltslag: pinName, name: pinName, kon: row.kon, alder: row.alder });
        setPins(prev => [...prev, pin]);
        const now2 = new Date();
        const season = now2.getMonth() >= 6 ? `${now2.getFullYear()}/${now2.getFullYear()+1}` : `${now2.getFullYear()-1}/${now2.getFullYear()}`;
        await api.addViltRegistration({ markId: mark.id, season, gameType: pinName, falltViltPinId: pin.id, shotDate: now2.toISOString().slice(0, 10), sex: row.kon, ageClass: row.alder });
      } else {
        // Skadskjutet (QV1): fällt vilt har ingen status för det, så det blir en obs med eftersök i namnet.
        const obsName = row.wounded ? `${pinName} – skadskjuten` : pinName;
        const pin = await api.createMarkPin({ markId: mark.id, type: 'observation', lat, lng, name: obsName, description: `${row.text}${row.wounded ? '\nSkadskjutet – eftersök.' : ''}\n(Uppfattat från: ${row.inferredFrom})` });
        setPins(prev => [...prev, pin]);
      }
      setHuntChatRows(prev => prev.map(r => (r.kind === 'assistant' && r.id === row.id ? { ...r, savedToMark: true } : r)));
    } catch {
      alert('Kunde inte spara till jakt och mark.');
    } finally {
      setHuntChatSavingId(null);
    }
  }

  // ── Invite helpers ───────────────────────────────────────────────
  async function generateInviteQR() {
    if (!huntSession) return;
    // GU1: anslutningskoden kommer från servern (slumpad, gäller bara medan jakten pågår).
    setInviteCode('');
    if (!isGuestHunt(huntSession.meetingId)) guestApi.invite(huntSession.meetingId).then(c => setInviteCode(c.formatted)).catch(() => setInviteCode(''));
    const joinUrl = `${publicWebAppUrl()}/?join=${encodeURIComponent(huntSession.meetingId)}&mark=${encodeURIComponent(mark.id)}`;
    setInviteJoinUrl(joinUrl);
    try {
      const dataUrl = await QRCode.toDataURL(joinUrl, {
        width: 512,
        margin: 3,
        errorCorrectionLevel: 'H',
        color: { dark: '#000000', light: '#ffffff' },
      });
      setInviteQR(dataUrl);
    } catch { setInviteQR(''); }
    setShowInviteModal(true);
  }

  return (
    <>
    {/* Platshållare med kartvyns höjd medan den är i helskärm: annars krymper sidan och webbläsaren klämmer skrolläget (B8). */}
    {fullscreen && inFlowHeight > 0 && <div aria-hidden style={{ height: inFlowHeight }} />}
    <div ref={rootRef} style={fullscreen ? {
      position: 'fixed', inset: 0, zIndex: 9990, background: T.bg,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    } : { display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* GU1: gäster som väntar på att släppas in (bara för inloggade deltagare) */}
      {huntSession && token && !isGuestHunt(huntSession.meetingId) && <GuestRequests meetingId={huntSession.meetingId} />}

      {/* Normal mode toolbar (döljs på jaktmarkens karta-flik — kartval/snabbstart flyttas till helskärm / Jakt-menyn) */}
      {!fullscreen && (
        <>
          {!isMarkDetail && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Fullscreen toggle */}
            <button onClick={() => { setFullscreen(true); }} title="Helskärm" style={{
              padding: '6px 14px', borderRadius: 8, border: `2px solid ${T.amber}`,
              background: T.amber, color: '#0a0a0a', cursor: 'pointer', fontSize: 16, fontWeight: 700, lineHeight: 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}>
              ⛶
            </button>

            {/* Map type toggle */}
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `2px solid ${T.amber}` }}>
              {BASE_MAP_TYPES.map(t => (
                <button key={t} onClick={() => setMapType(t)} style={{
                  padding: '6px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                  background: mapType === t ? T.amber : 'rgba(0,0,0,0.12)',
                  color: mapType === t ? '#0a0a0a' : T.amber,
                }}>
                  {t === 'mapnik' ? 'Karta' : t === 'satellite' ? 'Satellit' : 'Terräng'}
                </button>
              ))}
            </div>

            {/* Fastighetsgränser toggle */}
            <button
              onClick={() => setShowFastighetsgranser(v => !v)}
              title="Fastighetsgränser (Lantmäteriet)"
              style={{
                padding: '6px 14px', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${showFastighetsgranser ? '#FFD700' : T.forest}`,
                background: showFastighetsgranser ? 'rgba(255,215,0,0.15)' : 'transparent',
                color: showFastighetsgranser ? '#FFD700' : T.amber,
                transition: 'all 0.2s',
              }}
            >
              {showFastighetsgranser ? '⬜ Fastighetsgränser' : '⬜ Fastighetsgränser'}
            </button>

          </div>
          )}

          {placingPinType && (
            <div style={{ background: PIN_CONFIG[placingPinType].color, color: 'white', padding: '8px 14px', borderRadius: 4, fontSize: 13, fontWeight: 600 }}>
              Klicka på kartan för att placera: {PIN_CONFIG[placingPinType].label} — klicka igen för att avbryta
            </div>
          )}

          {/* Navigation banner (normal mode) */}
          {navigatingTo && (
            <div style={{
              background: T.surface,
              border: `1px solid ${pinConfig(navigatingTo.type).color}`,
              borderRadius: 5, padding: '8px 14px',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: pinConfig(navigatingTo.type).color, flexShrink: 0 }} />
              <span style={{ color: T.text, fontWeight: 600, fontSize: 13, flex: 1 }}>
                Navigerar: {navigatingTo.name || pinConfig(navigatingTo.type).label}
                {userPosition && (
                  <span style={{ color: pinConfig(navigatingTo.type).color, marginLeft: 8 }}>
                    {formatDistance(haversine(userPosition, navigatingTo))}
                  </span>
                )}
              </span>
              <button onClick={clearNavLine} style={{
                background: 'transparent', border: `1px solid ${pinConfig(navigatingTo.type).color}`,
                color: pinConfig(navigatingTo.type).color, borderRadius: 3, padding: '4px 12px',
                cursor: 'pointer', fontSize: 12, fontWeight: 600,
              }}>
                Avsluta
              </button>
            </div>
          )}
          {/* Snabbstart / jaktverktyg i infälld vy — döljs på jaktmarkens karta (använd Jakt-knappen) */}
          {!isMarkDetail && !huntSession && onStartHunt && (
            <button
              onClick={async () => { setHuntStarting(true); try { await onStartHunt(); } finally { setHuntStarting(false); } }}
              disabled={huntStarting}
              style={{
                padding: '10px 20px', borderRadius: 6,
                background: T.amber, color: '#0a0a0a', fontWeight: 700, fontSize: 14,
                cursor: huntStarting ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                opacity: huntStarting ? 0.6 : 1, fontFamily: 'inherit',
                border: `2px solid ${T.amber}`,
                boxShadow: '0 2px 12px rgba(238,143,9,0.35)',
              }}
            >
              {huntStarting ? 'Startar...' : 'Snabbstarta jakt'}
            </button>
          )}

          {!isMarkDetail && huntSession && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: T.surface, borderRadius: 6, padding: '6px 10px',
              border: `1px solid ${T.border}`,
            }}>
              <div title={lkConnected ? 'Radion är på' : radioOn ? (lkError || 'Ansluter') : 'Radion startar när någon trycker på walkie-talkien eller kameran'} style={{ width: 8, height: 8, borderRadius: '50%', background: lkConnected ? '#44ff44' : radioOn ? '#ffaa00' : T.border, animation: radioOn ? 'pulse 1.5s infinite' : undefined, flexShrink: 0 }} />
              
              <HuntToolbarButton size="sm" onClick={toggleMic} title={micOn ? 'Walkie-talkie: sluta sända' : walkieTitle} active={micOn || !!voiceRec} amber={T.amber} ink={T.text}
                onHoldStart={canSendHuntMedia ? () => { void startVoiceMessage(true); } : undefined} onHoldEnd={() => { void finishVoiceMessage(true); }}>
                <WalkieTalkieIcon size={16} />
              </HuntToolbarButton>
              <HuntToolbarButton size="sm" onClick={toggleSpeaker} title={speakerOn ? 'Stäng av ljud' : 'Slå på ljud'} active={speakerOn} amber={T.amber} ink={T.text}>
                <span style={{ fontSize: 15 }}>{speakerOn ? '🔊' : '🔈'}</span>
              </HuntToolbarButton>
              <HuntToolbarButton size="sm" onClick={() => { void toggleCam(); }} title={camOn ? 'Stäng av kamera' : 'Slå på kamera'} active={camOn} amber={T.amber} ink={T.text}>
                <span style={{ fontSize: 15 }}>📷</span>
              </HuntToolbarButton>
              <HuntToolbarButton size="sm" onClick={generateInviteQR} title="Dela jakt (QR / länk)" active={false} noStrike amber={T.amber} ink={T.text}>
                <IconShareArrow size={16} />
              </HuntToolbarButton>
              <HuntToolbarButton size="sm" onClick={() => setShowHuntCloseModal(true)} title="Avsluta jakt" active={false} noStrike amber={T.amber} ink={T.text}>
                <span style={{ fontWeight: 800, fontSize: 15 }}>✕</span>
              </HuntToolbarButton>
              {remoteParticipants.length > 0 && <span style={{ color: T.textSub, fontSize: 11 }}>{remoteParticipants.length + 1}</span>}
            </div>
          )}
        </>
      )}

      {/* Map container (+ Jakt-knapp på jaktmarkens karta-flik)
          I helskärm måste skalet vara flex:1 så Leaflet-höjd (100%/flex) inte kollapsar. */}
      <div style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        ...(fullscreen
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' as const }
          : isMarkDetail
            ? { display: 'grid', gridTemplateColumns: mob ? 'minmax(0,1fr)' : 'minmax(0,1fr) 320px', gap: mob ? 14 : 24, alignItems: 'start', overflow: 'visible' }
            : {}),
      }}>
      {/* Jakt-knapp i helskärm, svävande längst ner (i normalläge: Starta jakt-kortet efter kartan). UX3: frostad oval */}
      {isMarkDetail && token && !huntSession && fullscreen && (
        <div style={{
          position: 'fixed', left: 12, right: 12,
          bottom: 'calc(22px + env(safe-area-inset-bottom, 0px))',
          zIndex: 9996,
          display: 'flex', justifyContent: 'center', pointerEvents: 'none',
        }}
        >
          <HuntFloatingButton onClick={openJaktMenu} />
        </div>
      )}
      {showSatPanel && (
        <SatPanel map={mapRef.current} coordinatesJson={lastCoordinatesJsonRef.current} occupancy={satOcc} canEdit={!!canEditSatar} mobile={mob}
          onSave={async json => {
            const saved = await api.setMarkFastighet(mark.id, { coordinatesJson: json });
            lastCoordinatesJsonRef.current = saved.coordinatesJson ?? json;
            onSatarSaved?.(saved.coordinatesJson ?? json);
          }} onClose={() => setShowSatPanel(false)} />
      )}
      <div
        ref={mapDivRef}
        onClick={() => { if (mapDraggedRef.current) return; if (!fullscreen && token) setFullscreen(true); }}
        style={{
          width: '100%',
          height: fullscreen
            ? (leaderBroadcasting && leaderVideoTrack ? '65vh' : undefined)
            : markDetailCard ? (mob ? 320 : 460) : 520,
          flex: fullscreen && !(leaderBroadcasting && leaderVideoTrack) ? 1 : undefined,
          minHeight: fullscreen && !(leaderBroadcasting && leaderVideoTrack) ? 0 : undefined,
          borderRadius: fullscreen ? 0 : markDetailCard ? 16 : 6,
          border: markDetailCard ? `1px solid ${T.border}` : undefined,
          boxSizing: 'border-box',
          gridColumn: markDetailCard ? 1 : undefined, gridRow: markDetailCard ? 1 : undefined,
          overflow: 'hidden',
          background: T.bg,
          transformOrigin: 'center center',
          cursor: !fullscreen && token ? 'pointer' : undefined,
          // UX6: Leaflets lager (z 400–1000) ska stanna i kartan — annars ritas kartan över toppmenyn (z 800)
          // och markens klistrade rubrik (z 500) när den skrollas under dem, t.ex. efter att helskärmen stängts.
          isolation: fullscreen ? undefined : 'isolate',
        }}
      />
      {/* Synlig väg till helskärm (klick på kartan fungerar fortfarande) */}
      {markDetailCard && token && (
        <IconButton variant="float" round={false} label="Visa kartan i helskärm" onClick={() => setFullscreen(true)}
          style={{ gridColumn: 1, gridRow: 1, justifySelf: 'end', alignSelf: 'start', margin: 12, zIndex: 1, position: 'relative' }}>
          <Icon as={Maximize2} />
        </IconButton>
      )}
      {/* UX15: Min position även på den inbäddade kartan (under helskärmsknappen). */}
      {markDetailCard && (
        <IconButton variant="float" round={false} label={userPosition ? 'Min position' : locating ? 'Min position – söker …' : 'Min position – tryck för att hitta dig'} onClick={locateMe}
          style={{ gridColumn: 1, gridRow: 1, justifySelf: 'end', alignSelf: 'start', margin: token ? '64px 12px 12px' : 12, zIndex: 1, position: 'relative' }}>
          <LocateIcon />
        </IconButton>
      )}
      {markDetailCard && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {token && !huntSession && (
            <Card raised={!mob} padding={18}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 12 }}>{canOfferChatJoin ? 'Jakt pågår' : 'Jakt'}</div>
              <Button size="lg" block icon={<Icon as={Play} />} onClick={openJaktMenu}>
                {canOfferChatJoin ? 'Anslut till jakten' : 'Starta jakt'}
              </Button>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 10, lineHeight: 1.5 }}>
                Öppnar kartan i helskärm med radio, chatt och deltagarnas positioner.
              </div>
            </Card>
          )}
          {aside}
        </div>
      )}
      </div>

      {fullscreen && karta3dProps && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9991 }}>
          <Karta3D {...karta3dProps} />
        </div>
      )}

      {/* KA1: tryck på en deltagare på kartan */}
      {huntSession && statusFor && mapPositions[statusFor] && (
        <ParticipantStatusCard meetingId={huntSession.meetingId} participantKey={statusFor}
          name={mapPositions[statusFor].name} avatarUrl={mapPositions[statusFor].avatarUrl} onClose={() => setStatusFor(null)} />
      )}

      {/* Jakt-meny (UX3/UX4): frostad popup ovanför jaktknappen; först mark (om man har flera), sedan åtgärd */}
      {isMarkDetail && showJaktMenu && (() => {
        const close = () => setShowJaktMenu(false);
        const otherMark = huntMarkChoice && huntMarkChoice !== mark.id ? huntMarkChoice : null;
        const recent = (() => {
          try {
            const raw = safeLocalStorageGet(`qvenari_recent_hunt_${mark.id}`);
            if (!raw) return null;
            const saved = JSON.parse(raw) as { meetingId: string; title: string; startedAt: string };
            if (!saved?.meetingId || !saved?.startedAt) return null;
            return Date.now() - new Date(saved.startedAt).getTime() > 24 * 60 * 60 * 1000 ? null : saved;
          } catch { return null; }
        })();
        const join = async (meetingId: string) => {
          if (!onJoinOngoingHunt) return;
          setJaktMenuBusy(true);
          try { await onJoinOngoingHunt(meetingId); }
          finally { setJaktMenuBusy(false); close(); }
        };
        const actions: HuntSheetAction[] = [];
        let title = 'Jakt';
        let text: string | undefined;
        if (huntSession) {
          text = 'Du har redan en jakt igång. Öppna helskärm för att använda jaktläge och verktyg.';
          actions.push({ key: 'open', label: 'Öppna jakten', icon: HUNT_ICONS.open, primary: true, onClick: () => { setFullscreen(true); close(); } });
        } else if (canOfferChatJoin && !otherMark) {
          title = 'Jakt pågår';
          text = 'Det finns en pågående jakt för denna mark. Vill du ansluta till den?';
          actions.push({ key: 'join', label: jaktMenuBusy ? 'Ansluter...' : 'Anslut till jakten', icon: HUNT_ICONS.join, primary: true, onClick: () => { if (joinableHuntMeetingId) void join(joinableHuntMeetingId); } });
        } else {
          text = 'Starta en jakt med jaktlaget direkt, eller öppna såtplaneraren.';
          if (recent && !otherMark) actions.push({ key: 'rejoin', label: jaktMenuBusy ? 'Ansluter...' : 'Återanslut till jakt', hint: recent.title, icon: HUNT_ICONS.join, onClick: () => { void join(recent.meetingId); } });
          if (onStartHunt) actions.push({
            key: 'start', label: jaktMenuBusy ? 'Startar...' : 'Starta jakt', icon: HUNT_ICONS.play, primary: true, disabled: huntStarting,
            onClick: async () => {
              if (otherMark) { close(); onPickHuntMark?.(otherMark, 'start'); return; }
              setJaktMenuBusy(true);
              try { await onStartHunt(); }
              finally { setJaktMenuBusy(false); close(); }
            },
          });
          if (onPlanHunt) actions.push({
            key: 'plan', label: 'Planera jakt', hint: 'Såtplaneraren', icon: HUNT_ICONS.plan,
            onClick: () => { close(); if (otherMark) onPickHuntMark?.(otherMark, 'plan'); else { setFullscreen(false); onPlanHunt(); } },
          });
        }
        return (
          <HuntStartSheet title={title} text={text} actions={actions} onClose={close} busy={jaktMenuBusy}
            marks={!huntSession && huntMarkList.length > 1 ? huntMarkList : undefined}
            chosenMarkId={huntMarkChoice} onChooseMark={setHuntMarkChoice} />
        );
      })()}

      {/* UX14: hårkors och avståndslinje medan man drar (ovanför kartan, under knapparna). */}
      {centerDist && <CenterDistanceOverlay state={centerDist} zIndex={fullscreen ? 9992 : 1} color={T.amber} />}

      {/* Fullscreen overlay controls */}
      {fullscreen && (
        <>
          <input
            ref={gpxDrawerInputRef}
            type="file"
            accept=".gpx"
            style={{ display: 'none' }}
            onChange={(e) => { handleGpxImport(e); setShowMapSettings(false); e.target.value = ''; }}
            disabled={importing}
          />

          {/* Hamburgare + kompakt väder på samma rad (ingen ram/bakgrund på vädret) */}
          <div
            style={{
              position: 'fixed',
              left: 12,
              top: 'calc(2px + env(safe-area-inset-top, 0px))',
              zIndex: 9997,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              maxWidth: 'calc(100vw - 24px)',
            }}
          >
            {!showMapSettings && (
              <MapRoundButton title="Meny & inställningar" ariaExpanded={false} ariaControls="karta-map-settings-drawer" onClick={() => setShowMapSettings(true)}>
                <IconMenuLines size={22} />
              </MapRoundButton>
            )}
            {fullscreen && (showWeather || (showHunterHealth && (watchBpm != null || steps != null))) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            {showWeather && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '4px 6px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: T.amber,
                  lineHeight: 1.2,
                  textShadow: '0 0 5px #000, 0 1px 3px #000, 0 0 12px rgba(0,0,0,0.85)',
                  flexShrink: 1,
                  minWidth: 0,
                  fontFamily: 'inherit',
                }}
              >
                {weatherLoading && !weatherSnap && <span style={{ opacity: 0.9 }}>…</span>}
                {weatherSnap && (
                  <>
                    <span style={{ fontSize: 13, lineHeight: 1 }} aria-hidden title="Väder">{wmoWeatherEmoji(weatherSnap.code)}</span>
                    <span>{Math.round(weatherSnap.tempC)}°</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: T.amber }}>
                      <WindFlowArrow deg={weatherSnap.windDeg} size={11} />
                      <span style={{ fontWeight: 800, fontSize: 10 }}>{weatherSnap.windMs < 2 ? weatherSnap.windMs.toFixed(1) : Math.round(weatherSnap.windMs)} m/s</span>
                    </span>
                    <span style={{ opacity: 0.95, fontWeight: 800 }}>{weatherSnap.sunrise}</span>
                    <span style={{ fontSize: 12 }} aria-hidden>☀️</span>
                    <span style={{ opacity: 0.95, fontWeight: 800 }}>{weatherSnap.sunset}</span>
                  </>
                )}
                {!weatherLoading && !weatherSnap && <span style={{ fontSize: 10 }}>–</span>}
              </div>
            )}
            {showHunterHealth && (watchBpm != null || steps != null) && (
              <HunterHealthRow bpm={watchBpm} steps={steps} color={T.amber} />
            )}
            </div>
            )}
          </div>

          {showMapSettings && (
            <>
              <div
                role="presentation"
                onClick={() => setShowMapSettings(false)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 10000,
                  background: 'rgba(0,0,0,0.28)', // kartan syns till höger om menyn
                }}
              />
              <aside
                id="karta-map-settings-drawer"
                style={{
                  position: 'fixed',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 'min(80vw, 340px)',
                  maxWidth: '100vw',
                  zIndex: 10001,
                  // Samma frostade yta som jaktmenyn och kartknapparna.
                  background: `color-mix(in srgb, ${T.bg} 94%, transparent)`,
                  backdropFilter: 'blur(22px) saturate(1.6)',
                  WebkitBackdropFilter: 'blur(22px) saturate(1.6)',
                  borderRight: `1px solid ${T.border}`,
                  borderRadius: '0 22px 22px 0',
                  boxShadow: '8px 0 40px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  paddingTop: 'env(safe-area-inset-top, 0px)',
                  boxSizing: 'border-box',
                }}
              >
                {/* Användaren överst (som WeHunt) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 14px', borderBottom: `1px solid ${T.border}` }}>
                  {userAvatarUrl
                    ? <img src={userAvatarUrl} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 44, height: 44, borderRadius: '50%', background: T.amber, color: '#1b1f1b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 400, flexShrink: 0 }}>
                        {(userName || 'J').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: T.text, fontSize: 16, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName || 'Jägare'}</div>
                    <div style={{ color: T.textSub, fontSize: 12.5, fontWeight: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isJaktledare ? 'Jaktledare' : 'Jägare'} · {mark.name}
                    </div>
                  </div>
                  <MapRoundButton title="Stäng meny" size={38} onClick={() => setShowMapSettings(false)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </MapRoundButton>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', fontWeight: 400 }}>
                  <MenuSection title="Karta" open={menuSection === 'karta'} onToggle={() => toggleMenuSection('karta')} amber={T.amber}>
                    <SegmentedChoice
                      amber={T.amber}
                      value={mapType}
                      onChange={v => setMapType(v)}
                      options={[{ value: 'mapnik', label: 'Karta' }, { value: 'terrain', label: 'Terräng' }, { value: 'satellite', label: 'Satellit' }]}
                    />
                    <LightToggleRow label="Fastighetsgränser" checked={showFastighetsgranser} onChange={setShowFastighetsgranser} amber={T.amber} />
                    <LightToggleRow label="3D-terräng" checked={show3D && !lost3d} onChange={v => { setLost3d(false); setShow3D(v); }} amber={T.amber} hint={show3D && lost3d ? 'Stängdes: telefonens grafikminne tog slut' : show3D && !fullscreen ? 'Visas i helskärm' : undefined} />
                  </MenuSection>

                  {show3D && fullscreen && map3d && (
                    <MenuSection title="3D-lutning" open={menuSection === 'lutning'} onToggle={() => toggleMenuSection('lutning')} amber={T.amber}>
                      <PitchControl map={map3d} />
                    </MenuSection>
                  )}

                  <MenuSection title="Visa på kartan" open={menuSection === 'visa'} onToggle={() => toggleMenuSection('visa')} amber={T.amber}>
                    <LightToggleRow label="Avståndscirklar" checked={showDistanceRings} onChange={setShowDistanceRings} amber={T.amber} />
                    <LightToggleRow label="Vittring" checked={showVittring} onChange={setShowVittring} amber={T.amber}
                      hint={showVittring ? (vittringWindDir === null ? 'Hämtar vind…' : `Vind ${Math.round(vittringWindDir)}°${vittringWindSpd !== null ? ` · ${vittringWindSpd.toFixed(1)} m/s` : ''}`) : undefined} />
                    <LightToggleRow label="Väder" checked={showWeather} onChange={setShowWeather} amber={T.amber} />
                    <LightToggleRow label="Pass" checked={showPassPins} onChange={setShowPassPins} amber={T.amber} />
                    {showPassPins && <LightToggleRow label="Passnamn" checked={showPassPinLabels} onChange={setShowPassPinLabels} amber={T.amber} />}
                    <LightToggleRow label="Observationer" checked={showObservationPins} onChange={setShowObservationPins} amber={T.amber} />
                    <LightToggleRow label="Skjutet vilt" checked={showSkjutetViltPins} onChange={setShowSkjutetViltPins} amber={T.amber} />
                  </MenuSection>

                  <MenuSection title="Spår" open={menuSection === 'spar'} onToggle={() => toggleMenuSection('spar')} amber={T.amber}>
                    {(['mine', 'hunters', 'dogs'] as TrailKind[]).map(kind => {
                      const pref = trailPrefs[kind];
                      const setPref = (change: Partial<TrailPrefs[TrailKind]>) => setTrailPrefs(p => ({ ...p, [kind]: { ...p[kind], ...change } }));
                      const quick = TRAIL_QUICK[kind];
                      const isQuick = pref.minutes === 0 || quick.includes(pref.minutes);
                      return (
                        <SubRow amber={T.amber}
                          key={kind}
                          title={kind === 'mine' ? 'Mitt spår' : kind === 'hunters' ? 'Jägare' : 'Hundar'}
                          summary={<><span>{trailLabel(pref.minutes)}</span><span style={{ width: 10, height: 10, borderRadius: '50%', background: pref.color, display: 'inline-block' }} /></>}
                          open={trailOpen === kind}
                          onToggle={() => { setTrailOpen(o => (o === kind ? null : kind)); setTrailMore(null); }}
                        >
                          <SegmentedChoice
                            amber={T.amber}
                            value={trailMore === kind ? -1 : isQuick ? pref.minutes : -1}
                            onChange={v => { if (v === -1) setTrailMore(m => (m === kind ? null : kind)); else { setPref({ minutes: v }); setTrailMore(null); } }}
                            options={[{ value: 0, label: 'Av' }, ...quick.map(m => ({ value: m, label: trailLabel(m) })), { value: -1, label: isQuick ? '…' : trailLabel(pref.minutes) }]}
                          />
                          {trailMore === kind && (
                            <SegmentedChoice
                              amber={T.amber}
                              value={pref.minutes}
                              onChange={v => { setPref({ minutes: v }); setTrailMore(null); }}
                              options={TRAIL_MORE.map(m => ({ value: m, label: trailLabel(m) }))}
                            />
                          )}
                          <ColorDots value={pref.color} onChange={c => setPref({ color: c })} />
                          {kind !== 'mine' && !huntSession && pref.minutes > 0 && (
                            <div style={{ color: T.textSub, fontSize: 11.5, marginTop: 6 }}>{kind === 'dogs' ? 'Visas under pågående jakt. PAJ GPS-hundar visas alltid.' : 'Visas under pågående jakt.'}</div>
                          )}
                        </SubRow>
                      );
                    })}
                  </MenuSection>

                  <MenuSection title="Hundar" open={menuSection === 'hundar'} onToggle={() => toggleMenuSection('hundar')} amber={T.amber}>
                    {menuDogs.length === 0 && (
                      <div style={{ color: T.textSub, fontSize: 13, padding: '4px 0 2px' }}>Lägg till din hund i profilen för att koppla en Apple Watch, Garmin eller PAJ GPS.</div>
                    )}
                    {menuDogs.map(d => (
                      <SubRow amber={T.amber}
                        key={d.key}
                        title={<>{d.name}{d.barking && <span style={{ color: '#ff6b5f', fontSize: 12 }}> · skäller</span>}</>}
                        summary={d.battery != null ? <span>{Math.round(d.battery * 100)} %</span> : undefined}
                        open={dogOpen === d.key}
                        onToggle={() => setDogOpen(o => (o === d.key ? null : d.key))}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, color: T.textSub }}><span>Enhet</span><span style={{ color: T.text }}>{d.device}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, color: T.textSub }}><span>Batteri</span><span style={{ color: T.text }}>{d.battery != null ? `${Math.round(d.battery * 100)} %` : '–'}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, color: T.textSub }}><span>Senaste position</span><span style={{ color: T.text }}>{d.updatedAt ? agoLabel(d.updatedAt) : 'Inte i jakt'}</span></div>
                        {d.watch ? (
                          <>
                            <LightToggleRow label="Energispar" amber={T.amber} checked={d.watch.powerMode === 'saver'}
                              hint={d.watch.powerMode === 'saver' ? 'Position var 30:e sekund' : 'Position var 10:e sekund'}
                              onChange={v => changeDogWatch(d.watch!.dogId, { powerMode: v ? 'saver' : 'hunt' })} />
                            <LightToggleRow label="Skallavkänning" amber={T.amber} checked={d.watch.mic}
                              hint={d.watch.mic ? 'Mikrofonen lyssnar efter skall' : 'Mikrofonen är av'}
                              onChange={v => changeDogWatch(d.watch!.dogId, { mic: v })} />
                          </>
                        ) : (
                          <div style={{ color: T.textSub, fontSize: 12, marginTop: 4 }}>Inställningar görs i tillverkarens app.</div>
                        )}
                      </SubRow>
                    ))}
                  </MenuSection>

                  <MenuSection title="Jägarhälsa" open={menuSection === 'halsa'} onToggle={() => toggleMenuSection('halsa')} amber={T.amber}>
                    <LightToggleRow label="Visa puls och steg" checked={showHunterHealth} onChange={setShowHunterHealth} amber={T.amber}
                      hint="Under vädret högst upp. Puls från Apple Watch, steg från Hälsa." />
                  </MenuSection>

                  <MenuSection title="Såter" open={menuSection === 'sater'} onToggle={() => toggleMenuSection('sater')} amber={T.amber}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingTop: 10 }}>
                      <MenuActionButton onClick={() => { setShowSatPanel(true); setShowMapSettings(false); }}>{canEditSatar ? 'Rita och ändra såter' : 'Visa såter och dagens jakter'}</MenuActionButton>
                    </div>
                  </MenuSection>

                  {isAdmin && (
                    <MenuSection title="Ändra kartan" open={menuSection === 'andra'} onToggle={() => toggleMenuSection('andra')} amber={T.amber}>
<div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingTop: 10 }}>
                          <MenuActionButton onClick={() => { setPlacingPinType(p => (p === 'pass' ? null : 'pass')); setShowMapSettings(false); }}>Lägg till pass</MenuActionButton>
                          <MenuActionButton onClick={() => { setPlacingPinType(p => (p === 'atel' ? null : 'atel')); setShowMapSettings(false); }}>Lägg till åtel</MenuActionButton>
                          <MenuActionButton onClick={() => { setPlacingPinType(p => (p === 'kamera' ? null : 'kamera')); setShowMapSettings(false); }}>Lägg till åtelkamera</MenuActionButton>
                          <MenuActionButton onClick={() => { setPlacingPinType(p => (p === 'ovrigt' ? null : 'ovrigt')); setShowMapSettings(false); }}>Förbjudet område</MenuActionButton>
                          <MenuActionButton onClick={() => { setPlacingPinType(p => (p === 'observation' ? null : 'observation')); setShowMapSettings(false); }}>Lägg till pin (observation)</MenuActionButton>
                          <MenuActionButton onClick={() => { gpxDrawerInputRef.current?.click(); }} disabled={importing} busy={importing}>{importing ? 'Importerar WeHunt GPX…' : 'Ladda upp WeHunt GPX'}</MenuActionButton>
                          <MenuActionButton onClick={() => { startDrawPolygon(); setShowMapSettings(false); }} disabled={drawingPolygon} busy={drawingPolygon}>{drawingPolygon ? 'Ritar gräns…' : 'Ändra gränser (rita polygon)'}</MenuActionButton>
                        </div>
                    </MenuSection>
                  )}
                </div>
              </aside>
            </>
          )}

          {/* Höger: karttyp (ej jaktmark) + kompass + stäng helskärm */}
          <div style={{
            position: 'fixed', top: 'calc(4px + env(safe-area-inset-top, 0px))', right: 12, zIndex: 9995,
            display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'flex-end', maxWidth: 'calc(100vw - 88px)',
          }}>
            {!isMarkDetail && (
            <div style={{ ...huntBarSurface(T.bg, T.border), display: 'flex', gap: 2, padding: 3, flexShrink: 0 }}>
              {BASE_MAP_TYPES.map(t => {
                const on = mapType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setMapType(t); setShowMapSettings(false); }}
                    title={t === 'mapnik' ? 'Vägkarta' : t === 'satellite' ? 'Satellit' : 'Terrängkarta'}
                    aria-pressed={on}
                    style={{
                      padding: '8px 11px', fontSize: 12, fontWeight: on ? 700 : 500, cursor: 'pointer', borderRadius: 999,
                      border: `1.5px solid ${on ? T.amber : 'transparent'}`,
                      background: on ? `color-mix(in srgb, ${T.amber} 14%, transparent)` : 'transparent',
                      color: on ? T.amber : T.text,
                      whiteSpace: 'nowrap', fontFamily: 'inherit',
                    }}
                  >
                    {t === 'mapnik' ? 'Karta' : t === 'satellite' ? 'Satellit' : 'Terräng'}
                  </button>
                );
              })}
            </div>
            )}

            {/* UX15: Min position (cirkel med punkt) – egen knapp bredvid kompassen. */}
            <MapRoundButton onClick={locateMe}
              title={userPosition ? 'Min position' : locating ? 'Min position – söker …' : 'Min position – tryck för att hitta dig'}>
              <LocateIcon size={24} dim={!userPosition} />
            </MapRoundButton>

            {/* Kompassrosen är knappen för kompassläget: visar norr, tryck slår på/av (orange när på). */}
            <MapRoundButton onClick={toggleCompass} title={compassActive ? 'Stäng kompassläge' : 'Kompassläge'} active={compassActive}>
              <div style={{ transform: compassActive && heading !== null ? `rotate(${heading}deg)` : undefined, transition: 'transform 0.3s ease-out', width: 34, height: 34 }}>
                <svg width="34" height="34" viewBox="0 0 36 36" aria-hidden>
                  <text x="18" y="9" textAnchor="middle" fontSize="8" fontWeight="800" fill="#E53935" fontFamily="system-ui, sans-serif">N</text>
                  <polygon points="18,10 21,18 15,18" fill="#E53935"/>
                  <polygon points="18,30 15,18 21,18" fill="currentColor" opacity="0.75"/>
                  <circle cx="18" cy="18" r="1.6" fill={T.bg} stroke="currentColor" strokeWidth="0.8"/>
                </svg>
              </div>
            </MapRoundButton>

            <MapRoundButton title="Stäng helskärm" onClick={() => { setFullscreen(false); setShowMapSettings(false); if (compassActive) toggleCompass(); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </MapRoundButton>
          </div>

          {/* Navigation active banner */}
          {navigatingTo && (
            <div style={{
              position: 'fixed', bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))', left: '50%', transform: 'translateX(-50%)', zIndex: 9996,
              background: T.surface,
              border: `1px solid ${pinConfig(navigatingTo.type).color}`,
              borderRadius: 6, padding: '10px 20px',
              display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: pinConfig(navigatingTo.type).color, flexShrink: 0 }} />
              <span style={{ color: T.text, fontWeight: 600, fontSize: 13 }}>
                Navigerar: {navigatingTo.name || pinConfig(navigatingTo.type).label}
                {userPosition && (
                  <span style={{ color: pinConfig(navigatingTo.type).color, marginLeft: 8 }}>
                    {formatDistance(haversine(userPosition, navigatingTo))}
                  </span>
                )}
              </span>
              <button onClick={clearNavLine} style={{
                background: 'transparent', border: `1px solid ${T.border}`,
                color: T.textSub, borderRadius: 3, padding: '3px 10px',
                cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              }}>
                Avsluta
              </button>
            </div>
          )}

          {/* GPS status indicator */}
          {gpsStatus === 'pending' && !userPosition && (
            <div style={{
              position: 'fixed', top: 'calc(16px + env(safe-area-inset-top, 0px))', left: '50%', transform: 'translateX(-50%)', zIndex: 9995,
              background: T.surface,
              color: T.amber, padding: '8px 20px', borderRadius: 5,
              fontSize: 13, fontWeight: 600, border: `1px solid ${T.border}`,
            }}>
              Söker GPS-position...
            </div>
          )}
          {(gpsStatus === 'denied' || gpsStatus === 'unavailable') && !userPosition && (
            <div style={{
              position: 'fixed', top: 'calc(16px + env(safe-area-inset-top, 0px))', left: '50%', transform: 'translateX(-50%)', zIndex: 9995,
              background: T.surface,
              color: T.amber, padding: '8px 20px', borderRadius: 5,
              fontSize: 13, fontWeight: 600, border: `1px solid ${T.border}`,
            }}>
              {gpsStatus === 'denied' ? 'GPS nekad — tillåt platstjänster i inställningarna' : 'GPS ej tillgänglig — klicka på kartan'}
            </div>
          )}

          {/* Placing pin indicator */}
          {placingPinType && (
            <div style={{
              position: 'fixed', top: 'calc(16px + env(safe-area-inset-top, 0px))', left: '50%', transform: 'translateX(-50%)', zIndex: 9995,
              background: PIN_CONFIG[placingPinType].color, color: 'white',
              padding: '8px 20px', borderRadius: 5, fontSize: 13, fontWeight: 600,
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            }}>
              Klicka på kartan: {PIN_CONFIG[placingPinType].label}
            </div>
          )}

          {/* ── HUNT CONTROLS ON MAP ── */}
          {/* Snabbstarta jakt — döljs på jaktmarkens karta (använd Jakt-knappen i infälld vy) */}
          {!isMarkDetail && !huntSession && onStartHunt && (
            <button
              onClick={async () => { setHuntStarting(true); try { await onStartHunt(); } finally { setHuntStarting(false); } }}
              disabled={huntStarting}
              style={{
                position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))', left: '50%', transform: 'translateX(-50%)', zIndex: 9996,
                padding: '12px 28px', borderRadius: 8, border: `2px solid ${T.amber}`,
                background: T.amber, color: '#0a0a0a', fontWeight: 700, fontSize: 15,
                cursor: huntStarting ? 'wait' : 'pointer',
                boxShadow: '0 4px 20px rgba(238,143,9,0.35)',
                display: 'flex', alignItems: 'center', gap: 10,
                opacity: huntStarting ? 0.6 : 1, transition: 'opacity 0.2s',
                fontFamily: 'inherit',
              }}
            >
              {huntStarting ? (
                <><span style={{ width: 14, height: 14, border: '2px solid rgba(10,10,10,0.35)', borderTopColor: '#0a0a0a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Startar...</>
              ) : (
                <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none"/></svg> Snabbstarta jakt</>
              )}
            </button>
          )}

          {/* Active hunt: mic / speaker / cam — på jaktmarkens karta i ett bottenkort som kan dras ner */}
          {huntSession && (
            <HuntSheet enabled={isMarkDetail} bg={T.bg} border={T.border}>
            <div style={isMarkDetail ? {
              width: '100%', boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
            } : {
              // Flytande oval: halvgenomskinlig, frostad bakgrund från temat (förebild: Blockets bottenmeny).
              position: 'fixed', bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))', left: '50%', transform: 'translateX(-50%)', zIndex: 9996,
              maxWidth: 'calc(100vw - 20px)', boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', gap: 2,
              padding: '6px 10px',
              ...huntBarSurface(T.bg, T.border),
            }}>
              {qUser && (
                <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={() => { setShowQ(p => !p); setShowHuntChat(false); }} title="Q — det Q hört under jakten, fråga eller be om något" active={showQ} noStrike amber={T.amber} ink={T.text}>
                  <img src="/qvenari-logo.png" alt="Q" style={{ height: 22, width: 'auto', objectFit: 'contain' }} />
                  {huntQ.unheard > 0 && <span data-q-unheard style={{ position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, borderRadius: 8, background: '#E53935', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{huntQ.unheard}</span>}
                </HuntToolbarButton>
              )}
              <div title={lkConnected ? 'Radion är på' : radioOn ? (lkError || 'Ansluter') : 'Radion startar när någon trycker på walkie-talkien eller kameran'} style={{ width: 8, height: 8, borderRadius: '50%', background: lkConnected ? '#44ff44' : radioOn ? '#ffaa00' : T.border, animation: radioOn ? 'pulse 1.5s infinite' : undefined, flexShrink: 0 }} />
              

              <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={toggleMic} title={micOn ? 'Walkie-talkie: sluta sända' : walkieTitle} active={micOn || !!voiceRec} tall={micOn} amber={T.amber} ink={T.text}
                onHoldStart={canSendHuntMedia ? () => { void startVoiceMessage(true); } : undefined} onHoldEnd={() => { void finishVoiceMessage(true); }}>
                <WalkieTalkieIcon size={19} strokeOpacity={micOn && micLevel > 8 ? 1 : 0.78} />
                {micOn && (
                  <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 10 }}>
                    {[0.15, 0.35, 0.60, 0.80, 1.0].map((thresh, i) => (
                      <div key={i} style={{
                        width: 3,
                        height: `${Math.round(thresh * 10)}px`,
                        background: 'currentColor', opacity: (micLevel / 100) >= thresh ? 1 : 0.3,
                        borderRadius: 1,
                        transition: 'opacity 0.08s',
                      }} />
                    ))}
                  </div>
                )}
              </HuntToolbarButton>

              <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={toggleSpeaker} title={speakerOn ? 'Stäng av ljud' : 'Slå på ljud'} active={speakerOn} amber={T.amber} ink={T.text}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" fill="currentColor" />
                  {speakerOn ? (<><path d="M15.54 8.46a5 5 0 010 7.07" /><path d="M19.07 4.93a10 10 0 010 14.14" /></>) : null}
                </svg>
              </HuntToolbarButton>

              <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={() => setShowCamPicker(p => !p)} title="Kamera: välj bakre, selfie, glasögon eller drönare" active={camOn || glassesOn || showCamPicker} noStrike amber={T.amber} ink={T.text}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="2" y="5" width="14" height="14" rx="2" />
                  <polygon points="23,7 16,12 23,17" />
                </svg>
              </HuntToolbarButton>


              <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={generateInviteQR} title="Dela jakt (QR / länk)" active={false} noStrike amber={T.amber} ink={T.text}>
                <IconShareArrow size={20} />
              </HuntToolbarButton>

              <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={() => { setShowHuntChat(p => !p); setShowQ(false); }} title="Jaktchatt — meddelanden och bilder" active={showHuntChat} noStrike amber={T.amber} ink={T.text}>
                <IconChatBubble size={20} />
              </HuntToolbarButton>

              <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={() => setShowSkjutmenyModal(true)} title="Skjutmeny — vilt, priser och mål" active={showSkjutmenyModal} noStrike amber={T.amber} ink={T.text}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="3" />
                  <line x1="12" y1="2" x2="12" y2="5" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="2" y1="12" x2="5" y2="12" />
                  <line x1="19" y1="12" x2="22" y2="12" />
                </svg>
              </HuntToolbarButton>

              {!isMarkDetail && <div style={{ width: 1, height: 24, background: T.border, flexShrink: 0 }} />}

              <HuntToolbarButton size={isMarkDetail ? 'lg' : 'md'} onClick={() => setShowHuntCloseModal(true)} title="Avsluta jakt" active={false} noStrike amber={T.amber} ink={T.text}>
                <span style={{ fontWeight: 800, fontSize: 17 }}>✕</span>
              </HuntToolbarButton>

              {remoteParticipants.length > 0 && (
                <div style={{ color: T.textSub, fontSize: 11, fontWeight: 600, marginLeft: 2, flexShrink: 0 }}>
                  {remoteParticipants.length + 1}
                </div>
              )}
            </div>
            </HuntSheet>
          )}

          {videoFull && <VideoFullscreen stream={videoFull.stream} mirror={videoFull.mirror} onClose={closeVideoFull} />}

          {/* Kameraväljare ovanför jaktmenyn: bakre, selfie, Meta-glasögon (bara glasögonbygget), drönare (kommer) */}
          {huntSession && (
            <CameraSourcePicker open={showCamPicker} onClose={() => setShowCamPicker(false)} active={camSource}
              glassesAvailable={glassesAvailable} onSelect={src => { void selectCamSource(src); }} onStop={() => { void stopCamSource(); }}
              bottom={isMarkDetail ? 'calc(104px + env(safe-area-inset-bottom, 0px))' : 'calc(90px + env(safe-area-inset-bottom, 0px))'}
              onRecordVideo={canSendHuntMedia ? () => { void openVideoRecorder(); } : undefined} />
          )}

          {/* I2: röstmeddelande spelas in (håll in walkie-talkien eller + i jaktchatten) */}
          {huntSession && voiceRec && (
            <div role="status" style={{
              ...huntBarSurface(T.bg, T.border), position: 'fixed', left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
              bottom: isMarkDetail ? 'calc(104px + env(safe-area-inset-bottom, 0px))' : 'calc(90px + env(safe-area-inset-bottom, 0px))',
              width: 'min(340px, calc(100vw - 20px))', boxSizing: 'border-box', borderRadius: 26, padding: '8px 10px 8px 16px',
              display: 'flex', alignItems: 'center', gap: 10, color: T.text,
            }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.2s infinite', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                Röstmeddelande {Math.max(0, Math.floor((voiceRecNow - voiceRec.startedAt) / 1000))} s
                {voiceRec.hold && <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: T.textSub }}>Släpp för att skicka till jaktchatten</span>}
              </span>
              {!voiceRec.hold && (
                <>
                  <button type="button" onClick={() => { void finishVoiceMessage(false); }} style={{ minHeight: 36, padding: '0 12px', borderRadius: 999, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Avbryt</button>
                  <button type="button" onClick={() => { void finishVoiceMessage(true); }} style={{ minHeight: 36, padding: '0 14px', borderRadius: 999, border: 'none', background: T.amber, color: '#0a0a0a', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>Skicka</button>
                </>
              )}
            </div>
          )}
          {huntSession && mediaNotice && !voiceRec && (
            <div role="status" style={{
              ...huntBarSurface(T.bg, T.border), position: 'fixed', left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
              bottom: isMarkDetail ? 'calc(104px + env(safe-area-inset-bottom, 0px))' : 'calc(90px + env(safe-area-inset-bottom, 0px))',
              maxWidth: 'calc(100vw - 20px)', boxSizing: 'border-box', borderRadius: 20, padding: '8px 14px', fontSize: 13, color: T.text,
            }}>{mediaNotice}</div>
          )}
          {huntSession && showVideoRec && (
            <HuntVideoRecorder onClose={closeVideoRecorder} onSend={(blob, ms) => { closeVideoRecorder(); void postHuntChatMedia('video', blob, ms); }} />
          )}

          {/* Q under jakt: ett litet chattkort ovanför bottenmenyn (samma samtal som toppradens Q) */}
          {huntSession && qUser && (
            <QPanel open={showQ} onClose={() => setShowQ(false)} listenRequest={qListen} askRequest={qAsk} user={qUser} home={Q_CONVERSATION} variant="mini" onVoiceActivity={onQVoiceActivity} getContext={qContext} onLook={qLook}
              huntTab={{
                label: 'Jakt',
                badge: huntQ.unheard,
                content: (
                  <QHuntLog heard={transcript} voice={huntQ.voice} listening={transcribingRef.current} summary={huntQ.summary} onPlayVoice={v => void huntQ.playVoice(v)} shotLabel={SHOT_EVENT_TEXT}
                    insights={huntChatRows.filter((r): r is Extract<HuntChatRow, { kind: 'assistant' }> => r.kind === 'assistant').map(row => ({ id: row.id, ts: row.ts, node: (
                    <div key={row.id} style={{ alignSelf: 'stretch', borderRadius: 10, padding: '8px 10px', background: 'rgba(80,120,200,0.15)', border: '1px solid rgba(120,160,255,0.35)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 900, color: '#a8c4ff', letterSpacing: 0.06 }}>Q</span>
                        <span style={{ color: 'rgba(200,220,255,0.85)', fontSize: 10, fontWeight: 600 }}>Assistent</span>
                      </div>
                      <div style={{ color: T.text, fontSize: 12, lineHeight: 1.45 }}>{row.text}</div>
                      <div style={{ color: 'rgba(200,220,255,0.65)', fontSize: 10, marginTop: 6 }}>
                        Uppfattat från: <b style={{ fontWeight: 700 }}>{row.inferredFrom}</b>
                        {row.lat != null && row.lng != null && (
                          <span style={{ color: T.textMuted, fontWeight: 500, marginLeft: 6 }}>({row.lat.toFixed(4)}°N {row.lng.toFixed(4)}°Ö)</span>
                        )}
                      </div>
                      {!row.savedToMark ? (
                        row.subcats && row.subcats.length > 0 ? (
                          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {row.subcats.map(subcat => (
                              <button
                                key={subcat}
                                type="button"
                                disabled={!!huntChatSavingId}
                                onClick={async () => {
                                  setHuntChatSavingId(row.id);
                                  const lat = row.lat ?? userPositionRef.current?.lat ?? (mark.mapLat ?? undefined);
                                  const lng = row.lng ?? userPositionRef.current?.lng ?? (mark.mapLng ?? undefined);
                                  const now2 = new Date();
                                  const season = now2.getMonth() >= 6 ? `${now2.getFullYear()}/${now2.getFullYear()+1}` : `${now2.getFullYear()-1}/${now2.getFullYear()}`;
                                  const familyName = subcat.split(' — ')[0];
                                  const typ = parseHeardGame(subcat.replace(' — ', ' '));
                                  try {
                                    let newReg: import('./api').MarkViltRegistration;
                                    if (lat != null && lng != null) {
                                      const pin = await api.createFalltViltPin({ markId: mark.id, lat, lng, viltslag: familyName, name: subcat, kon: typ?.kon, alder: typ?.alder });
                                      setPins(prev => [...prev, pin]);
                                      newReg = await api.addViltRegistration({ markId: mark.id, season, gameType: familyName, falltViltPinId: pin.id, shotDate: now2.toISOString().slice(0, 10), sex: typ?.kon, ageClass: typ?.alder });
                                    } else {
                                      newReg = await api.addViltRegistration({ markId: mark.id, season, gameType: familyName, shotDate: now2.toISOString().slice(0, 10), sex: typ?.kon, ageClass: typ?.alder });
                                    }
                                    // Notify App.tsx so viltkött list is updated immediately
                                    onViltRegistered?.(newReg);
                                    // Track confirmed kill for hunt summary
                                    confirmedKillsRef.current = [...confirmedKillsRef.current, { subcat, lat: lat ?? undefined, lng: lng ?? undefined, ts: now2.getTime() }];
                                    // Update shooting plan actualCount for this subcat
                                    try {
                                      const planRows = await api.markShootingPlan(mark.id);
                                      const fk = planSpeciesFamilyKey(subcat);
                                      const planRow = planRows.find((p: MarkShootingPlanItem) => p.gameType === subcat) || planRows.find((p: MarkShootingPlanItem) => planSpeciesFamilyKey(p.gameType) === fk);
                                      if (planRow) {
                                        await api.upsertMarkShootingPlan({ markId: mark.id, season: planRow.season, gameType: planRow.gameType, plannedCount: planRow.plannedCount, actualCount: (planRow.actualCount || 0) + 1, costPerAnimal: planRow.costPerAnimal, notes: `Uppdaterad från jaktchatt (+1 ${subcat})` });
                                        // Refresh plan state so avskjutningsplan shows updated actualCount
                                        setSkjutmenyTick(t => t + 1);
                                      } else {
                                        console.warn('[plan] No matching plan row found for subcat:', subcat, 'fk:', fk, 'available:', planRows.map((p: MarkShootingPlanItem) => p.gameType));
                                      }
                                    } catch (planErr) { console.error('[plan] update failed:', planErr); }
                                    setHuntChatRows(prev => prev.map(r => r.kind === 'assistant' && r.id === row.id ? { ...r, savedToMark: true, text: `✓ ${subcat} registrerad` } : r));
                                  } catch { alert('Kunde inte spara.'); }
                                  finally { setHuntChatSavingId(null); }
                                }}
                                style={{
                                  padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.amber}`,
                                  background: 'rgba(238,143,9,0.15)', color: T.amber, fontSize: 11, fontWeight: 700,
                                  cursor: huntChatSavingId ? 'wait' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                                }}
                              >
                                {subcat.split(' — ')[1] || subcat}
                              </button>
                            ))}
                            <button
                              type="button"
                              disabled={!!huntChatSavingId}
                              onClick={() => setHuntChatRows(prev => prev.filter(r => r.id !== row.id))}
                              style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' as const }}
                            >Avfärda</button>
                          </div>
                        ) : (
                          <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              disabled={!!huntChatSavingId}
                              onClick={() => { void confirmAndSaveChatInsight(row); }}
                              style={{
                                flex: 1, padding: '7px 10px', borderRadius: 8, border: `1px solid ${T.amber}`,
                                background: 'rgba(238,143,9,0.2)', color: T.amber, fontSize: 11, fontWeight: 700, cursor: huntChatSavingId ? 'wait' : 'pointer', fontFamily: 'inherit',
                              }}
                            >
                              {huntChatSavingId === row.id ? 'Sparar…' : 'Bekräfta och spara'}
                            </button>
                            <button
                              type="button"
                              disabled={!!huntChatSavingId}
                              onClick={() => setHuntChatRows(prev => prev.filter(r => r.id !== row.id))}
                              style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' as const }}
                            >Avfärda</button>
                          </div>
                        )
                      ) : (
                        <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(120,220,160,0.9)', fontWeight: 600 }}>✓ Sparat till jakt och mark</div>
                      )}
                    </div>
                    ) }))} />
                ),
              }}
              miniBottom={huntChatBottomInset ? huntChatBottomInset + 8 : isMarkDetail ? 'calc(104px + env(safe-area-inset-bottom, 0px))' : 'calc(90px + env(safe-area-inset-bottom, 0px))'} />
          )}

          {/* Jaktchatt: meddelanden och bilder (Q:s slutsatser finns i Q-fliken, QJ2) */}
          {huntSession && showHuntChat && (
            <div style={{
              position: 'fixed',
              // Ovanför bottenkortet och tangentbordet (mäts), annars som förut.
              bottom: huntChatBottomInset
                ? huntChatBottomInset + 8
                : isMarkDetail
                  ? 'calc(104px + env(safe-area-inset-bottom, 0px))'
                  : 'calc(90px + env(safe-area-inset-bottom, 0px))',
              right: 12, zIndex: 9995,
              width: 'min(380px, calc(100vw - 24px))',
              maxHeight: 'min(52vh, 440px)', borderRadius: 12,
              background: T.surface,
              color: T.text,
              border: `1px solid ${T.border}`, boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
            >
              <div style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconChatBubble size={18} />
                  <span style={{ color: T.amber, fontSize: 13, fontWeight: 800 }}>Jaktchatt</span>
                </div>
                <button type="button" onClick={() => setShowHuntChat(false)} aria-label="Stäng" title="Stäng" style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: T.textSub, borderRadius: 15, padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
                </button>
              </div>
              <div style={{ padding: '6px 12px', borderBottom: `1px solid ${T.border}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                  <button type="button" onClick={() => { setTranscribeLang('sv'); transcribeLangRef.current = 'sv'; }} style={{ padding: '4px 10px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', background: transcribeLang === 'sv' ? T.amber : appTheme.amberDim, color: transcribeLang === 'sv' ? '#000' : T.textSub }}>SV</button>
                  <button type="button" onClick={() => { setTranscribeLang('en'); transcribeLangRef.current = 'en'; }} style={{ padding: '4px 10px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', background: transcribeLang === 'en' ? T.amber : appTheme.amberDim, color: transcribeLang === 'en' ? '#000' : T.textSub }}>EN</button>
                </div>
                <button type="button" onClick={() => { const next = !chatTtsEnabled; setChatTtsEnabled(next); if (next && window.speechSynthesis) { const utt = new SpeechSynthesisUtterance('Röstuppläsning aktiverad'); window.speechSynthesis.cancel(); window.speechSynthesis.speak(utt); } }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontFamily: 'inherit' }}>
                  <span style={{ display: 'inline-block', width: 28, height: 16, borderRadius: 8, background: chatTtsEnabled ? '#61D836' : T.border, position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                    <span style={{ position: 'absolute', top: 2, left: chatTtsEnabled ? 13 : 2, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }}/>
                  </span>
                  <span style={{ fontSize: 11, color: chatTtsEnabled ? '#61D836' : T.textMuted, fontWeight: chatTtsEnabled ? 700 : 400 }}>Läs upp meddelanden</span>
                </button>
              </div>
              <input ref={huntChatFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleHuntChatImagePick} />
              <div ref={huntChatScrollRef} style={{ flex: 1, minHeight: 120, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {!huntChatRows.some(r => r.kind === 'user') && (
                  <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.45 }}>
                    Skriv till jaktlaget här. Det Q hör under jakten (skott, drev, observationer) finns i Q-fliken.
                  </div>
                )}
                {huntChatRows.map(row => (
                  row.kind === 'user' ? (
                    <div key={row.id} style={{ alignSelf: 'flex-start', maxWidth: '92%', borderRadius: 10, padding: '8px 10px', background: 'rgba(238,143,9,0.12)', border: '1px solid rgba(238,143,9,0.35)' }}>
                      <div style={{ color: T.amber, fontSize: 10, fontWeight: 800, marginBottom: 4 }}>
                        {row.senderName}
                        {row.lat != null && row.lng != null && row.text && (killPat.test(row.text) || obsPat.test(row.text)) && (
                          <span style={{ color: T.textMuted, fontWeight: 500, marginLeft: 6 }}>({row.lat.toFixed(4)}°N {row.lng.toFixed(4)}°Ö)</span>
                        )}
                      </div>
                      {row.text && <div style={{ color: T.text, fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{row.text}</div>}
                      {row.imageUrl && (
                        <button type="button" onClick={() => window.open(resolveUrl(row.imageUrl!), '_blank', 'noopener,noreferrer')} style={{ marginTop: 6, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}>
                          <img src={resolveUrl(row.imageUrl)} alt="" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, display: 'block' }} />
                        </button>
                      )}
                      {row.audioUrl && (
                        <audio controls preload="none" src={row.audioUrl.startsWith('blob:') ? row.audioUrl : resolveUrl(row.audioUrl)} aria-label={`Röstmeddelande från ${row.senderName}`} style={{ marginTop: 6, width: 240, maxWidth: '100%', height: 36, display: 'block' }} />
                      )}
                      {row.videoUrl && (
                        <video controls playsInline preload="metadata" src={row.videoUrl.startsWith('blob:') ? row.videoUrl : resolveUrl(row.videoUrl)} style={{ marginTop: 6, maxWidth: '100%', maxHeight: 220, borderRadius: 8, display: 'block', background: '#000' }} />
                      )}
                      {row.pending && <div style={{ color: T.textMuted, fontSize: 10, marginTop: 4 }}>Skickar …</div>}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 4 }}>
                        <span style={{ color: T.textMuted, fontSize: 9 }}>{new Date(row.ts).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</span>
                        {/* AS1c: rapportera/blockera andras meddelanden i jaktchatten. */}
                        {!row.mine && huntSession?.meetingId && (
                          <ReportMenu target={{ type: 'hunt_chat', id: row.id, meetingId: huntSession.meetingId }} ownerName={row.senderName} noun="meddelandet" up
                            onBlocked={() => {
                              blockedNamesRef.current.add(row.senderName.trim().toLowerCase());
                              setHuntChatRows(prev => prev.filter(r => r.kind !== 'user' || r.senderName !== row.senderName));
                            }} />
                        )}
                      </div>
                    </div>
                  ) : null // QJ2: Q:s slutsatser visas i Q-fliken
                ))}
              </div>
              <div style={{ padding: '8px 12px calc(10px + env(safe-area-inset-bottom, 0px))', borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* Samma skrivfält som gruppchatten (C1). Diktering är av här: jaktens radio använder redan mikrofonen. */}
                <ChatComposer
                  value={huntChatDraft} onChange={setHuntChatDraft} onSend={sendHuntChatMessage} dictation={false}
                  attachments={[
                    { key: 'bild', label: 'Skicka bild', onSelect: () => huntChatFileInputRef.current?.click() },
                    ...(canSendHuntMedia ? [
                      { key: 'rost', label: 'Spela in röstmeddelande', onSelect: () => { void startVoiceMessage(false); } },
                      { key: 'video', label: 'Spela in video', onSelect: () => { void openVideoRecorder(); } },
                    ] : []),
                  ]}
                />
              </div>
            </div>
          )}

          {/* MG1: glasögonkamerans förhandsvisning och status */}
          {huntSession && glassesAvailable && (glassesOn || glassesMsg) && (
            <div style={{
              position: 'fixed', top: 'calc(70px + env(safe-area-inset-top, 0px))', left: 16, zIndex: 9996,
              width: glassesOn ? 120 : 220, borderRadius: 8, overflow: 'hidden',
              border: `2px solid ${T.amber}`, boxShadow: '0 4px 20px rgba(0,0,0,0.5)', background: '#000',
            }}>
              {glassesOn && (
                <video ref={glassesVideoRef} onClick={openVideoFull} autoPlay muted playsInline style={{ cursor: 'zoom-in', width: '100%', aspectRatio: '9 / 16', objectFit: 'cover', display: 'block' }} />
              )}
              <div style={{ padding: '4px 6px', fontSize: 11, color: '#fff', background: glassesError ? 'rgba(120,20,20,0.85)' : 'rgba(0,0,0,0.7)' }}>
                {glassesMsg || 'Glasögonkameran delas'}
              </div>
              {glassesDiag && (glassesError || !glassesOn || glassesMsg) && (
                <div style={{ padding: '2px 6px 4px', fontSize: 9, lineHeight: 1.3, color: 'rgba(255,255,255,0.75)', background: 'rgba(0,0,0,0.7)', fontFamily: 'monospace', wordBreak: 'break-word', userSelect: 'text', WebkitUserSelect: 'text' }}>
                  {glassesDiag}
                </div>
              )}
              {!glassesOn && (
                <button type="button" onClick={() => { setGlassesMsg(null); setGlassesDiag(null); setGlassesError(false); }} style={{ width: '100%', padding: '4px 0', fontSize: 11, border: 'none', background: T.amber, color: '#0a0a0a', fontWeight: 700 }}>
                  Stäng
                </button>
              )}
            </div>
          )}

          {/* Own camera preview (small floating window top-left) */}
          {huntSession && showOwnCam && camOn && (
            <div style={{
              position: 'fixed', top: 'calc(70px + env(safe-area-inset-top, 0px))', left: 16, zIndex: 9996,
              width: 160, height: 120, borderRadius: 8, overflow: 'hidden',
              border: `2px solid ${T.amber}`, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            }}>
              <video
                ref={ownVideoRef}
                onClick={openVideoFull}
                autoPlay
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover', cursor: 'zoom-in',
                  transform: camFacingMode === 'user' ? 'scaleX(-1)' : 'none',
                }}
              />
              <button
                type="button"
                onClick={() => { void flipCameraFacing(); }}
                title={camFacingMode === 'environment' ? 'Byt till selfiekamera' : 'Byt till bakkamera'}
                style={{
                  position: 'absolute', bottom: 4, right: 4,
                  padding: '4px 8px', borderRadius: 6,
                  background: T.amber, border: `1px solid ${T.amber}`, color: '#0a0a0a',
                  fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                }}
              >
                {camFacingMode === 'environment' ? 'Selfie' : 'Bak'}
              </button>
              <button onClick={() => setShowOwnCam(false)} style={{
                position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: 4,
                background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', cursor: 'pointer',
                fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
              <div style={{ position: 'absolute', bottom: 4, left: 6, color: 'white', fontSize: 10, fontWeight: 600, textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                {userName || 'Du'}
              </div>
            </div>
          )}

          {/* Floating participants list — right edge of map, click to view cam */}
          {huntSession && (remoteParticipants.length > 0 || watchPositions.length > 0) && (
            <div style={{
              position: 'fixed', top: '50%', transform: 'translateY(-50%)',
              right: 16, zIndex: 9996,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {remoteParticipants.map(p => (
                <button
                  key={p.identity}
                  onClick={() => {
                    if (viewingParticipantId === p.identity) {
                      setViewingParticipantId(null);
                      detachRemotePiP();
                    } else {
                      viewRemoteCam(p.identity);
                    }
                  }}
                  title={`${p.name}${p.camOn ? ' — kamera på' : ''}`}
                  style={{
                    width: 46, height: 46, borderRadius: '50%', cursor: 'pointer',
                    border: `2px solid ${p.camOn ? T.success : T.border}`,
                    background: viewingParticipantId === p.identity
                      ? T.amber
                      : p.camOn ? 'rgba(97,216,54,0.18)' : T.surface,
                    backdropFilter: 'blur(12px)',
                    color: viewingParticipantId === p.identity ? '#0a0a0a' : T.text,
                    fontSize: 15, fontWeight: 700, padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
                    transition: 'all 0.2s',
                  }}
                >
                  {p.name.charAt(0).toUpperCase()}
                  {p.camOn && (
                    <div style={{
                      position: 'absolute', top: -2, right: -2,
                      width: 12, height: 12, borderRadius: '50%',
                      background: T.success, border: `2px solid ${T.bg}`,
                    }} />
                  )}
                </button>
              ))}
              {/* Med bara klockan (ingen telefon i radion) — en gång per jägare, utan kamera */}
              {watchPositions.map(w => (
                <div
                  key={`watch:${w.userEmail}`}
                  title={`${w.name} — ${w.source === 'watch' ? 'Apple Watch' : 'utan radio'}`}
                  style={{
                    width: 46, height: 46, borderRadius: '50%',
                    border: `2px dashed ${T.border}`, background: T.surface,
                    backdropFilter: 'blur(12px)', color: T.text,
                    fontSize: 15, fontWeight: 700, boxSizing: 'border-box',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
                  }}
                >
                  {w.name.charAt(0).toUpperCase()}
                </div>
              ))}
            </div>
          )}

          {/* Remote participant camera view (floating window) */}
          {huntSession && viewingParticipantId && (
            <div style={{
              position: 'fixed', top: 'calc(70px + env(safe-area-inset-top, 0px))', right: 80, zIndex: 9997,
              width: 260, height: 195, borderRadius: 8, overflow: 'hidden',
              border: `2px solid ${T.amber}`, boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
              background: '#000',
            }}>
              <video ref={remoteVideoRef} onClick={openVideoFull} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
              <button onClick={() => { setViewingParticipantId(null); detachRemotePiP(); }} style={{
                position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: 5,
                background: 'rgba(0,0,0,0.7)', border: 'none', color: 'white', cursor: 'pointer',
                fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
              }}>✕</button>
              <div style={{ position: 'absolute', bottom: 6, left: 8, color: 'white', fontSize: 11, fontWeight: 600, textShadow: '0 1px 4px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.5)', padding: '2px 6px', borderRadius: 3 }}>
                {remoteParticipants.find(x => x.identity === viewingParticipantId)?.name || viewingParticipantId}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── LEADER BROADCAST: split map + video when jaktledare turns on cam ── */}
      {fullscreen && huntSession && leaderBroadcasting && leaderVideoTrack && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: '35vh', zIndex: 9993,
          background: '#000', borderTop: `2px solid ${T.amber}`,
        }}>
          <video ref={leaderVideoRef} onClick={openVideoFull} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in' }} />
            <div style={{
              position: 'absolute', top: 8, left: 12, color: T.amber, fontSize: 12, fontWeight: 700,
              textShadow: '0 1px 4px rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff4444', animation: 'pulse 1.5s infinite' }} />
            {featuredBroadcastName || 'Livesändning'}
          </div>
        </div>
      )}

      {/* Fallt Vilt Modal */}
      {showFalltViltModal && (
        <FalltViltModal
          lat={showFalltViltModal.lat}
          lng={showFalltViltModal.lng}
          markId={mark.id}
          token={token}
          onSave={(newPin) => {
            setPins(prev => [...prev, newPin]);
            setShowFalltViltModal(null);
            setSelectedViltPin(newPin);
          }}
          onCancel={() => setShowFalltViltModal(null)}
        />
      )}

      {/* Vilt Details Panel */}
      {selectedViltPin && (
        <ViltDetailsPanel
          pin={selectedViltPin}
          token={token}
          onClose={() => setSelectedViltPin(null)}
          onStatusUpdate={(pinId, status) => {
            setPins(prev => prev.map(p => p.id === pinId ? { ...p, falltStatus: status } : p));
            setSelectedViltPin(prev => prev ? { ...prev, falltStatus: status } : null);
          }}
          onPinUpdate={(updated) => {
            setPins(prev => prev.map(p => p.id === updated.id ? updated : p));
            setSelectedViltPin(updated);
          }}
        />
      )}

      {/* Pin name modal */}
      {showPinModal && showPinModal.type !== 'skjutet_vilt' && (
        <div style={{
          position: 'fixed', inset: 0, background: T.scrim, display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{ background: T.surface, borderRadius: 6, padding: 24, minWidth: 300, border: `1px solid ${T.border}` }}>
            <div style={{ fontWeight: 700, color: PIN_CONFIG[showPinModal.type].color, marginBottom: 12, fontSize: 16 }}>
              Namnge {PIN_CONFIG[showPinModal.type].label}
            </div>
            <input
              value={newPinName}
              onChange={e => setNewPinName(e.target.value)}
              placeholder={showPinModal.type === 'kamera' ? 'Kamerans namn, t.ex. Gläntan' : 'Namn (valfritt)'}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
              onKeyDown={e => e.key === 'Enter' && confirmPin()}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={confirmPin} disabled={saving} style={{
                flex: 1, padding: '8px 0', borderRadius: 4, border: 'none',
                background: PIN_CONFIG[showPinModal.type].color, color: 'white', fontWeight: 700, cursor: 'pointer',
              }}>
                {saving ? '...' : 'Spara'}
              </button>
              <button onClick={() => { setShowPinModal(null); setNewPinName(''); }} style={{
                flex: 1, padding: '8px 0', borderRadius: 4, border: `1px solid ${T.forest}`,
                background: 'transparent', color: T.textMuted, cursor: 'pointer',
              }}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Åtelkamera */}
      {selectedCamera && (
        <div style={{
          position: 'fixed', inset: 0, background: T.scrim,
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001,
        }} onClick={() => setSelectedCamera(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: T.surface, borderRadius: 8, padding: 24, color: T.text,
            minWidth: 300, maxWidth: 420, width: '90%',
            border: `1px solid ${T.forest}`, position: 'relative',
            boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
          }}>
            <button onClick={() => setSelectedCamera(null)} style={{
              position: 'absolute', top: 12, right: 12, background: 'transparent',
              border: 'none', color: '#888', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 4,
            }}>✕</button>
            <div style={{ fontWeight: 700, color: PIN_CONFIG.kamera.color, fontSize: 13, marginBottom: 2, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {PIN_CONFIG.kamera.label}
            </div>
            <div style={{ fontWeight: 700, color: T.text, fontSize: 20, marginBottom: 14 }}>{selectedCamera.name}</div>
            {selectedCamera.lastImageUrl ? (
              <div style={{ marginBottom: 8, borderRadius: 5, overflow: 'hidden', border: `1px solid ${T.forest}` }}>
                <img
                  src={mediaPublicUrl(selectedCamera.lastImageUrl)}
                  alt={`Senaste bilden från ${selectedCamera.name}`}
                  onClick={() => window.open(mediaPublicUrl(selectedCamera.lastImageUrl), '_blank')}
                  style={{ width: '100%', maxHeight: 280, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: 8, padding: '28px 0', textAlign: 'center', color: T.textMuted, fontSize: 13, border: `1px dashed ${T.border}`, borderRadius: 5 }}>Ingen bild än</div>
            )}
            {selectedCamera.lastImageAt && (
              <div style={{ color: T.textMuted, fontSize: 12, marginBottom: 14 }}>
                Senaste bild {new Date(selectedCamera.lastImageAt).toLocaleString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · alla bilder finns i albumet
              </div>
            )}
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>Kamerans e-postadress</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ color: PIN_CONFIG.kamera.color, fontFamily: 'monospace', fontSize: 13, flex: 1, wordBreak: 'break-all' }}>{selectedCamera.emailAddress}</span>
              <button onClick={() => { navigator.clipboard?.writeText(selectedCamera.emailAddress); }} style={{
                background: PIN_CONFIG.kamera.color, color: '#000', border: 'none', borderRadius: 4,
                padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
              }}>Kopiera</button>
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={async () => {
                  const name = prompt('Kamerans namn', selectedCamera.name)?.trim();
                  if (!name || name === selectedCamera.name) return;
                  const updated = await api.updateMarkCamera({ id: selectedCamera.id, name });
                  setCameras(prev => prev.map(c => c.id === updated.id ? updated : c));
                  setSelectedCamera(updated);
                }} style={{
                  flex: 1, padding: '8px 0', borderRadius: 4, border: `1px solid ${T.forest}`,
                  background: 'transparent', color: T.text, cursor: 'pointer', fontSize: 13,
                }}>Byt namn</button>
                <button onClick={async () => {
                  if (!confirm(`Ta bort ${selectedCamera.name}? Adressen slutar fungera. Bilderna finns kvar i albumet.`)) return;
                  await api.deleteMarkCamera(selectedCamera.id);
                  setCameras(prev => prev.filter(c => c.id !== selectedCamera.id));
                  setSelectedCamera(null);
                }} style={{
                  flex: 1, padding: '8px 0', borderRadius: 4, border: '1px solid #c0392b',
                  background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: 13,
                }}>Ta bort</button>
              </div>
            )}
            {isAdmin && <div style={{ color: T.textMuted, fontSize: 11, marginTop: 10 }}>Dra kamerans nål på kartan för att flytta den.</div>}
          </div>
        </div>
      )}

      {/* Pin detail modal */}
      {selectedPin && (
        <div style={{
          position: 'fixed', inset: 0, background: T.scrim,
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001,
        }} onClick={() => setSelectedPin(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: T.surface, borderRadius: 8, padding: 24, color: T.text,
            minWidth: 300, maxWidth: 360, width: '90%',
            border: `1px solid ${T.forest}`, position: 'relative',
            boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
          }}>
            {/* Close */}
            <button onClick={() => setSelectedPin(null)} style={{
              position: 'absolute', top: 12, right: 12, background: 'transparent',
              border: 'none', color: T.textMuted, fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 4,
            }}>✕</button>

            {/* Type */}
            <div style={{ fontWeight: 700, color: pinConfig(selectedPin.type).color, fontSize: 13, marginBottom: 2, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {pinConfig(selectedPin.type).label}
            </div>

            {/* Name */}
            <div style={{ fontWeight: 700, color: T.text, fontSize: 20, marginBottom: selectedPin.description ? 6 : 14 }}>
              {selectedPin.name || '—'}
            </div>

            {/* Description */}
            {selectedPin.description && (
              <div style={{ color: T.textSub, fontSize: 13, marginBottom: 14 }}>{selectedPin.description}</div>
            )}

            {/* Image */}
            {selectedPin.imageUrl && (
              <div style={{ marginBottom: 14, borderRadius: 5, overflow: 'hidden', border: `1px solid ${T.forest}` }}>
                <img
                  src={mediaPublicUrl(selectedPin.imageUrl)}
                  alt="Passbild"
                  style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                />
              </div>
            )}

            {/* Navigate to */}
            <button
              onClick={() => {
                if (!userPosition) {
                  alert('GPS-position saknas. Tillåt platstjänster eller klicka på kartan för att ange din position.');
                  return;
                }
                setNavigatingTo(selectedPin);
                drawNavLine(userPosition, selectedPin, selectedPin);
                setSelectedPin(null);
              }}
              style={{
                display: 'block', width: '100%', padding: '9px 0', borderRadius: 4,
                border: `1px solid ${pinConfig(selectedPin.type).color}`,
                background: 'transparent',
                color: pinConfig(selectedPin.type).color,
                fontWeight: 700, cursor: 'pointer', fontSize: 13, marginBottom: 10,
              }}
            >
              Navigera till
            </button>

            {/* Upload image */}
            <label style={{
              display: 'block', textAlign: 'center', padding: '9px 0', borderRadius: 4,
              border: `1px solid ${T.amber}`, color: T.amber, cursor: uploadingImage ? 'default' : 'pointer',
              fontSize: 13, fontWeight: 600, marginBottom: 10,
              opacity: uploadingImage ? 0.6 : 1, transition: 'opacity 0.2s',
            }}>
              {uploadingImage ? 'Laddar upp...' : selectedPin.imageUrl ? 'Byt bild' : 'Lägg till bild'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                disabled={uploadingImage}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploadingImage(true);
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const res = await fetch(resolveUrl('/api/upload'), { method: 'POST', body: formData });
                    const uploadData = await res.json();
                    if (uploadData.url) {
                      await gqlFetch(
                        `mutation($input: UpdateMarkPinInput!) { updateMarkPin(input: $input) { id imageUrl } }`,
                        { input: { id: selectedPin.id, imageUrl: uploadData.url } }
                      );
                      const updated = { ...selectedPin, imageUrl: uploadData.url };
                      setPins(prev => prev.map(p => p.id === selectedPin.id ? updated : p));
                      setSelectedPin(updated);
                    }
                  } catch (err) { console.error('Upload failed', err); }
                  finally { setUploadingImage(false); }
                }}
              />
            </label>

            {/* Admin: delete */}
            {isAdmin && (
              <button
                onClick={async () => {
                  if (!confirm(`Ta bort ${selectedPin.name || pinConfig(selectedPin.type).label}?`)) return;
                  await gqlFetch(`mutation($id: ID!) { deleteMarkPin(id: $id) }`, { id: selectedPin.id });
                  setPins(prev => prev.filter(p => p.id !== selectedPin.id));
                  setSelectedPin(null);
                }}
                style={{
                  display: 'block', width: '100%', padding: '9px 0', borderRadius: 4,
                  border: 'none', background: 'rgba(229,57,53,0.18)', color: '#E53935',
                  fontWeight: 700, cursor: 'pointer', fontSize: 13,
                }}
              >
                Ta bort
              </button>
            )}

            {/* Drag hint for admin */}
            {isAdmin && (
              <div style={{ textAlign: 'center', color: '#666', fontSize: 11, marginTop: 10 }}>
                Dra pinn direkt på kartan för att flytta
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Skjutmeny modal (lovligt vilt, priser, mål) ── */}
      {showSkjutmenyModal && huntSession && (
        <div
          style={{ position: 'fixed', inset: 0, background: T.scrim, zIndex: 10102, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowSkjutmenyModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.surface, borderRadius: 14, padding: '22px 20px 18px', color: T.text,
              maxWidth: 440, width: '94%', maxHeight: 'min(85vh, 640px)', overflowY: 'auto',
              border: `1.5px solid ${T.amber}`,
              boxShadow: '0 16px 56px rgba(0,0,0,0.7)',
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={() => setShowSkjutmenyModal(false)}
              style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}
            >
              ✕
            </button>
            <div style={{ color: T.amber, fontSize: 17, fontWeight: 800, marginBottom: 4, letterSpacing: '0.3px' }}>Skjutmeny</div>
            <div style={{ color: T.textSub, fontSize: 12, marginBottom: 14 }}>
              {mark.name}
              {skjutmeny?.sunrise && skjutmeny?.sunset && <> · sol {new Date(skjutmeny.sunrise).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}–{new Date(skjutmeny.sunset).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</>}
              {skjutmeny?.sunNote && <> · {skjutmeny.sunNote}</>}
              {userPosition ? ' · din position' : ''}
            </div>

            {skjutmenyLoading && !skjutmeny ? (
              <div style={{ color: T.textSub, fontSize: 14, padding: '12px 0' }}>Laddar skjutmenyn…</div>
            ) : skjutmenyError && !skjutmeny ? (
              <div style={{ color: T.textSub, fontSize: 13, padding: '12px 0' }}>Skjutmenyn kunde inte hämtas: {skjutmenyError}</div>
            ) : (<>
              <section style={{ marginBottom: 4 }}>
                {skjutmenyFamilies.length === 0 ? (
                  <div style={{ color: T.textSub, fontSize: 13, lineHeight: 1.45 }}>Inget vilt är inlagt på marken (sätts i viltvårdsplanen).</div>
                ) : (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                          <th style={{ width: 22, padding: '4px 4px' }} />
                          <th style={{ textAlign: 'left', padding: '4px 4px', color: T.textMuted, fontWeight: 600, fontSize: 11 }}>Vilt</th>
                          <th style={{ textAlign: 'right', padding: '4px 4px', color: T.textMuted, fontWeight: 600, fontSize: 11 }}>Kvar</th>
                          <th style={{ textAlign: 'right', padding: '4px 4px', color: T.textMuted, fontWeight: 600, fontSize: 11 }}>Fällavgift</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skjutmenyFamilies.map(group => {
                          const hasSubcats = group.rows.length > 1 || group.rows[0].gameType !== group.familyLabel;
                          return (
                            <React.Fragment key={group.familyKey}>
                              {hasSubcats && (
                                <tr style={{ borderTop: `1px solid ${T.border}` }}>
                                  <td colSpan={4} style={{ padding: '8px 4px 3px', fontWeight: 700, color: T.text, fontSize: 13, letterSpacing: '0.2px' }}>
                                    {group.familyLabel}
                                    {group.rows[0].kgPrice != null && <span style={{ color: T.textMuted, fontWeight: 500, fontSize: 11 }}> · kött {group.rows[0].kgPrice.toLocaleString('sv-SE')} kr/kg</span>}
                                  </td>
                                </tr>
                              )}
                              {group.rows.map(r => {
                                const label = r.gameType.includes(' — ') ? r.gameType.split(' — ').slice(1).join(' — ') : r.gameType;
                                const open = skjutmenyOpenRow === r.gameType;
                                const dim = r.status === 'ej_jakttid' || r.status === 'okand';
                                return (
                                  <React.Fragment key={r.gameType}>
                                    <tr
                                      onClick={() => setSkjutmenyOpenRow(open ? null : r.gameType)}
                                      style={{ borderBottom: open ? 'none' : `1px solid ${T.border}`, borderTop: hasSubcats ? undefined : `1px solid ${T.border}`, cursor: 'pointer', opacity: dim ? 0.75 : 1 }}
                                      title={r.reason}
                                    >
                                      <td style={{ padding: '8px 6px 8px 4px' }}><GameStatusIcon status={r.status} T={appTheme} title={r.reason} /></td>
                                      <td style={{ padding: '8px 4px', paddingLeft: hasSubcats ? 14 : 4, color: T.text, fontWeight: hasSubcats ? 400 : 700 }}>
                                        {label}
                                        {r.status !== 'lovligt' && <div style={{ color: T.textMuted, fontSize: 11, marginTop: 1 }}>{r.status === 'slut' ? r.reason : GAME_STATUS_LABEL[r.status]}</div>}
                                      </td>
                                      <td style={{ textAlign: 'right', padding: '8px 4px', color: T.textSub, fontVariantNumeric: 'tabular-nums' }}>{r.planned != null ? `${r.remaining} av ${r.planned}` : '—'}</td>
                                      <td style={{ textAlign: 'right', padding: '8px 4px', color: T.textSub, fontSize: 12, whiteSpace: 'nowrap' }}>{r.fieldFee != null ? `${r.feeFrom ? 'från ' : ''}${kr(r.fieldFee)}` : '—'}</td>
                                    </tr>
                                    {open && (
                                      <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                        <td />
                                        <td colSpan={3} style={{ padding: '0 4px 10px', paddingLeft: hasSubcats ? 14 : 4, color: T.textSub, fontSize: 12, lineHeight: 1.45 }}>
                                          <div style={{ color: T.text }}>{r.reason}</div>
                                          {r.notes.map(n => <div key={n}>· {n}</div>)}
                                          {r.periods.length > 0 && <div style={{ marginTop: 4, color: T.textMuted }}>Jakttider i {skjutmeny?.county ?? 'länet'}: {r.periods.join('; ')}</div>}
                                          {!hasSubcats && r.kgPrice != null && <div>Kött {r.kgPrice.toLocaleString('sv-SE')} kr/kg</div>}
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 12 }}><GameStatusLegend T={appTheme} /></div>
                    <p style={{ color: T.textMuted, fontSize: 11, lineHeight: 1.45, margin: '8px 0 0' }}>
                      Tryck på ett viltslag för förklaring. Priserna gäller din grupp på marken (lägsta om du är med i flera).
                      {skjutmeny?.kottIngar ? ' Kött ingår.' : ''}
                      {skjutmeny?.source && <> Jakttider: <a href={skjutmeny.source.url} target="_blank" rel="noreferrer" style={{ color: T.textMuted }}>{skjutmeny.source.title}</a>{skjutmeny.source.amendedThrough ? ` (t.o.m. ${skjutmeny.source.amendedThrough})` : ''}. Licensjakt och lokala beslut från länsstyrelsen visas som okänd jakttid – kontrollera alltid gällande beslut.</>}
                    </p>
                  </>
                )}
              </section>
              {skjutmeny?.birds && <SkjutmenyFagel markId={mark.id} birds={skjutmeny.birds} county={skjutmeny.county} T={appTheme} onChanged={() => setSkjutmenyTick(t => t + 1)} />}
            </>)}

            <button
              type="button"
              onClick={() => setShowSkjutmenyModal(false)}
              style={{ marginTop: 18, width: '100%', padding: '11px 0', borderRadius: 8, border: `2px solid ${T.amber}`, background: T.amber, color: '#0a0a0a', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            >
              Stäng
            </button>
          </div>
        </div>
      )}

      {/* ── Invite Modal ── */}
      {showInviteModal && huntSession && (
        <div
          style={{ position: 'fixed', inset: 0, background: T.scrim, zIndex: 10100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowInviteModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.surface, borderRadius: 14, padding: '28px 28px 24px', color: T.text,
              maxWidth: 360, width: '92%', textAlign: 'center',
              border: `1.5px solid ${T.amber}`,
              boxShadow: '0 16px 56px rgba(0,0,0,0.7)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setShowInviteModal(false)}
              style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}
            >✕</button>

            <div style={{ color: T.amber, fontSize: 17, fontWeight: 800, marginBottom: 4, letterSpacing: '0.3px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <IconShareArrow size={22} /> Dela jakten
            </div>
            <div style={{ color: T.textSub, fontSize: 12, marginBottom: 20 }}>
              QR-kod eller länk — mottagaren kan ansluta utan konto
            </div>
            {inviteJoinUrl && (
              <div style={{ marginBottom: 14, textAlign: 'left' }}>
                <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Länk</div>
                <div style={{ fontSize: 10, color: T.textSub, wordBreak: 'break-all', fontFamily: 'ui-monospace, monospace', lineHeight: 1.35, background: T.bg, padding: '8px 10px', borderRadius: 6, border: `1px solid ${T.border}` }}>{inviteJoinUrl}</div>
              </div>
            )}

            {/* QR code */}
            {inviteQR ? (
              <div style={{
                background: 'white', borderRadius: 10, padding: 12,
                display: 'inline-block', marginBottom: 20,
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              }}>
                <img
                  src={inviteQR}
                  alt="QR-kod"
                  style={{ width: 220, height: 220, display: 'block', imageRendering: 'pixelated' }}
                />
              </div>
            ) : (
              <div style={{ width: 220, height: 220, background: T.bg, borderRadius: 10, margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted, fontSize: 13 }}>
                Genererar…
              </div>
            )}

            {/* Short code */}
            <div style={{ color: T.textSub, fontSize: 11, marginBottom: 6, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Anslutningskod
            </div>
            <div style={{
              color: T.amber, fontSize: 28, fontWeight: 900, letterSpacing: 4,
              fontFamily: '"Courier New", Courier, monospace',
              background: T.amberDim, borderRadius: 8,
              padding: '10px 20px', display: 'inline-block',
              marginBottom: 16, border: `1px solid ${T.amber}`,
            }}>
              {inviteCode || '····-····'}
            </div>
            <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.4, margin: '-6px 0 12px' }}>
              Gäster utan konto skannar QR-koden eller anger koden på {publicWebAppUrl().replace(/^https?:\/\//, '')}/anslut. Du godkänner dem här.
            </div>

            {/* Copy link */}
            <div style={{ marginTop: 4 }}>
              <button
                onClick={() => {
                  const url = inviteJoinUrl || `${publicWebAppUrl()}/?join=${encodeURIComponent(huntSession.meetingId)}&mark=${encodeURIComponent(mark.id)}`;
                  navigator.clipboard?.writeText(url).catch(() => {});
                }}
                style={{
                  background: 'none', border: `1px solid ${T.border}`,
                  color: T.textSub, borderRadius: 5, padding: '6px 14px',
                  cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                }}
              >
                Kopiera länk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Hunt close modal (Lämna jakt / Spara och Avsluta) ── */}
      {showHuntCloseModal && huntSession && (
        <div
          style={{ position: 'fixed', inset: 0, background: T.scrim, zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowHuntCloseModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.surface, borderRadius: 16, padding: '28px 24px 24px', color: T.text,
              maxWidth: 360, width: '92%',
              border: `1.5px solid ${T.amber}`,
              boxShadow: '0 16px 56px rgba(0,0,0,0.7)',
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800, color: T.amber, marginBottom: 6 }}>Avsluta jakten?</div>
            <p style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5, margin: '0 0 20px' }}>
              {remoteParticipants.length + watchPositions.length > 0
                ? `Det finns ${remoteParticipants.length + watchPositions.length} andra deltagare. "Lämna" avslutar bara för dig — jakten fortsätter. "Spara och avsluta" stänger hela jakten.`
                : myWatchInHunt
                  ? 'Din klocka är med i jakten. "Lämna" kopplar bara bort telefonen — jakten fortsätter på klockan. "Spara och avsluta" stänger hela jakten.'
                  : 'Du är ensam i jakten. Välj hur du vill avsluta.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                type="button"
                disabled={qSummarizing}
                onClick={async () => {
                  // QJ2: Q skriver transkriberingen och sammanfattningen innan jakten stängs (högst 25 s, annars som förut).
                  let qSummary: HuntSummary | null = null;
                  const meetingId = huntSession?.meetingId;
                  if (meetingId && token && !isGuestHunt(meetingId)) {
                    setQSummarizing(true);
                    qSummary = await Promise.race([
                      huntQApi.summary(meetingId).catch(() => null),
                      new Promise<null>(r => setTimeout(() => r(null), 25_000)),
                    ]);
                    setQSummarizing(false);
                  }
                  setShowHuntCloseModal(false);
                  // Clear stored session so "Återanslut" never shows after an intentional end
                  try { localStorage.removeItem(`qvenari_recent_hunt_${mark.id}`); } catch (_) {}
                  const ck = confirmedKillsRef.current;
                  confirmedKillsRef.current = [];
                  if (meetingId) void finishHuntWorkout(meetingId);
                  onEndHunt?.(transcriptRef.current, { confirmedKills: ck, qSummary });
                }}
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: 10,
                  border: `2px solid ${T.amber}`, background: T.amber, color: '#0a0a0a',
                  fontWeight: 800, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                }}
              >
                {qSummarizing ? 'Q sammanfattar jakten…' : 'Spara och avsluta'}
              </button>
              {(remoteParticipants.length + watchPositions.length > 0 || myWatchInHunt) && (
                <button
                  type="button"
                  onClick={() => {
                    setShowHuntCloseModal(false);
                    if (huntSession?.meetingId) void finishHuntWorkout(huntSession.meetingId);
                    onLeaveHunt?.();
                  }}
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: 10,
                    border: `2px solid ${T.amber}`, background: 'transparent', color: T.amber,
                    fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Lämna jakt (fortsätt utan mig)
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowHuntCloseModal(false)}
                style={{
                  width: '100%', padding: '10px 16px', borderRadius: 10,
                  border: `1px solid ${T.border}`, background: 'transparent', color: T.textSub,
                  fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pin legend — hidden in fullscreen */}
      {!fullscreen && pins.length > 0 && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {(Object.keys(PIN_CONFIG) as PinType[]).map(type => {
            const count = pins.filter(p => p.type === type).length;
            if (count === 0) return null;
            return (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: PIN_CONFIG[type].color }} />
                <span style={{ fontSize: 13, color: T.textMuted }}>{PIN_CONFIG[type].label}: {count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
}

/** Jägarhälsa under vädret: puls från Apple Watch och dagens steg. Bara den egna, delas inte med andra. */
function HunterHealthRow({ bpm, steps, color }: { bpm: number | null; steps: number | null; color: string }) {
  return (
    <div role="status" style={{
      display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 800, color, lineHeight: 1.2,
      fontVariantNumeric: 'tabular-nums',
      textShadow: '0 0 5px #000, 0 1px 3px #000, 0 0 12px rgba(0,0,0,0.85)',
    }}>
      {bpm != null && (
        <span title="Puls från Apple Watch" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <span style={{ color: '#ff3b30', fontSize: 12 }}>♥</span>{bpm}
        </span>
      )}
      {steps != null && (
        <span title="Dagens steg" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <span style={{ fontSize: 11 }} aria-hidden>👣</span>{steps.toLocaleString('sv-SE')} steg
        </span>
      )}
    </div>
  );
}

/**
 * Rutan för en hund med Apple Watch. För din egen hund finns knappar för klockans energispar
 * (positionen var 30:e s i stället för var 10:e) och mikrofon (skallavkänning).
 */
function dogWatchPopup(
  d: HuntDogPosition, barking: boolean, batt: string,
  onChange: (change: { powerMode?: 'hunt' | 'saver'; mic?: boolean }) => void,
): HTMLElement {
  const esc = (t: string) => t.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
  const el = document.createElement('div');
  el.style.minWidth = '170px';
  el.innerHTML = `<b>${esc(d.dogName)}</b> (${esc(d.ownerName)})<br/>Apple Watch${batt}`
    + (barking ? '<br/><b style="color:#ff3b30">Skäller</b>' : '')
    + `<br/><span style="color:#666">${d.powerMode === 'saver' ? 'Energispar · var 30:e s' : 'Jakt · var 10:e s'} · mikrofon ${d.mic ? 'på' : 'av'}</span>`;
  if (!d.isMine) return el;
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;gap:6px;margin-top:8px';
  const button = (label: string, active: boolean, onClick: () => void) => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = `flex:1;padding:6px 8px;border-radius:6px;border:1px solid #EE8F09;font-weight:700;font-size:12px;cursor:pointer;`
      + (active ? 'background:#EE8F09;color:#111' : 'background:transparent;color:#EE8F09');
    b.addEventListener('click', e => { e.stopPropagation(); onClick(); });
    return b;
  };
  row.appendChild(button(d.powerMode === 'saver' ? '🍃 Energispar' : '⚡ Jakt', d.powerMode === 'saver',
    () => onChange({ powerMode: d.powerMode === 'saver' ? 'hunt' : 'saver' })));
  row.appendChild(button(d.mic ? '🎙 Mik på' : '🎙 Mik av', d.mic, () => onChange({ mic: !d.mic })));
  el.appendChild(row);
  return el;
}

/** "8 s sedan", "3 min sedan", "14:02" */
function agoLabel(ms: number): string {
  const sec = Math.round((Date.now() - ms) / 1000);
  if (sec < 60) return `${Math.max(sec, 0)} s sedan`;
  if (sec < 3600) return `${Math.round(sec / 60)} min sedan`;
  return new Date(ms).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

/** UX15: Min position – cirkel med en punkt i (lokalisera). */
function LocateIcon({ size = 20, dim = false }: { size?: number; dim?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden style={{ opacity: dim ? 0.6 : 1 }}>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  );
}
